import { WorkflowTemplate } from 'types';

/**
 * Pre-built workflow templates for users to get started
 */
export const workflowTemplates: WorkflowTemplate[] = [
  {
    id: 'sports-broadcast-fan-cameo',
    name: 'Sports Broadcast Fan Cameo – Put Yourself in the Crowd',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/original_274bace7-290a-4689-af2a-277ee49851dd.mp4',
    description:
      'Drop a single portrait into nine iconic sports broadcasts – NFL, MLB, KBO, Premier League, F1, NBA, cricket, Wimbledon, and volleyball – then animate the best scenes as live-broadcast clips using four different video models.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-1778682171729',
          type: 'note',
          position: { x: -310.083, y: 1327.067 },
          data: {
            type: 'note',
            label: 'About this workflow',
            config: {
              note: "<h1>Sports Broadcast Fan Cameo</h1><p><strong>Place yourself in the crowd of the world's biggest sporting events as if caught by a live broadcast camera.</strong></p><ol><li><p><strong>Upload</strong> a clear portrait of yourself (replace the sample photo).</p></li><li><p>The workflow generates <strong>nine broadcast-style fan cutaways</strong> – NFL, MLB, KBO, Premier League, F1 paddock, NBA, cricket, Wimbledon, and volleyball – each with authentic scoreboards, lighting, and crowd context.</p></li><li><p>Four of those scenes are <strong>animated into short live-broadcast clips</strong> using different video models so you can compare results.</p></li><li><p>Tweak any prompt by clicking <strong>Settings</strong> on the node, then re-run.</p></li></ol>",
            },
          },
          measured: { width: 400, height: 132 },
        },
        {
          id: 'note-1778682199614',
          type: 'note',
          position: { x: 240.573, y: 885.726 },
          data: {
            type: 'note',
            label: 'Step 1 – Upload Your Photo',
            config: {
              note: '<h1>Upload Your Portrait</h1><p>Remove the sample image and upload <strong>a clear photo of yourself</strong>. A front-facing, well-lit portrait works best – your identity will be preserved across every sport.</p>',
            },
          },
          measured: { width: 400, height: 144 },
        },
        {
          id: 'upload-1778666617587',
          type: 'upload',
          position: { x: 653.587, y: 1546.883 },
          origin: [1, 0.5],
          data: {
            type: 'upload',
            label: 'Upload Your Photo',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/03b55bbf-3a6a-43c1-85c1-8d7c754570a0.jpeg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/03b55bbf-3a6a-43c1-85c1-8d7c754570a0.jpeg',
          },
          measured: { width: 424, height: 634 },
        },
        {
          id: 'note-1778683503061',
          type: 'note',
          position: { x: 2768.983, y: -443.762 },
          data: {
            type: 'note',
            label: 'Step 2 – Customize Scenes',
            config: {
              note: '<h1>Tweak Any Scene</h1><p>Each sport is generated by its own <strong>Image to Image</strong> node. Click <strong>Settings</strong> on any node to edit the prompt – swap teams, change camera angle, switch attire, or describe the moment differently – then re-run that node.</p>',
            },
          },
          measured: { width: 400, height: 108 },
        },
        {
          id: 'image-edit-1778682537622',
          type: 'image-edit',
          position: { x: 1784.735, y: -531.015 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'Cricket Match Fan',
            config: {
              useHD: false,
              prompt:
                'Photorealistic international cricket live broadcast fan cutaway from a packed stadium. Identity preserved from reference. Subject is seated shoulder-to-shoulder among fans in the lower stands, chest-up medium shot, eye-level broadcast camera angle, clearly part of the crowd. Subject wears an authentic cricket home team jersey, gazing toward the pitch with a focused serious expression, caught candidly during play. Surrounding fans fill the entire frame — people directly beside him and behind him, team jerseys, caps, flags, sunglasses, phones, drinks, scorecards, blurred faces and crowd motion. No empty space around the subject, no isolated portrait look. Bright daylight mixed with stadium lighting, subtle skin sheen, authentic pores and texture, realistic shadows. Green cricket outfield only faintly visible through gaps behind the crowd, not the main focus. Telephoto compression, shallow depth of field, sharp focus on subject, broadcast color grade. Live TV graphics overlay: cricket scoreboard, batting score, wickets, overs, run rate, batter and bowler names, tournament watermark, LIVE indicator. Documentary realism, faint broadcast grain.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2bfa4443-8be4-4e69-b758-810558bb264c.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778682458335',
          type: 'image-edit',
          position: { x: 1775.265, y: 103.681 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'NBA Game Fan',
            config: {
              useHD: false,
              prompt:
                'Photorealistic NBA live broadcast fan cutaway from a basketball arena. Identity preserved from reference. Subject wearing casual sport event attire is seated among fans in the lower-bowl stands, eye-level broadcast camera angle, clearly embedded inside the crowd. Subject gazing toward the court with a focused serious expression, caught candidly mid-play. Surrounding fans fill the entire frame — people directly beside him and behind him, jerseys, caps, phones, drinks, foam fingers, blurred faces and crowd motion. Bright indoor arena lighting, subtle skin sheen, realistic shadows. Telephoto compression, shallow depth of field, sharp focus on subject, broadcast color grade. Live TV graphics overlay: NBA-style scoreboard, team names, score, quarter, game clock, shot clock, LIVE watermark. Documentary realism, faint digital broadcast grain.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/cc856605-4877-4bdc-95c7-e613035efede.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778682019820',
          type: 'image-edit',
          position: { x: 1764.841, y: 713.345 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'F1 Ferrari Paddock',
            config: {
              useHD: false,
              prompt:
                'Ultra-realistic F1 live TV broadcast screenshot, identity preserved from reference. Fan seated inside the VIP Ferrari paddock garage during a Formula 1 race. Final lap tension — wearing a professional racing headset, listening to team radio, leaning forward with one hand near her mouth, eyes on the garage monitors, proud yet anxious expression. Ferrari red gear and pit crew blurred behind her, telemetry screens glowing. Harsh garage lighting, subtle skin sheen, authentic texture, telephoto compression, shallow depth of field. ',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/fbe0043a-49f0-4dc2-8338-6a4f6316b7e1.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778679297993',
          type: 'image-edit',
          position: { x: 1768.248, y: 1349.12 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'NFL Stadium Fan',
            config: {
              useHD: false,
              prompt:
                'Photorealistic NFL Jumbotron broadcast capture of a fan seated in the lower stands during a night game. Subject wears an NFL home team jersey, gazing toward the field with a focused, intent expression, caught candidly mid-play. Bright stadium floodlights cast crisp highlights and soft shadows, subtle sweat glow on the skin, authentic pores and texture. Surrounding fans slightly blurred, cheering with foam fingers and phones raised. Telephoto compression, shallow depth of field, live sports graphics overlay in lower third — score, quarter, down and distance. ',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c678fa43-99d2-413f-98dd-a193d03f4625.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778681994696',
          type: 'image-edit',
          position: { x: 1743.932, y: 1957.583 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'Premier League Fan',
            config: {
              useHD: false,
              prompt:
                'Realistic English Premier League  soccer broadcast screenshot from the spectator stands, distant broadcast camera angle. Identity preserved from reference. Subject seated among the crowd, focused serious expression. Wearing the authentic Arsenal FC home jersey. Leaning slightly forward. Wider framing, smaller in frame as if zoomed from across the stadium. Bright floodlights, subtle skin sheen, authentic texture. Fans blurred — jerseys, scarves, phones, flags, crowd motion. Long telephoto, shallow depth of field, candid mid-match moment. Live broadcast graphics at top: scoreboard, timer, team names, league watermark. Faint grain, cinematic color, documentary realism.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/73967429-ee24-4317-b9a6-0c1f0549d4b4.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778681366221',
          type: 'image-edit',
          position: { x: 1738.053, y: 2563.796 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'MLB Baseball Fan',
            config: {
              useHD: false,
              prompt:
                'Hyper-realistic live broadcast camera catch inside a packed baseball stadium. Subject sits relaxed in stadium seats wearing a team home jersey, gazing toward the field with a calm, focused expression, caught unaware. Subtle motion blur, authentic skin texture with natural sheen, surrounding fans slightly out of focus holding drinks and phones. Warm golden-hour light mixed with stadium floodlights, telephoto compression, shallow depth of field. Semi-transparent live scoreboard overlay in lower corner. Documentary realism, broadcast color grade.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2dc4790b-4b36-4b76-9666-07f1413b9588.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778681437688',
          type: 'image-edit',
          position: { x: 1735.729, y: 3130.495 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'KBO Baseball Fan',
            config: {
              useHD: false,
              prompt:
                'Realistic KBO live broadcast frame, identity preserved exactly from the attached reference. Subject casually seated among spectators in a packed Korean baseball stadium, wearing an authentic KBO team jersey faithful to the reference. Aware of the camera but not posing — caught mid-moment with a slightly surprised candid expression, lips parted, eyes meeting the lens. Natural skin texture, subtle sheen under stadium floodlights, authentic pores. Surrounding fans slightly blurred, waving thundersticks, holding drinks and phones, chanting fan songs. Telephoto compression, shallow depth of field, crowd motion blur. Faint TV broadcast noise and grain, cinematic color grade. Live KBO scoreboard graphics in lower third — team logos, inning, score, count, runner indicators, network watermark. Documentary realism, candid spontaneity. ',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/56d3daed-2cf1-49f0-b0c3-c6250a08300d.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778682437952',
          type: 'image-edit',
          position: { x: 1720.228, y: 3665.948 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'Wimbledon Tennis Fan',
            config: {
              useHD: false,
              prompt:
                'Realistic Wimbledon tennis broadcast screenshot from the spectator stands, distant broadcast camera angle. Identity preserved from reference. Subject seated among the Centre Court crowd, focused serious expression, watching the match intently. Wearing a refined Wimbledon-appropriate outfit, such as a crisp white polo or light summer shirt with subtle tennis-inspired styling. Leaning slightly forward, hands near lap or clasped, absorbed in a tense rally. Wider framing, smaller in frame as if zoomed from across the court. Natural daylight or soft late-afternoon stadium light, subtle skin sheen, authentic facial texture. Spectators blurred around him — summer hats, sunglasses, strawberries and cream, phones, programs, subtle applause and crowd motion. Green court visible in the background with white baseline markings and players faintly out of focus. Long telephoto lens, shallow depth of field, candid mid-match moment. Live broadcast graphics at top: Wimbledon-style scoreboard, player names, set/game score, serve indicator, tournament watermark. Faint film grain, cinematic color, documentary realism, authentic Grand Slam atmosphere.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/761b01d0-4fd8-4e7d-8202-2a2cdf24e6ee.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-edit-1778682581742',
          type: 'image-edit',
          position: { x: 1708.82, y: 4211.444 },
          origin: [0, 0.5],
          data: {
            type: 'image-edit',
            label: 'Volleyball Match Fan',
            config: {
              useHD: false,
              prompt:
                'Photorealistic professional volleyball live broadcast fan cutaway from a packed indoor arena. Identity preserved from reference. Subject is seated shoulder-to-shoulder among fans in the lower arena stands, chest-up medium shot, eye-level broadcast camera angle, clearly embedded inside the crowd. Subject wears an authentic volleyball home team jersey, gazing toward the court with a focused serious expression, caught candidly mid-rally. Surrounding fans fill the entire frame — people directly beside him and behind him, jerseys, scarves, clappers, flags, phones raised, drinks, blurred faces and crowd motion. No empty space around the subject, no isolated portrait look, not standing, not courtside alone. Bright indoor sports arena lighting, subtle skin sheen, authentic pores and texture, realistic shadows. Volleyball court only faintly visible through gaps behind the crowd, with net and colored court barely recognizable, not the main focus. Telephoto compression, shallow depth of field, sharp focus on subject, broadcast color grade. Live TV graphics overlay: volleyball scoreboard, team names, set score, current points, serve indicator, tournament watermark, LIVE indicator. Documentary realism, faint digital broadcast grain.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/96892b9f-54e3-4cee-a89a-772f5bf1c349.png',
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'note-1778682842367',
          type: 'note',
          position: { x: 3884.819, y: 1244.949 },
          data: {
            type: 'note',
            label: 'Step 3 – Animate Your Favorites',
            config: {
              note: '<h1>Bring the Scenes to Life</h1><p>Pick your favorite still frames and animate them with the <strong>Image to Video</strong> nodes on the right. Each one uses a <strong>different video model</strong> (Veo 3.1, Happy Horse, Seedance, Kling) so you can compare quality, motion, and style side by side.</p>',
            },
          },
          measured: { width: 400, height: 180 },
        },
        {
          id: 'note-1778683434012',
          type: 'note',
          position: { x: 4029.532, y: 2487.697 },
          data: {
            type: 'note',
            label: 'Tip – Video Prompt',
            config: {
              note: '<p><strong>Example of video prompt:</strong></p><p>realistic sports broadcast video of a fan in a crowd, camera slowly zooms in as she is unaware of being on camera, fan adjusts hair or look around casually watching the match, or sips a drink, but all in a natural casual way</p>',
            },
          },
          measured: { width: 400, height: 164 },
        },
        {
          id: 'image-to-video-1778681772477',
          type: 'image-to-video',
          position: { x: 3073.428, y: 1359.771 },
          origin: [0, 0.5],
          data: {
            type: 'image-to-video',
            label: 'Animate NFL Scene (Veo 3.1)',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'realistic sports broadcast video of a fan in a crowd, camera slowly zooms in as she is unaware of being on camera, fan adjusts hair or look around casually watching the match, or sips a drink, but all in a natural casual way',
              duration: '8s',
              resolution: '1080p',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/3e09055c-410c-40c6-b645-d22f9820f9b1.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a9a0db0/914bwp9CLvdapTE3iC3XW_c6d9c33bd72f48cfa52cd305e39d48ac.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a9a0db0/A2LqhavstTUGmRssVZRqZ_c09ad9f668f94bcf8567e65083c5c340.jpg',
            },
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-to-video-1778682271923',
          type: 'image-to-video',
          position: { x: 3069.273, y: 1955.935 },
          origin: [0, 0.5],
          data: {
            type: 'image-to-video',
            label: 'Animate Premier League Scene (Seedance)',
            config: {
              model: 'Seedance 2.0 Fast Image',
              prompt:
                'realistic sports broadcast video of a fan in a crowd, camera slowly zooms in as she is unaware of being on camera, fan adjusts hair or look around casually watching the match, or sips a drink, but all in a natural casual way',
              duration: '10',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/video_final_ed9380c4-1b3f-4388-bf5b-f975b3f6cb96.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a9a0ddd/h6d0ifuKfQ-XuBLdzXF9u_e23711b4766a4ff4bf57f9fc27b06829.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a9a0ddd/zee79KpWlLsfGHP_Ufm-U_f1716668f04544639d38c3e725c73749.jpg',
            },
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-to-video-1778682049111',
          type: 'image-to-video',
          position: { x: 3067.138, y: 2569.289 },
          origin: [0, 0.5],
          data: {
            type: 'image-to-video',
            label: 'Animate MLB Scene (Happy Horse)',
            config: {
              model: 'Happy Horse Image',
              prompt:
                'realistic sports broadcast video of a fan in a crowd, camera slowly zooms in as she is unaware of being on camera, fan adjusts hair or look around casually watching the match, or sips a drink, but all in a natural casual way',
              duration: '10',
              resolution: '720p',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_8fdb8bcb-6026-475c-904e-f75efbe20e36.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a9a0dbe/yS0v6NlfuZhZDFoAT4lqS_f7dfd6ce8cdd4d6face1b6c15ae843a4.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a9a0dbe/HHzg2qJnRC1sxERiax0lZ_55a24896b7b24c65b43db4aefd579baf.jpg',
            },
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
        {
          id: 'image-to-video-1778682999667',
          type: 'image-to-video',
          position: { x: 3056.988, y: 3164.396 },
          origin: [0, 0.5],
          data: {
            type: 'image-to-video',
            label: 'Animate Wimbledon Scene (Kling 3.0)',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                'realistic sports broadcast video of a fan in a crowd, camera slowly zooms in as she is unaware of being on camera, fan adjusts hair or look around casually watching the match, or sips a drink, but all in a natural casual way',
              duration: '8',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_274bace7-290a-4689-af2a-277ee49851dd.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a9a0e17/KdIYktFnamrBa2qj265xd_146ae9850ee14732afe4707167f5d68d.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a9a0e17/coXXa-M_D-OwUcSVLui33_f922ef6591104b028f8b63cefd16d02b.jpg',
            },
            status: 'success',
          },
          measured: { width: 704, height: 398 },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778679297993image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778679297993',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778681366221image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778681366221',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778681437688image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778681437688',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1778679297993image-image-to-video-1778681772477image',
          source: 'image-edit-1778679297993',
          target: 'image-to-video-1778681772477',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778681994696image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778681994696',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778682019820image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778682019820',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1778681366221image-image-to-video-1778682049111image',
          source: 'image-edit-1778681366221',
          target: 'image-to-video-1778682049111',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1778681994696image-image-to-video-1778682271923image',
          source: 'image-edit-1778681994696',
          target: 'image-to-video-1778682271923',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778682437952image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778682437952',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778682458335image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778682458335',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778682537622image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778682537622',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1778666617587image-image-edit-1778682581742image',
          source: 'upload-1778666617587',
          target: 'image-edit-1778682581742',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1778682437952image-image-to-video-1778682999667image',
          source: 'image-edit-1778682437952',
          target: 'image-to-video-1778682999667',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'ai-animated-short-film',
    name: 'AI Animated Short Film – Story to 15-Second Video',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/original_29e2f134-735b-413c-b923-2893c9e9db46.mp4',
    description:
      'Turn a story idea into a complete 15-second animated short film. Upload character references, write your story, and the workflow generates character sheets, a 12-shot cinematic storyboard, and the final video — all with consistent characters, environment and style.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'note-1778149072841',
          type: 'note',
          position: { x: -992.261, y: 102.137 },
          data: {
            type: 'note',
            label: 'About this workflow',
            config: {
              note: '<h1>AI Animated Short Film – Story to Video</h1><p><strong>Turn a story idea into a complete 15-second animated short film in minutes.</strong></p><p>Upload your character references, write a short description for each, and add your story. The workflow builds the full production around it — character sheets, color palette, a 12-shot cinematic storyboard and the final animated video — all visually consistent, all ready in one pass.</p>',
            },
          },
        },
        {
          id: 'group-1778149038010',
          type: 'group',
          position: { x: -182.922, y: -215.679 },
          style: { width: 1009.386, height: 1957.006 },
          data: {
            color: 'green',
            label: 'Characters & Setting',
            style: { width: 1009.386, height: 1957.006 },
          },
        },
        {
          id: 'note-1778148681931',
          type: 'note',
          position: { x: 25.028, y: 19.065 },
          parentId: 'group-1778149038010',
          data: {
            type: 'note',
            label: 'Step 1 – Add Characters & Setting',
            config: {
              note: "<h1>Step 1 – Add Characters & Setting</h1><p>Upload an image and a short description for each character. Describe who they are, what their setting looks like, and which props matter most. That's everything the workflow needs to start the production.</p>",
            },
          },
        },
        {
          id: 'upload-1778148387282',
          type: 'upload',
          position: { x: 542.56, y: 324.825 },
          parentId: 'group-1778149038010',
          data: {
            type: 'upload',
            label: 'Milo, the Curious Inventor',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/a4a51803-2b4b-4686-bb07-95011b9ee7db.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/a4a51803-2b4b-4686-bb07-95011b9ee7db.png',
          },
        },
        {
          id: 'input-1778148851303',
          type: 'input',
          position: { x: 557.866, y: 40.221 },
          parentId: 'group-1778149038010',
          data: {
            type: 'input',
            label: "Milo's",
            config: {
              value:
                'Milo is a bright, optimistic young inventor who loves fixing tiny magical machines and exploring hidden places. His setting is a cozy miniature workshop at the edge of a soft needle-felted forest, with warm golden light, mossy textures, wooden tables, and a handcrafted stop-motion feel. His key props are brass goggles, a small tool satchel, tiny gears, stitched pockets, and handmade repair tools.',
            },
            result:
              'Milo is a bright, optimistic young inventor who loves fixing tiny magical machines and exploring hidden places. His setting is a cozy miniature workshop at the edge of a soft needle-felted forest, with warm golden light, mossy textures, wooden tables, and a handcrafted stop-motion feel. His key props are brass goggles, a small tool satchel, tiny gears, stitched pockets, and handmade repair tools.',
            status: 'success',
          },
        },
        {
          id: 'upload-1778148771266',
          type: 'upload',
          position: { x: 527.468, y: 1286.896 },
          parentId: 'group-1778149038010',
          data: {
            type: 'upload',
            label: 'Nia, the Lantern Gardener',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/9122a0f7-7567-44aa-963f-bbf31ff109b5.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/9122a0f7-7567-44aa-963f-bbf31ff109b5.png',
          },
        },
        {
          id: 'input-1778148908210',
          type: 'input',
          position: { x: 537.518, y: 1013.838 },
          parentId: 'group-1778149038010',
          data: {
            type: 'input',
            label: "Nia's",
            config: {
              value:
                'Nia is a calm, brave forest guide who grows and protects magical glowing plants. Her setting is a dreamy felted woodland garden at dusk, filled with soft moss, wool leaves, tiny mushrooms, warm lantern light, and floating fireflies. Her key props are a glowing acorn lantern, an ochre headband, stitched leaf patches on her coat, and small forest-gardening tools.',
            },
            result:
              'Nia is a calm, brave forest guide who grows and protects magical glowing plants. Her setting is a dreamy felted woodland garden at dusk, filled with soft moss, wool leaves, tiny mushrooms, warm lantern light, and floating fireflies. Her key props are a glowing acorn lantern, an ochre headband, stitched leaf patches on her coat, and small forest-gardening tools.',
            status: 'success',
          },
        },
        {
          id: 'group-1778149684583',
          type: 'group',
          position: { x: 1632.833, y: -76.889 },
          style: { width: 904.924, height: 1731.949 },
          data: {
            color: 'orange',
            label: 'Character Sheets',
            style: { width: 904.924, height: 1731.949 },
          },
        },
        {
          id: 'note-1778149104665',
          type: 'note',
          position: { x: 106.309, y: 116.925 },
          parentId: 'group-1778149684583',
          data: {
            type: 'note',
            label: 'Step 2 – Generate Character Sheets',
            config: {
              note: '<h1>Step 2 – Generate Character Sheets</h1><p>From your references, the workflow produces a detailed character sheet for each role — multiple angles, expressions, props and a unified color palette. Every visual the story needs, defined before the storyboard is drawn.</p>',
            },
          },
        },
        {
          id: 'image-edit-1778149154889',
          type: 'image-edit',
          position: { x: 103.329, y: 544.289 },
          parentId: 'group-1778149684583',
          data: {
            type: 'image-edit',
            label: 'Character Sheet – Milo',
            config: {
              useHD: false,
              prompt:
                "Make a character sheet with Milo. Show his personality based on @Milo's . White background. Photo realistic.",
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/37204035-1947-4d11-8269-0fafe0cb9f62.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1778149298083',
          type: 'image-edit',
          position: { x: 106.215, y: 1180.607 },
          parentId: 'group-1778149684583',
          data: {
            type: 'image-edit',
            label: 'Character Sheet – Nia',
            config: {
              useHD: false,
              prompt:
                "Make a character sheet with Nia. Show her personality based on @Nia's . White background. Photo realistic.",
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/6b0d9626-36de-454f-b53f-f9eaa5e8968e.png',
            status: 'success',
          },
        },
        {
          id: 'group-1778150801945',
          type: 'group',
          position: { x: 3247.925, y: 160.464 },
          style: { width: 1508.42, height: 1238.208 },
          data: {
            color: 'pink',
            label: 'Cinematic Storyboard',
            style: { width: 1508.42, height: 1238.208 },
          },
        },
        {
          id: 'note-1778149555899',
          type: 'note',
          position: { x: 107.343, y: 98.524 },
          parentId: 'group-1778150801945',
          data: {
            type: 'note',
            label: 'Step 3 – Generate the Cinematic Storyboard',
            config: {
              note: '<h1>Step 3 – Generate the Cinematic Storyboard</h1><p>Using your story and character sheets, the workflow generates a 12-panel storyboard — shot type, camera movement, timing and dialogue for every cut. Cinematically composed and ready to animate.</p>',
            },
          },
        },
        {
          id: 'input-1778149460216',
          type: 'input',
          position: { x: 128.139, y: 749.455 },
          parentId: 'group-1778150801945',
          data: {
            type: 'input',
            label: 'Story',
            config: {
              value:
                'In a tiny needle-felted forest at golden dusk, Milo the curious inventor carefully repairs a broken glowing acorn lantern while Nia the brave lantern gardener watches the shadows grow around them; just as the last spark fades, Milo tightens a tiny brass gear, Nia gently blows on the lantern, and warm golden light bursts out, waking hundreds of soft wool fireflies that swirl around them like a magical constellation; the camera starts in a close-up on Milo’s hands, pulls back to reveal both characters smiling in wonder, then rises slowly above the miniature forest as the glowing fireflies form a bright path into the distance, creating a cozy, magical, stop-motion-inspired ending.',
            },
            result:
              'In a tiny needle-felted forest at golden dusk, Milo the curious inventor carefully repairs a broken glowing acorn lantern while Nia the brave lantern gardener watches the shadows grow around them; just as the last spark fades, Milo tightens a tiny brass gear, Nia gently blows on the lantern, and warm golden light bursts out, waking hundreds of soft wool fireflies that swirl around them like a magical constellation; the camera starts in a close-up on Milo’s hands, pulls back to reveal both characters smiling in wonder, then rises slowly above the miniature forest as the glowing fireflies form a bright path into the distance, creating a cozy, magical, stop-motion-inspired ending.',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1778149372971',
          type: 'image-edit',
          position: { x: 726.191, y: 235.776 },
          parentId: 'group-1778150801945',
          data: {
            type: 'image-edit',
            label: '12-Panel Storyboard',
            config: {
              useHD: false,
              prompt:
                'Create a storyboard from @Story, using characters as strict visual references — character design, environment, props, colors and style must match the references exactly in every panel. Generate 12 shots for a 15-second animated short. Avoid shots that are too similar to each other. Each panel must include: shot number, shot type, camera movement, duration in seconds, visual description, and dialogue or action line. Not every shot needs characters. Ensure cinematic composition and variety of angles. Total runtime must add up to exactly 15 seconds.',
              editModel: 'OpenAI GPT Image 2.5 Flare Medium',
              dimensions: 'auto',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d3cb770e-b703-4181-8c86-be5233d05fdb.png',
            status: 'success',
          },
        },
        {
          id: 'group-1778151213018',
          type: 'group',
          position: { x: 5902.401, y: 407.947 },
          style: { width: 857.028, height: 905.34 },
          data: {
            color: 'purple',
            label: 'Final Animated Video',
            style: { width: 857.028, height: 905.34 },
          },
        },
        {
          id: 'note-1778150856241',
          type: 'note',
          position: { x: 229.551, y: 71.631 },
          parentId: 'group-1778151213018',
          data: {
            type: 'note',
            label: 'Step 4 – Render the 15-Second Animated Short',
            config: {
              note: '<h1>Step 4 – Render the 15-Second Animated Short</h1><p>The storyboard becomes a finished animated video — every shot, every cut, every second. Consistent characters, environment and lighting from the first frame to the last.</p>',
            },
          },
        },
        {
          id: 'references-to-video-1778150634829',
          type: 'references-to-video',
          position: { x: 89.473, y: 438.103 },
          parentId: 'group-1778151213018',
          data: {
            type: 'references-to-video',
            label: 'Animate the Short Film',
            config: {
              model: 'Seedance 2.0 Reference',
              prompt: '',
              duration: '15',
              aspectRatio: '16:9',
              referenceType: 'character',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_29e2f134-735b-413c-b923-2893c9e9db46.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a993e46/2OGtclwbJ6PW2BYbh1K3c_3974db2a03bb4adb9711ec63cdc0b21e.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a993e46/-wvyAUUiusrAEyBUutqzx_a2eef26a88534981a9ad463838cd2581.jpg',
            },
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1778148387282image-image-edit-1778149154889image',
          source: 'upload-1778148387282',
          target: 'image-edit-1778149154889',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1778148851303text-image-edit-1778149154889prompt',
          source: 'input-1778148851303',
          target: 'image-edit-1778149154889',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__upload-1778148771266image-image-edit-1778149298083image',
          source: 'upload-1778148771266',
          target: 'image-edit-1778149298083',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1778148908210text-image-edit-1778149298083prompt',
          source: 'input-1778148908210',
          target: 'image-edit-1778149298083',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1778149154889image-image-edit-1778149372971image',
          source: 'image-edit-1778149154889',
          target: 'image-edit-1778149372971',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1778149298083image-image-edit-1778149372971image',
          source: 'image-edit-1778149298083',
          target: 'image-edit-1778149372971',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1778149460216text-image-edit-1778149372971prompt',
          source: 'input-1778149460216',
          target: 'image-edit-1778149372971',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1778149372971image-references-to-video-1778150634829references',
          source: 'image-edit-1778149372971',
          target: 'references-to-video-1778150634829',
          sourceHandle: 'image',
          targetHandle: 'references',
        },
        {
          id: 'xy-edge__image-edit-1778149154889image-references-to-video-1778150634829references',
          source: 'image-edit-1778149154889',
          target: 'references-to-video-1778150634829',
          sourceHandle: 'image',
          targetHandle: 'references',
        },
        {
          id: 'xy-edge__image-edit-1778149298083image-references-to-video-1778150634829references',
          source: 'image-edit-1778149298083',
          target: 'references-to-video-1778150634829',
          sourceHandle: 'image',
          targetHandle: 'references',
        },
      ],
    },
  },
  {
    id: 'realtor-listing-video-virtual-staging',
    name: 'Real Estate Listing Video – Virtual Staging & Cinematic Tour',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/667f732b-15a9-4e60-8eb8-049988da7031.mp4',
    description:
      'Turn a single room photo into a listing-ready video: clear out the clutter, virtually stage the space in any design style, and produce cinematic walkthroughs that help homes sell faster.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-1777432279928',
          type: 'note',
          position: { x: -69.193, y: 147.571 },
          data: {
            type: 'note',
            label: 'About this workflow',
            config: {
              note: '<h1>Real Estate Listing Video</h1><p><strong>Turn one room photo into a listing-ready video that helps homes sell faster.</strong></p><ol><li><p><strong>Upload</strong> a photo of the room you want to showcase.</p></li><li><p><strong>Empty the Room</strong> – AI removes existing furniture so buyers see the true space.</p></li><li><p><strong>Generate Style Prompt</strong> – AI writes a detailed design brief (Scandinavian, by default).</p></li><li><p><strong>Apply New Style</strong> – AI virtually stages the empty room based on that prompt.</p></li><li><p><strong>Animate</strong> the staged room with a furniture-reveal transition and three cinematic walkthroughs.</p></li></ol>',
            },
          },
        },
        {
          id: 'note-1777432224041',
          type: 'note',
          position: { x: 673.879, y: -25.26 },
          data: {
            type: 'note',
            label: 'Step 1 – Upload Photo',
            config: {
              note: '<h1>Upload Room Photo</h1><p>Replace the sample image with a photo of the <strong>listing you want to stage</strong>. Wide-angle, well-lit shots produce the best results.</p>',
            },
          },
        },
        {
          id: 'upload-1777296132449',
          type: 'upload',
          position: { x: 478.691, y: 121.032 },
          data: {
            type: 'upload',
            label: 'Upload Room Photo',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/838d894d-e76c-45a4-aef6-72d87fbfaa9f.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/838d894d-e76c-45a4-aef6-72d87fbfaa9f.jpg',
          },
        },
        {
          id: 'note-1777359404028',
          type: 'note',
          position: { x: 2528.163, y: -880.242 },
          data: {
            type: 'note',
            label: 'Step 2 – Empty the Room',
            config: {
              note: '<h1>Make the Room Empty &amp; Brighter</h1><p><strong>AI removes all furniture, decor, and movable items</strong> while keeping walls, floors, ceiling, windows, doors, built-in features, and light fixtures intact. The result is a clean, bright canvas perfect for virtual staging.</p>',
            },
          },
        },
        {
          id: 'image-edit-1777296160275',
          type: 'image-edit',
          position: { x: 2374.534, y: -592.856 },
          data: {
            type: 'image-edit',
            label: 'Empty the Room',
            config: {
              useHD: false,
              prompt:
                'Remove all furniture, decor, and movable items from this room. Keep the room structure intact: walls, floors, ceiling, windows, doors, built-in features, and light fixtures. Show the bare empty room with clean floors and walls. Professional real estate photo',
              editModel: 'Nano Banana 2',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/6dd28aa5-8bd4-44fd-bb88-0051bb5ed4d1.png',
            status: 'success',
          },
        },
        // ── Virtual Staging Group ──
        {
          id: 'group-1777433505352',
          type: 'group',
          position: { x: 1815.005, y: 771.225 },
          style: { width: 1559.938, height: 851.527 },
          data: {
            color: 'pink',
            label: 'Virtual Staging',
            style: {
              width: 1559.938,
              height: 851.527,
            },
          },
        },
        {
          id: 'note-1777432368962',
          type: 'note',
          position: { x: 880.25, y: 557.129 },
          parentId: 'group-1777433505352',
          data: {
            type: 'note',
            label: 'Step 3 – Choose a Design Style',
            config: {
              note: '<h1>Transform to a New Style</h1><p><strong>Text Generation writes a detailed interior-design brief.</strong></p><p>Edit its prompt to swap styles, e.g. <em>"Mid-Century Modern"</em>, <em>"Japandi"</em>, or <em>"Industrial Loft"</em>. The output is fed directly into the Image to Image node as the prompt.</p>',
            },
          },
        },
        {
          id: 'text-generation-1777296436249',
          type: 'text-generation',
          position: { x: 100.418, y: 339.447 },
          parentId: 'group-1777433505352',
          data: {
            type: 'text-generation',
            label: 'Generate Style Prompt',
            config: {
              model: 'claude-sonnet-4-6',
              prompt:
                'generate prompt to create Scandinavian interior design for this room. Return only prompt',
              systemPrompt: '',
            },
            result:
              'A bright and airy Scandinavian interior design for a spacious high-rise apartment living room with light blonde hardwood floors and white walls, featuring a minimalist light gray linen sectional sofa with a low profile, a simple round light oak coffee table, a soft white and muted sage green wool area rug, sheer white linen curtains framing the floor-to-ceiling windows with city views, a sleek matte black pendant lamp replacing the existing ceiling fixture, a handful of potted indoor plants including a fiddle leaf fig and small succulents, a simple open shelving unit in natural birch wood displaying minimal decorative objects and books, soft warm ambient lighting, cozy knitted throw blankets and cushions in muted tones of beige, gray, and dusty blue, a minimalist dining area with a light oak table and matching Wishbone chairs, hygge-inspired decor with candles and natural textures, ultra-clean lines, functional furniture, neutral color palette with subtle warm accents, photorealistic, interior design photography, 8k, natural daylight',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1777296420063',
          type: 'image-edit',
          position: { x: 771.614, y: 101.21 },
          parentId: 'group-1777433505352',
          data: {
            type: 'image-edit',
            label: 'Apply New Style',
            config: {
              useHD: false,
              prompt: '@Generate Style Prompt',
              editModel: 'Nano Banana 2',
              dimensions: 'auto',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7286f97c-5730-41a0-9f94-6c7027bd95bf.png',
            status: 'success',
          },
        },
        // ── Cinematic Tours Group ──
        {
          id: 'group-1777432490566',
          type: 'group',
          position: { x: 4024.571, y: -1508.602 },
          style: { width: 1528.041, height: 2174.877 },
          data: {
            color: 'orange',
            label: 'Cinematic Tours',
            style: {
              width: 1528.041,
              height: 2174.877,
            },
          },
        },
        {
          id: 'note-1777432448618',
          type: 'note',
          position: { x: 970.069, y: 937.599 },
          parentId: 'group-1777432490566',
          data: {
            type: 'note',
            label: 'Three Cinematic Tours',
            config: {
              note: '<h1>Three Camera Moves, Three Models</h1><p>The same staged room is animated three different ways.</p><ul><li><p><strong>Furniture Fly-Over</strong> – focuses on each piece of furniture in turn.</p></li><li><p><strong>Forward Dolly</strong> – glides into the living room.</p></li><li><p><strong>Dolly &amp; Pan</strong> – pushes toward the sofa, then pans to the table.</p></li></ul><p>Pick the move and model that fit your listing.</p>',
            },
          },
        },
        {
          id: 'image-to-video-1777301293339',
          type: 'image-to-video',
          position: { x: 69.587, y: 141.218 },
          parentId: 'group-1777432490566',
          data: {
            type: 'image-to-video',
            label: 'Furniture Fly-Over (Grok)',
            config: {
              model: 'Grok Imagine Image',
              prompt: 'Smooth fly-over focusing on each item of furniture',
              duration: '10',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7113386d-0195-4abd-8a26-debc03d09680.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a97f278/SHfV4K5W2HuaT9Cqahd_5_3eb6a8a7b2af4c5888a219f1831f1caa.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a97f278/z5ivGinjXQuNhFzAutdoU_5c92ec3c24ae4da5bf85fb78a9dbda5d.jpg',
            },
            status: 'success',
          },
        },
        {
          id: 'image-to-video-1777297431489',
          type: 'image-to-video',
          position: { x: 58.655, y: 907.189 },
          parentId: 'group-1777432490566',
          data: {
            type: 'image-to-video',
            label: 'Forward Dolly (Seedance)',
            config: {
              model: 'Seedance 1.5 Pro Image',
              prompt: 'Smooth forward dolly shot into the living room',
              duration: '5',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/153db24b-8c1b-407d-a45a-dfcb49469a0e.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a97f15c/dFKeoi52XJE_F_y2aBMP5_86270e6fad0341cc9cb087df8a79db3f.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a97f15c/NJ0OLIgtYJWIUNTdypIIc_17113c7acb394b7a93fa0aed63c948e9.jpg',
            },
            status: 'success',
          },
        },
        {
          id: 'image-to-video-1777297498994',
          type: 'image-to-video',
          position: { x: 63.822, y: 1600.48 },
          parentId: 'group-1777432490566',
          data: {
            type: 'image-to-video',
            label: 'Dolly & Pan (Veo 3.1)',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'Smooth forward dolly shot to the sofa and then slow pan to the table further',
              duration: '8s',
              resolution: '1080p',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7bb8124e-23eb-4a69-860f-a1bf7ee9de51.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a97f17a/fSh_uG0y-AosGWeUegGrh_4d806ac6c42e4ae3a66e4ef80a6481bc.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a97f17a/GookxFsyKWoYu1ADPPXBq_52607371e1f346c1a43e31d7256ecf25.jpg',
            },
            status: 'success',
          },
        },
        // ── Reveal & Story Group ──
        {
          id: 'group-1777433569759',
          type: 'group',
          position: { x: 4014.195, y: 1390.014 },
          style: { width: 1984.133, height: 883.11 },
          data: {
            color: 'green',
            label: 'Reveal & Story',
            style: {
              width: 1984.133,
              height: 883.11,
            },
          },
        },
        {
          id: 'note-1777432534515',
          type: 'note',
          position: { x: 822.641, y: 569.733 },
          parentId: 'group-1777433569759',
          data: {
            type: 'note',
            label: 'Step 4 – Furniture Reveal Transition',
            config: {
              note: '<h1>From Empty Room to Designed Space</h1><p><strong>First-Last Frame Video</strong> animates the transition from the empty room (first frame) to the staged room (last frame). Each piece of furniture appears one by one, like a time-lapse of the design process.</p><p>The last frame is then handed to <strong>Family Moves In</strong> to extend the story and help buyers picture themselves in the home.</p>',
            },
          },
        },
        {
          id: 'first-last-frame-video-1777296924937',
          type: 'first-last-frame-video',
          position: { x: 103.773, y: 74.697 },
          parentId: 'group-1777433569759',
          data: {
            type: 'first-last-frame-video',
            label: 'Furniture Reveal Transition',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                'living room transition from empty to room with furniture. Each item appear one by one.',
              duration: '8',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_df4bfdfb-0c93-4c80-a43e-4cb05293eb4f.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a97f123/dJC4tnvwWW_RTU8NkzF27_7bd07fcd59e24800a7b6dbacbeb34771.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a97f123/d4gyMv5XM40Rg908LTkyu_a387c08fbbf84dfcb09825dc6e13b1f3.jpg',
            },
            status: 'success',
          },
        },
        {
          id: 'image-to-video-1777298366253',
          type: 'image-to-video',
          position: { x: 1176.361, y: 77.924 },
          parentId: 'group-1777433569759',
          data: {
            type: 'image-to-video',
            label: 'Family Moves In',
            config: {
              model: 'Seedance 1.5 Pro Image',
              prompt:
                'Happy Singaporean family is coming inside and sit all on sofa. Camera is static.',
              duration: '8',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b7e205f2-dd0a-442b-afb2-9f3f2114a686.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a97f266/MzEEYTnG90k_QTuy6dbTt_41567b21fb5145228f97f44a0b403b98.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a97f266/xOqjcpxnRIMW6kVmN5ftU_8ae04debf41d4828908fe1ca26b5630c.jpg',
            },
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1777296132449image-image-edit-1777296160275image',
          source: 'upload-1777296132449',
          target: 'image-edit-1777296160275',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1777296160275image-image-edit-1777296420063image',
          source: 'image-edit-1777296160275',
          target: 'image-edit-1777296420063',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1777296160275image-text-generation-1777296436249image',
          source: 'image-edit-1777296160275',
          target: 'text-generation-1777296436249',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__text-generation-1777296436249text-image-edit-1777296420063prompt',
          source: 'text-generation-1777296436249',
          target: 'image-edit-1777296420063',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1777296420063image-first-last-frame-video-1777296924937last-frame',
          source: 'image-edit-1777296420063',
          target: 'first-last-frame-video-1777296924937',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__image-edit-1777296160275image-first-last-frame-video-1777296924937first-frame',
          source: 'image-edit-1777296160275',
          target: 'first-last-frame-video-1777296924937',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__image-edit-1777296420063image-image-to-video-1777297498994image',
          source: 'image-edit-1777296420063',
          target: 'image-to-video-1777297498994',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1777296420063image-image-to-video-1777297431489image',
          source: 'image-edit-1777296420063',
          target: 'image-to-video-1777297431489',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__first-last-frame-video-1777296924937last-frame-image-to-video-1777298366253image',
          source: 'first-last-frame-video-1777296924937',
          target: 'image-to-video-1777298366253',
          sourceHandle: 'last-frame',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1777296420063image-image-to-video-1777301293339image',
          source: 'image-edit-1777296420063',
          target: 'image-to-video-1777301293339',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'image-to-storyboard-cinematic',
    name: 'Image to Cinematic Storyboard',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/original_7cfcd7cf-0656-45cf-99a8-60be95131ede.mp4',
    description:
      'Turn a single reference image into a full cinematic storyboard with AI-generated keyframes, high-resolution upscales, and video clips for each scene.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'image-edit-1771823144473',
          data: {
            type: 'image-edit',
            label: 'Master Contact Sheet',
            config: {
              useHD: false,
              prompt: '@Storyboard Director',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/20b4fbe3-6627-4b1d-b46a-a4afeff8ccfe.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          position: {
            x: 908.5104319045233,
            y: -1101.39458647721,
          },
          selected: false,
        },
        {
          id: 'note-1771827790005',
          data: {
            type: 'note',
            label: 'Upscale Prompt Guide',
            config: {
              note: '<h1>Upscale Prompt Guide</h1><p></p><p>Each keyframe upscale node uses a prompt to <strong>isolate and enhance one panel</strong> from the storyboard grid. Here are the key rules:</p><p></p><ul><li><p><strong>Do not</strong> change composition, framing, camera angle, or perspective.</p></li><li><p><strong>Do not</strong> add, remove, or modify any characters, props, objects, or background elements.</p></li><li><p><strong>Do not</strong> change lighting, color grading, time of day, or mood.</p></li><li><p><strong>Do not</strong> invent new details that are not clearly suggested by the original panel.</p></li><li><p><strong>Ignore</strong> any labels, text, or grid borders — they must not appear in the final image.</p></li></ul><p></p><p><strong>Your only job is to:</strong></p><ul><li><p>Clean up artifacts</p></li><li><p>Sharpen existing details</p></li><li><p>Improve resolution and texture quality</p></li><li><p>Keep the image as a faithful, higher-quality version of that specific panel</p></li></ul>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 652,
          },
          position: {
            x: 1515.9918472456075,
            y: -1937.8967536084972,
          },
          selected: false,
        },
        {
          id: 'note-1771830119359',
          data: {
            type: 'note',
            label: 'How to Use This Template',
            config: {
              note: '<h1>Image to Cinematic Storyboard</h1><p></p><p>Turn <strong>one reference image</strong> into a complete cinematic short sequence with 9–12 keyframes, high-resolution upscales, and AI-generated video clips.</p><p></p><h2>How it works</h2><ol><li><p><strong>Upload your image</strong> — Open the <strong>Reference Image</strong> group and select or upload a photo. This is the single image the AI will use to build the entire storyboard.</p></li><li><p><strong>Run the Storyboard Director</strong> — This text-generation node analyzes your image and writes a full cinematic breakdown: subjects, environment, emotional arc, and 9–12 keyframe descriptions.</p></li><li><p><strong>Generate the Master Contact Sheet</strong> — The image-to-image node creates a single storyboard grid with all keyframes in one image.</p></li><li><p><strong>Upscale each keyframe</strong> — Each Scene group contains an upscale node that isolates and enhances one panel from the contact sheet into a high-resolution standalone image.</p></li><li><p><strong>Animate each scene</strong> — The image-to-video node in each Scene group generates a 3–4 second video clip from the upscaled keyframe.</p></li><li><p><strong>Generate a soundtrack</strong> — Use the Background Score node in the Soundtrack group to create cinematic music.</p></li><li><p><strong>Assemble in Video Editor</strong> — Download all video clips and the music track, then combine them in our <a target="_blank" rel="noopener noreferrer nofollow" href="https://easy-peasy.ai/ai-video-editor">Video Editor</a> to produce the final sequence.</p></li></ol><p></p><p><strong>Tip:</strong> If a keyframe doesn\'t look right, tweak the upscale prompt or regenerate it. You can also edit the Storyboard Director prompt to change the cinematic style, mood, or number of scenes.</p>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 916,
          },
          position: {
            x: -1821.6932907920045,
            y: -1170.8920127219415,
          },
          selected: false,
        },
        {
          id: 'note-1771830362057',
          data: {
            type: 'note',
            label: 'About Keyframe Upscaling',
            config: {
              note: '<h1>Keyframe Upscaling</h1><p></p><p>The <strong>Master Contact Sheet</strong> generates all keyframes in a single grid image. Each <strong>Upscale</strong> node then:</p><p></p><ol><li><p>Takes the grid image as input</p></li><li><p>Isolates the specific panel for that scene</p></li><li><p>Enhances it to a high-resolution standalone image</p></li></ol><p></p><p>The upscaled image is then fed into the <strong>Animate</strong> node to generate the video clip.</p>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 360,
          },
          position: {
            x: 1030.1924783155978,
            y: -596.425923545715,
          },
          selected: false,
        },
        {
          id: 'text-generation-1771838677319',
          data: {
            type: 'text-generation',
            label: 'Extended Storyboard (KF10–12)',
            config: {
              model: 'gemini-3.1-pro-preview',
              prompt: '',
              systemPrompt: '',
            },
            result:
              'Here are the details for the three additional keyframes that resolve the narrative by showing Subject C taking action, followed by the suggested closing moral text.\n\n***\n\n### D) Keyframes (Extension: The Resolution)\n\n**[KF10 | 3.0s | MCU]**\n*   **Composition:** Returning to the perspective of looking through the horizontal blinds, but the foreground where the men stood is now empty and bathed in deep shadow. Subject C is framed directly in the center of the background.\n*   **Action/beat:** Subject C remains still for a beat, ensuring the men are gone. She then leans decisively toward her monitor. Her hands rise and begin to type rapidly. Her expression transitions from passive observer to resolute actor.\n*   **Camera:** Eye level. Slow, deliberate push-in past the empty foreground space, breaking through the visual barrier of the blinds.\n*   **Lens/DoF:** 85mm. Shallow DoF initially, racking focus sharply onto Subject C as the camera pushes forward.\n*   **Lighting & grade:** The cold cyan overheads remain, but her face is now intensely illuminated by the stark, bright white glow of her computer monitor, cutting through the shadows. \n*   **Sound/atmos:** The pulsing air-conditioning hum is overtaken by the sharp, rapid, rhythmic *clack-clack-clack* of a mechanical keyboard. \n\n**[KF11 | 2.0s | ECU]**\n*   **Composition:** Extreme close-up of Subject C’s hand resting on the keyboard. In the blurred background, the monitor displays a high-contrast corporate email interface with the subject line visible: *“URGENT: Confidential Whistleblower Report”*.\n*   **Action/beat:** Her index finger hovers for a fraction of a second over the "Enter" key, then presses it down with finality.\n*   **Camera:** Desk height, angled slightly up toward the monitor. Locked off.\n*   **Lens/DoF:** 100mm Macro. Extremely shallow DoF. Focus is pinpointed on the texture of the "Enter" key and her fingernail. \n*   **Lighting & grade:** High contrast. The silver of the keyboard reflects the harsh, clinical light of the screen. \n*   **Sound/atmos:** The heavy, resonant *CLACK* of the keystroke. A distinct, sterile digital *whoosh* signifying the message has been sent. The ominous background drone abruptly stops.\n\n**[KF12 | 4.0s | LS]**\n*   **Composition:** A wide shot from inside the sterile office, looking out through the glass partition into the main hallway. The thick window mullions frame the shot.\n*   **Action/beat:** Subject C, wearing a dark trench coat over her light blue shirt, walks confidently down the hallway away from the camera. Her posture is straight; a burden has been lifted. \n*   **Camera:** Static. A slow fade to black begins in the final 2 seconds.\n*   **Lens/DoF:** 35mm. Deep DoF. Everything is in sharp focus.\n*   **Lighting & grade:** The oppressive cyan/steel-blue cast is finally broken. At the far end of the hallway, a window lets in a distinct, warm slice of golden morning sunlight. Subject C walks toward the light, breaking the low-key darkness.\n*   **Sound/atmos:** The crisp, even sound of her heels echoing down the corridor. The faint, ambient sound of city traffic begins to filter in—the world returning to normal. \n\n***\n\n### E) Closing Moral (On-Screen Text)\n\n*(As KF12 fades to black, stark white, minimalist text fades up onto the center of the screen, holding for 4 seconds before cutting to black.)*\n\n**Closing Text Suggestion:**\n\n> **CORRUPTION THRIVES IN THE SHADOWS.**\n> **INTEGRITY BRINGS IT TO LIGHT.**\n> \n> *If you witness misconduct, don\'t just watch.*\n> *Break the silence. Report it.*',
            status: 'success',
          },
          type: 'text-generation',
          dragging: false,
          measured: {
            width: 404,
            height: 363,
          },
          position: {
            x: 842.2928701722115,
            y: 589.5618934293911,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771839424286',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 10',
            config: {
              useHD: false,
              prompt:
                '[KF10 | 3.0s | MCU]\n*   Composition: Returning to the perspective of looking through the horizontal blinds, but the foreground where the men stood is now empty and bathed in deep shadow. Subject C is framed directly in the center of the background.\n*   Action/beat: Subject C remains still for a beat, ensuring the men are gone. She then leans decisively toward her monitor. Her hands rise and begin to type rapidly. Her expression transitions from passive observer to resolute actor.\n*   Camera: Eye level. Slow, deliberate push-in past the empty foreground space, breaking through the visual barrier of the blinds.\n*   Lens/DoF: 85mm. Shallow DoF initially, racking focus sharply onto Subject C as the camera pushes forward.\n*   Lighting & grade: The cold cyan overheads remain, but her face is now intensely illuminated by the stark, bright white glow of her computer monitor, cutting through the shadows. \n*   Sound/atmos: The pulsing air-conditioning hum is overtaken by the sharp, rapid, rhythmic clack-clack-clack of a mechanical keyboard.',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/f8e82fc3-6239-410e-8091-32d2b67555f4.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          position: {
            x: 2167.2043373455676,
            y: 773.464022293792,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771839500792',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 11',
            config: {
              useHD: false,
              prompt:
                '[KF11 | 2.0s | ECU]\n*   Composition: close-up of woman right hand resting on the keyboard. Her index finger up from the Enter key about to click it. In the blurred background, the monitor displays a high-contrast corporate email interface with the subject line visible: “URGENT: Confidential Whistleblower Report”.\n\n*   Camera: Desk height, angled slightly up toward the monitor. Locked off.\n*   Lighting & grade: High contrast. The silver of the keyboard reflects the harsh, clinical light of the screen. \n',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a49c1a8f-4874-4cd2-a12a-fa45bea3dc61.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          position: {
            x: 4359.41900253998,
            y: 765.1060453134941,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771839579673',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 12',
            config: {
              useHD: false,
              prompt:
                '[KF12 | 4.0s | LS]\n*   Composition: A wide shot from inside the sterile office, looking out through the glass partition into the main hallway. The thick window mullions frame the shot.\n*   Action/beat: Subject C, wearing a dark trench coat over her light blue shirt, walks confidently down the hallway away from the camera. Her posture is straight; a burden has been lifted. \n*   Camera: Static. A slow fade to black begins in the final 2 seconds.\n*   Lens/DoF: 35mm. Deep DoF. Everything is in sharp focus.\n*   Lighting & grade: The oppressive cyan/steel-blue cast is finally broken. At the far end of the hallway, a window lets in a distinct, warm slice of golden morning sunlight. Subject C walks toward the light, breaking the low-key darkness.\n*   Sound/atmos: The crisp, even sound of her heels echoing down the corridor. The faint, ambient sound of city traffic begins to filter in—the world returning to normal.',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/51ebd171-e26d-46c7-8156-8bd04a9cb7e6.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          position: {
            x: 6676.180131757541,
            y: 732.0025546023006,
          },
          selected: false,
        },
        {
          id: 'note-1772259807693',
          data: {
            type: 'note',
            label: 'Extended Scenes (KF10–12)',
            config: {
              note: '<h1>Extended Scenes (KF10–12)</h1><p></p><p>The <strong>Extended Storyboard</strong> node generates 3 additional keyframes (KF10, KF11, KF12) to extend the narrative beyond the initial 9 scenes. These are optional — use them to add a resolution or epilogue to your story.</p><p></p><p>Each extended scene follows the same pipeline: upscale from the contact sheet, then animate into a video clip.</p>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 336,
          },
          position: {
            x: 319.9351824340421,
            y: 613.8533976045468,
          },
          selected: false,
        },
        {
          id: 'group-1771829662694',
          data: {
            color: 'orange',
            label: 'Scene 1 – Wide Establishing Shot',
            style: {
              width: 1633.3639028146881,
              height: 567.9133661090045,
            },
          },
          type: 'group',
          style: {
            width: 1633.3639028146881,
            height: 567.9133661090045,
          },
          dragging: false,
          measured: {
            width: 1633,
            height: 568,
          },
          position: {
            x: 2111.0854784295525,
            y: -3474.756555361025,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771823894361',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 1',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF1 panel from the provided storyboard contact sheet (the panel labeled “LS (KF1)”). Crop to that panel only\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2814791a-3914-47a2-a411-5f2dd66400f2.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829662694',
          position: {
            x: 59.02691899067668,
            y: 54.09334446390585,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828017665',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 1',
            config: {
              model: 'Kling 3.0 Pro Image',
              prompt:
                '*   Composition: Replicates reference image. Framed perfectly symmetrically between two vertical black window mullions. The horizontal blinds cut across the entire frame. Subjects A and B in profile; Subject C out of focus in the deep background left.\n*   Action/beat: Subject B slowly lifts the manila envelope from the briefcase, holding it out. Subject A leans in.\n*   Camera: Eye level. Slow, imperceptible 5% push-in.\n*   Lens/DoF: 35mm. Medium DoF. Blinds in extreme foreground are slightly soft; the men are sharp.\n*   Lighting & grade: Cold overhead cyan. High contrast.\n*   Sound/atmos: Low, pulsing air-conditioning hum.',
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_f237b775-5087-44d9-a46d-b81c86ed52b9.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9871/xHI1u7pJMcbSVIqjaznPp_2a5ad76947ec419aa965194d3d60a251.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9871/rsC21h-jR6gbmVPxTy2K8_01b63b9a1fd446eaaaad294a3901e8e9.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 473,
          },
          parentId: 'group-1771829662694',
          position: {
            x: 880.2364690128911,
            y: 55.61064096683549,
          },
          selected: false,
        },
        {
          id: 'group-1771829701943',
          data: {
            color: 'orange',
            label: 'Scene 2 – Over-the-Shoulder',
            style: {
              width: 1565.3072350418852,
              height: 564.1541212112552,
            },
          },
          type: 'group',
          style: {
            width: 1565.3072350418852,
            height: 564.1541212112552,
          },
          dragging: false,
          measured: {
            width: 1565,
            height: 564,
          },
          position: {
            x: 4403.1469390871125,
            y: -3454.0312223511914,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771823984336',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 2',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF2 panel from the provided storyboard contact sheet (the panel labeled “MCU (KF2)”). Crop to that panel only\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/950089c4-d972-4e52-bd7a-4b91c908d3c2.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829701943',
          position: {
            x: 20,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828196864',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 2',
            config: {
              model: 'Kling 3.0 Pro Image',
              prompt:
                "*[KF2 | 1.5s | MCU]*\n*   Composition: Clean over-the-shoulder (OTS) from behind Subject A, looking through the blinds at Subject B. Subject B's face is bisected by shadow.\n*   Action/beat: Subject B hesitates for a fraction of a second, his eyes flicking to Subject A's face before looking back at the envelope.\n*   Camera: Eye level, locked off.\n*   Lens/DoF: 85mm. Shallow DoF. Subject B is razor-sharp; Subject A's shoulder and the blinds are highly blurred.\n*   Lighting & grade: Harsh top-light catching the sweat/oil on Subject B's forehead.\n*   Sound/atmos: The crisp rustle of thick paper.",
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_f8eae11f-2fc2-441a-9cb4-a5945a0e4753.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9879/VSyF0RuRZziWa6XAJhOkJ_09899a8412c04977929b6d8f0bc52114.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9879/6qbKfuuEBU5tHu9aKZ98m_b36f78d85e9a440594fef065b77c8787.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 473,
          },
          parentId: 'group-1771829701943',
          position: {
            x: 814.2688740689864,
            y: 55.47805058330505,
          },
          selected: false,
        },
        {
          id: 'group-1771829777136',
          data: {
            color: 'orange',
            label: 'Scene 3 – Extreme Close-Up',
            style: {
              width: 1832.4681342278518,
              height: 566.5551204950266,
            },
          },
          type: 'group',
          style: {
            width: 1832.4681342278518,
            height: 566.5551204950266,
          },
          dragging: false,
          measured: {
            width: 1832,
            height: 567,
          },
          position: {
            x: 6421.2267668809745,
            y: -3402.7893836593767,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771824130193',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 3',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF3 panel from the provided storyboard contact sheet (the panel labeled “ECU (KF3)”). Crop to that panel only\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e6a0aa54-bc4e-4427-af56-33f4e5b59e54.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829777136',
          position: {
            x: 105,
            y: 55,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828248698',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 3',
            config: {
              model: 'Kling 3.0 Pro Image',
              prompt:
                "*[KF3 | 1.5s | ECU]*\n*   Composition: Extreme close-up on the physical exchange over the desk. Horizontal blind slats cut across the very top and bottom of the frame.\n*   Action/beat: Subject B's hand holds the envelope; Subject A's hand enters the frame and tightly grips the other side of it. Knuckles slightly white.\n*   Camera: Desk height, angled slightly down. Locked off.\n*   Lens/DoF: 100mm Macro. Extremely shallow DoF. Focus is dead center on the edge of the envelope and their overlapping fingers.\n*   Lighting & grade: Envelope provides the only warm, yellowish reflection in the cold blue scene.\n*   Sound/atmos: The sharp friction of skin on heavy paper.",
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_f8bd6904-89af-4849-bcd8-568c6ecd37f6.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f987e/CdJXinehKt8rikyTeMzLh_38ffa3f7fa1e45a297e1b416c9110e23.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f987e/e0tKAZW-2g4L-02aQ74R9_73b4b0b51cef4e5eba005891da2d8cc1.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 473,
          },
          parentId: 'group-1771829777136',
          position: {
            x: 963.4681342278518,
            y: 63.555120495026586,
          },
          selected: false,
        },
        {
          id: 'group-1771829819049',
          data: {
            color: 'orange',
            label: 'Scene 7 – Rack Focus Reveal',
            style: {
              width: 1698.8742144767702,
              height: 551.3759497144383,
            },
          },
          type: 'group',
          style: {
            width: 1698.8742144767702,
            height: 551.3759497144383,
          },
          dragging: false,
          measured: {
            width: 1699,
            height: 551,
          },
          position: {
            x: 2131.999498314286,
            y: -221.18972382339837,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771824371297',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 7',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF7 image from the provided storyboard contact sheet (the image labeled “MS to LS / Rack Focus (KF7)”). Crop to that image only (first image in third row)\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details. Only perform restoration/upscale: - reduce compression artifacts and noise while preserving natural film grain - sharpen existing edges and textures (suit fabric, hair, facial contours) without halos - improve clarity of blinds/shelving details without changing their design - maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/6332facb-806a-4df5-aa0c-f7eff95286d8.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829819049',
          position: {
            x: 20,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828609733',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 7',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '**[KF7 | 2.5s | MS to LS / Rack Focus]**\n*   Composition: Same framing as KF6.\n*   Action/beat: The turn. Subject A begins to step back. As he moves, the focus violently shifts past him.\n*   Camera: Eye level. Static.\n*   Lens/DoF: 50mm. Shallow DoF. Rack focus from the men in the midground to Subject C (the woman) in the deep background. The men become soft blurs.\n*   Lighting & grade: Subject C is illuminated by the eerie glow of her computer monitor (blue-white).\n*   Sound/atmos: A low, ominous synthesized drone begins to swell.',
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_65791553-f88b-4295-823c-c517130d8435.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f989a/as1g-j7KQ1w-8wjChjrBn_54ea9513987e45898b77ee32a154242f.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f989a/2yEw1G62EI92VaVqZOL1k_eac9eb51f71148f88f4c0b518fdd3737.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829819049',
          position: {
            x: 974.8742144767702,
            y: 59.37594971443829,
          },
          selected: false,
        },
        {
          id: 'group-1771829841997',
          data: {
            color: 'orange',
            label: 'Scene 8 – The Silent Witness',
            style: {
              width: 1714.9936824841643,
              height: 557.881580378469,
            },
          },
          type: 'group',
          style: {
            width: 1714.9936824841643,
            height: 557.881580378469,
          },
          dragging: false,
          measured: {
            width: 1715,
            height: 558,
          },
          position: {
            x: 4297.928406695645,
            y: -271.7338154454645,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771824420626',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 8',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF8 panel from the provided storyboard contact sheet (the panel labeled “CU (KF9)”). Crop to that panel only\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8279d301-e735-4569-853d-9bcae3e5fb9f.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829841997',
          position: {
            x: 20,
            y: 63.88158037846904,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828617598',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 8',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '**[KF8 | 2.5s | CU]**\n*   Composition: Telephoto punch-in directly on Subject C, framed between the blurred silhouettes of the two men. Horizontal blinds cast harsh, prison-like shadows across her face.\n*   Action/beat: She does not blink. She simply watches the exchange. Her expression is utterly unreadable.\n*   Camera: Eye level. Very slow, creeping push-in.\n*   Lens/DoF: 135mm. Shallow DoF. Only her eyes and the bridge of her nose are in sharp focus.\n*   Lighting & grade: High contrast. Monitor glow on her face, deep darkness around her.\n*   Sound/atmos: A single, muffled tick of the wall clock.',
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_e0a8e97b-d2e9-497a-9c76-07236798d2e0.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f98a1/HkMue-OibRi5D3teS6kny_88ca8f6a35444141b4e5cee16631a987.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f98a1/Wuu-IMzIBDyjrIm1a3v3W_0db12c68092440d784e6391dce73cd7d.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829841997',
          position: {
            x: 990.9936824841643,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771839732931',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 10',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '[KF10 | 3.0s | MCU]\n*   Composition: Returning to the perspective of looking through the horizontal blinds, but the foreground where the men stood is now empty and bathed in deep shadow. Subject C is framed directly in the center of the background.\n*   Action/beat: Subject C remains still for a beat, ensuring the men are gone. She then leans decisively toward her monitor. Her hands rise and begin to type rapidly. Her expression transitions from passive observer to resolute actor.\n*   Camera: Eye level. Slow, deliberate push-in past the empty foreground space, breaking through the visual barrier of the blinds.\n*   Lens/DoF: 85mm. Shallow DoF initially, racking focus sharply onto Subject C as the camera pushes forward.\n*   Lighting & grade: The cold cyan overheads remain, but her face is now intensely illuminated by the stark, bright white glow of her computer monitor, cutting through the shadows. \n*   Sound/atmos: The pulsing air-conditioning hum is overtaken by the sharp, rapid, rhythmic clack-clack-clack of a mechanical keyboard.',
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_7cfcd7cf-0656-45cf-99a8-60be95131ede.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9cf2/IwVrwfMjAkfQAN53W-1Xu_f3f529eb8a544eb887d7c78631a7ef64.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9cf2/oHiFXVwHNIJy0PRbpLsJr_83bc4ae09d9c490fada63d6682add692.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829841997',
          position: {
            x: -1174.1124385654548,
            y: 1048.959810129841,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771839742595',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 11',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '**[KF11 | 2.0s | ECU]** * Composition: Extreme close-up of Subject C’s hand resting on the keyboard. In the blurred background, the monitor displays a high-contrast corporate email interface with the subject line visible: “URGENT: Confidential Whistleblower Report”. * Action/beat: Her index finger hovers for a fraction of a second over the \"Enter\" key, then presses it down with finality. * Camera: Desk height, angled slightly up toward the monitor. Locked off. * Lens/DoF: 100mm Macro. Extremely shallow DoF. Focus is pinpointed on the texture of the \"Enter\" key and her fingernail. * Lighting & grade: High contrast. The silver of the keyboard reflects the harsh, clinical light of the screen. * Sound/atmos: The heavy, resonant CLACK of the keystroke. A distinct, sterile digital whoosh signifying the message has been sent. ',
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_82444840-1693-40d6-b7de-ca5879a2131d.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9d9c/aCAyyW9TR8_zI3t4fQEK__90bf4160defa41648539eba10f5242a0.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9d9c/orcej27kmGVK4mp-2I2oZ_80fe1dcc2b864411ae1bfbc069cce89c.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829841997',
          position: {
            x: 1021.6251904565506,
            y: 1044.2797708636522,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771839745171',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 12',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '[KF12 | 4.0s | LS]\n*   Composition: A wide shot from inside the sterile office, looking out through the glass partition into the main hallway. The thick window mullions frame the shot.\n*   Action/beat: Subject C, wearing a dark trench coat over her light blue shirt, walks confidently down the hallway Her posture is straight; a burden has been lifted. \n*   Camera: Static\n*   Lens/DoF: 35mm. Deep DoF. Everything is in sharp focus.\n*   Lighting & grade: The oppressive cyan/steel-blue cast is finally broken. At the far end of the hallway, a window lets in a distinct, warm slice of golden morning sunlight. Subject C walks toward the light, breaking the low-key darkness.\n*   Sound/atmos: The crisp, even sound of her heels echoing down the corridor. The faint, ambient sound of city traffic begins to filter in—the world returning to normal.',
              duration: '4',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_44a27652-91f7-4f4c-9987-5cf31de0f1a0.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9d1a/xK-XYCHP7qGlSo1oe2q6x_eb07df00552a4a4d9f887230eacd1a16.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9d1a/3o6qB1uoqimokbXzsUPyR_58ab1a2085424ae59f0b73cd2e00f903.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: true,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829841997',
          position: {
            x: 3291.00511430155,
            y: 971.9576815629905,
          },
          selected: false,
        },
        {
          id: 'group-1771829863338',
          data: {
            color: 'orange',
            label: 'Scene 9 – The Departure',
            style: {
              width: 1832.105382574412,
              height: 622.3517276831144,
            },
          },
          type: 'group',
          style: {
            width: 1832.105382574412,
            height: 622.3517276831144,
          },
          dragging: false,
          measured: {
            width: 1832,
            height: 622,
          },
          position: {
            x: 6478.62083592717,
            y: -283.49927916004435,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771824461933',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 9',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF9 panel from the provided storyboard contact sheet (the panel labeled “LS (KF9)”). Crop to that panel only\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/fbedb6a2-b92f-47fa-a853-0fbea90af9ee.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829863338',
          position: {
            x: 65,
            y: 68.35172768311435,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828615610',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 9',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '**[KF9 | 4.0s | LS]**\n*   Composition: Return to the wide establishing framing of KF1, but further back. The thick black vertical window frame dominates the left third.\n*   Action/beat: Subject A turns and walks out of the frame to the left. Subject B picks up his briefcase and turns right. Subject C remains perfectly still in the background.\n*   Camera: Eye level. Slow lateral dolly to the left, causing the vertical black mullion to slowly wipe the frame, swallowing the scene in darkness.\n*   Lens/DoF: 35mm. Deep DoF.\n*   Lighting & grade: The men step out of the overhead lights and into deep shadows before leaving the frame.\n*   Sound/atmos: Footsteps echoing on carpet, fading out. Drone sustains and cuts to black.',
              duration: '4',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_ffbd9715-ef0f-4807-add9-ac0290076aee.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f98a6/nOlkUzCXV3P7vMzLTbaau_a5146dbb05ae4ec7ac35c9a3f1d9c734.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f98a6/uTuUZwQ1nUfLb14GIha1-_8385b1d204294e598cbdfb6374344dc2.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829863338',
          position: {
            x: 1108.1053825744111,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'group-1771829892522',
          data: {
            color: 'orange',
            label: 'Scene 6 – Tension Hold',
            style: {
              width: 1751.5462681433419,
              height: 587.8220884714469,
            },
          },
          type: 'group',
          style: {
            width: 1751.5462681433419,
            height: 587.8220884714469,
          },
          dragging: false,
          measured: {
            width: 1752,
            height: 588,
          },
          position: {
            x: 6453.769902263756,
            y: -1933.2178683287023,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771824309636',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 6',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF6 panel from the provided storyboard contact sheet (the panel labeled “MS (KF6)”). Crop to that panel only—remove all grid borders, labels, and surrounding margins so only the original frame remains.\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/36ae9c08-c472-4dd7-b18d-12d9de371736.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829892522',
          position: {
            x: 20,
            y: 93.82208847144693,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828556597',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 6',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                '**[KF6 | 2.0s | MS]**\n*   Composition: Profile two-shot of the men, similar to KF1 but slightly tighter. The background is visible between them.\n*   Action/beat: Both men stand still for a beat, the tension thick.\n*   Camera: Eye level. Static.\n*   Lens/DoF: 50mm. Medium DoF. Focus begins sharply on the men in the midground.\n*   Lighting & grade: Unchanged. Cold cyan overheads.\n*   Sound/atmos: Silence, save for the hum.',
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_02847dc7-70cb-435f-8592-a165d8a4d096.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9893/t1nXa_2HyXb3oyxXunAXX_2769e5798f90449f92c22907779f88d4.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9893/wiKkUwmYc0XZP3tF9cQ3L_392e8208d18246d684c7c64572c2511e.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771829892522',
          position: {
            x: 942.5894017717965,
            y: 74.98731363868956,
          },
          selected: false,
        },
        {
          id: 'group-1771829910959',
          data: {
            color: 'orange',
            label: 'Scene 4 – Reverse Angle',
            style: {
              width: 1654.9774778488586,
              height: 594.8587960938255,
            },
          },
          type: 'group',
          style: {
            width: 1654.9774778488586,
            height: 594.8587960938255,
          },
          dragging: false,
          measured: {
            width: 1655,
            height: 595,
          },
          position: {
            x: 2116.4550951006113,
            y: -1919.9750420480211,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771824238073',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 4',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF4 panel from the provided storyboard contact sheet (the panel labeled “MCU (KF4)”). Crop to that panel only\n\n**Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details.\n\nOnly perform restoration/upscale:  \n- reduce compression artifacts and noise while preserving natural film grain  \n- sharpen existing edges and textures (suit fabric, hair, facial contours) without halos  \n- improve clarity of blinds/shelving details without changing their design  \n- maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ae457cf9-3f46-41e2-ba3e-e9e35560ce2b.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771829910959',
          position: {
            x: 41.5444260937702,
            y: 63.92549421879107,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828402245',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 4',
            config: {
              model: 'Kling 3.0 Pro Image',
              prompt:
                "**[KF4 | 2.0s | MCU]**\n*   Composition: Reverse OTS from behind Subject B, looking at Subject A. Profile angle.\n*   Action/beat: Subject A pulls the envelope firmly toward his chest, his jaw clenching. He does not break eye contact with B.\n*   Camera: Eye level. Slow 1m lateral truck right (parallax shifting the foreground blinds).\n*   Lens/DoF: 85mm. Shallow DoF. Focus tracks Subject A perfectly.\n*   Lighting & grade: Deep shadow on the left side of Subject A's face.\n*   Sound/atmos: Fabric of the suit shifting.",
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_ddc332bc-aa7d-40e3-881b-b3c5a739d57b.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f988b/hAb1mGXMANGKHncsmlmQe_e71b9d84f7284d1ebf762e0a1758938f.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f988b/39BOh4MRxZNWZo2lMm1kx_ba2ef6e29fad4c60b4b2b2e0ae6efb7d.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 473,
          },
          parentId: 'group-1771829910959',
          position: {
            x: 930.9774778488586,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'group-1771830242380',
          data: {
            color: 'purple',
            label: 'Reference Image',
            style: {
              width: 744,
              height: 686.3028139768231,
            },
          },
          type: 'group',
          style: {
            width: 744,
            height: 686.3028139768231,
          },
          measured: {
            width: 744,
            height: 686,
          },
          position: {
            x: -787.2747818871223,
            y: -1148.5390526558756,
          },
          selected: false,
        },
        {
          id: 'select-image-1771822946616',
          data: {
            type: 'select-image',
            label: 'Reference Image',
            config: {
              selectedImageId: 3754523,
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8947045a-998c-4dd8-b1c2-1c48f46fe18f.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8947045a-998c-4dd8-b1c2-1c48f46fe18f.png',
          },
          type: 'select-image',
          dragging: false,
          measured: {
            width: 704,
            height: 395,
          },
          parentId: 'group-1771830242380',
          position: {
            x: 17.63589904384048,
            y: 137.47173537790115,
          },
          selected: false,
        },
        {
          id: 'note-1771830156202',
          data: {
            type: 'note',
            label: 'Select Your Image',
            config: {
              note: '<h1>Select Your Image</h1><p></p><p>Upload, generate, or select from your existing images. This is the <strong>single reference image</strong> that the Storyboard Director will analyze and transform into a full cinematic sequence.</p><p></p><p><strong>Best results with:</strong></p><ul><li><p>Clear subjects and environment</p></li><li><p>Interesting lighting or mood</p></li><li><p>Visible spatial relationships between elements</p></li></ul>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 360,
          },
          parentId: 'group-1771830242380',
          position: {
            x: -457.6575422217677,
            y: 149.14643656350222,
          },
          selected: false,
        },
        {
          id: 'group-1771830401278',
          data: {
            color: 'orange',
            label: 'Scene 5 – Low-Angle Power Shot',
            style: {
              width: 1724.844571019119,
              height: 628.256562323103,
            },
          },
          type: 'group',
          style: {
            width: 1724.844571019119,
            height: 628.256562323103,
          },
          dragging: false,
          measured: {
            width: 1725,
            height: 628,
          },
          position: {
            x: 4277.348962099688,
            y: -1963.2333794848514,
          },
          selected: false,
        },
        {
          id: 'image-edit-1771823842421',
          data: {
            type: 'image-edit',
            label: 'Upscale – Scene 5',
            config: {
              useHD: false,
              prompt:
                'Create a faithful high‑resolution upscale of the KF5 image from the provided storyboard contact sheet (the image labeled “Low Angle / MS (KF5)”). Crop to that image only (second image in the second row) **Do not** add/remove/alter any characters, objects, props, or background elements. Do not change facial features, wardrobe, poses, expressions, lighting direction/intensity, color grading, time of day, or mood. Do not invent new details. Only perform restoration/upscale: - reduce compression artifacts and noise while preserving natural film grain - sharpen existing edges and textures (suit fabric, hair, facial contours) without halos - improve clarity of blinds/shelving details without changing their design - maintain the same cool teal/blue tone and contrast',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/3034d8b3-f0c7-40ce-b383-722b62f8a982.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: {
            width: 704,
            height: 474,
          },
          parentId: 'group-1771830401278',
          position: {
            x: 102.34636349327684,
            y: 77.11276984505344,
          },
          selected: false,
        },
        {
          id: 'image-to-video-1771828504321',
          data: {
            type: 'image-to-video',
            label: 'Animate – Scene 5',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                "**[KF5 | 1.5s | Low-Angle / MS]**\n*   Composition: Power angle from the surface of the desk, looking up at both men. The open briefcase is in the foreground right.\n*   Action/beat:  Subject B's is gently and carefully closing his briefcase.\n*   Camera: Worm’s-eye, resting on the desk looking up. Static.\n*   Lens/DoF: 24mm. Deep DoF. Both men's torsos and faces are sharp against the ceiling lights.\n*   Lighting & grade: Overhead lights flare slightly into the lens, creating a clinical glow.\n*   Sound/atmos: Heavy metallic click of the briefcase latch closing.",
              duration: '3',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_14af49a5-2dbd-4a7b-b9c6-92f794a47d20.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f9921/6xkr8zS6rRCYkRHl50Ox4_7cb38c1dcf9d4e938eff8bb964c77841.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f9921/JbuOKeNtn8ERa2x8dovgf_41ab6fee0b4d4a178e343c181e50cb6a.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: {
            width: 704,
            height: 472,
          },
          parentId: 'group-1771830401278',
          position: {
            x: 981.1282795376055,
            y: 84.9822390941656,
          },
          selected: false,
        },
        {
          id: 'group-1772262801028',
          data: {
            color: 'red',
            label: 'Soundtrack',
            style: {
              width: 990.0214376561769,
              height: 350,
            },
          },
          type: 'group',
          style: {
            width: 990.0214376561769,
            height: 350,
          },
          measured: {
            width: 990,
            height: 350,
          },
          position: {
            x: 584.8236325893756,
            y: 1314.3963885457383,
          },
          selected: false,
        },
        {
          id: 'music-generation-1771831088926',
          data: {
            type: 'music-generation',
            label: 'Background Score',
            config: {
              prompt:
                'Please write the appropriate background music for this story: *   Theme: The devastating weight of a secret transaction hidden in plain sight.\n*   Logline: Late at night in a sterile corporate high-rise, a covert handover of sensitive documents shifts the balance of power—while a silent witness watches from the shadows.\n*   Emotional Arc:\n    *   Setup: Voyeuristic observation of a tense, late-night corporate meeting.\n',
              isInstrumental: true,
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/tts/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/736f823e-a6e1-404c-b0f4-07eee1b1bab3.mp3',
            status: 'success',
          },
          type: 'music-generation',
          dragging: false,
          measured: {
            width: 404,
            height: 114,
          },
          parentId: 'group-1772262801028',
          position: {
            x: 566.0214376561769,
            y: 160.60079882455238,
          },
          selected: false,
        },
        {
          id: 'note-1772259648835',
          data: {
            type: 'note',
            label: 'About the Soundtrack',
            config: {
              note: '<h1>Soundtrack</h1><p></p><p>Use the <strong>Background Score</strong> node to generate cinematic music that matches the mood of your storyboard. The prompt is pre-configured based on the scene analysis.</p><p></p><p>You can change the music style by editing the prompt — try keywords like \"tense orchestral\", \"ambient electronic\", or \"dark minimal piano\".</p>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 280,
          },
          parentId: 'group-1772262801028',
          position: {
            x: 20,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'group-1772262887639',
          data: {
            color: 'green',
            label: 'Master Prompt',
            style: {
              width: 998.9268445506254,
              height: 594,
            },
          },
          type: 'group',
          style: {
            width: 998.9268445506254,
            height: 594,
          },
          measured: {
            width: 999,
            height: 594,
          },
          position: {
            x: -353.05054514771945,
            y: -219.74618759478312,
          },
          selected: false,
        },
        {
          id: 'text-generation-1771817038968',
          data: {
            type: 'text-generation',
            label: 'Storyboard Director',
            config: {
              model: 'gemini-3.1-pro-preview',
              prompt:
                'User provides: one reference image (image). 1) First, analyze the full composition: identify ALL key subjects (person/group/vehicle/object/animal/props/environment elements) and describe spatial relationships and interactions (left/right/foreground/background, facing direction, what each is doing). 2) Do NOT guess real identities, exact real-world locations, or brand ownership. Stick to visible facts. Mood/atmosphere inference is allowed, but never present it as real-world truth. 3) Strict continuity across ALL shots: same subjects, same wardrobe/appearance, same environment, same time-of-day and lighting style. Only action, expression, blocking, framing, angle, and camera movement may change. 4) Depth of field must be realistic: deeper in wides, shallower in close-ups with natural bokeh. Keep ONE consistent cinematic color grade across the entire sequence. 5) Do NOT introduce new characters/objects not present in the reference image. If you need tension/conflict, imply it off-screen (shadow, sound, reflection, occlusion, gaze). Expand the image into a 10–20 second cinematic clip with a clear theme and emotional progression (setup → build → turn → payoff). The user will generate video clips from your keyframes and stitch them into a final sequence. Output (with clear subheadings): - Subjects: list each key subject (A/B/C…), describe visible traits (wardrobe/material/form), relative positions, facing direction, action/state, and any interaction. - Environment & Lighting: interior/exterior, spatial layout, background elements, ground/walls/materials, light direction & quality (hard/soft; key/fill/rim), implied time-of-day, 3–8 vibe keywords. - Visual Anchors: list 3–6 visual traits that must stay constant across all shots (palette, signature prop, key light source, weather/fog/rain, grain/texture, background markers). From the image, propose: - Theme: one sentence. - Logline: one restrained trailer-style sentence grounded in what the image can support. - Emotional Arc: 4 beats (setup/build/turn/payoff), one line each. Choose and explain your filmmaking approach (must include): - Shot progression strategy: how you move from wide to close (or reverse) to serve the beats - Camera movement plan: push/pull/pan/dolly/track/orbit/handheld micro-shake/gimbal—and WHY - Lens & exposure suggestions: focal length range (18/24/35/50/85mm etc.), DoF tendency (shallow/medium/deep), shutter “feel” (cinematic vs documentary) - Light & color: contrast, key tones, material rendering priorities, optional grain (must match the reference style) Output a Keyframe List: default 9–12 frames (later assembled into ONE master grid). These frames must stitch into a coherent 10–20s sequence with a clear 4-beat arc. Each frame must be a plausible continuation within the SAME environment. Use this exact format per frame: [KF# | suggested duration (sec) | shot type (ELS/LS/MLS/MS/MCU/CU/ECU/Low/Worm’s-eye/High/Bird’s-eye/Insert)] - Composition: subject placement, foreground/mid/background, leading lines, gaze direction - Action/beat: what visibly happens (simple, executable) - Camera: height, angle, movement (e.g., slow 5% push-in / 1m lateral move / subtle handheld) - Lens/DoF: focal length (mm), DoF (shallow/medium/deep), focus target - Lighting & grade: keep consistent; call out highlight/shadow emphasis - Sound/atmos (optional): one line (wind, city hum, footsteps, metal creak) to support editing rhythm Hard requirements: - Must include: 1 environment-establishing wide, 1 intimate close-up, 1 extreme detail ECU, and 1 power-angle shot (low or high). - Ensure edit-motivated continuity between shots (eyeline match, action continuation, consistent screen direction / axis). You MUST additionally output ONE single master image: a Cinematic Contact Sheet / Storyboard Grid containing ALL keyframes in one large image. - Default grid: 3x3. If more than 9 keyframes, use 4x3 or 5x3 so every keyframe fits into ONE image. Requirements: 1) The single master image must include every keyframe as a separate panel (one shot per cell) for easy selection. 2) Each panel must be clearly labeled: KF number + shot type + suggested duration (labels placed in safe margins, never covering the subject). 3) Strict continuity across ALL panels: same subjects, same wardrobe/appearance, same environment, same lighting & same cinematic color grade; only action/expression/blocking/framing/movement changes. 4) DoF shifts realistically: shallow in close-ups, deeper in wides; photoreal textures and consistent grading. 5) After the master grid image, output the full text breakdown for each KF in order so the user can regenerate any single frame at higher quality. Output in this order: A) Scene Breakdown B) Theme & Story C) Cinematic Approach D) Keyframes (KF# list) E) ONE Master Contact Sheet Image (All KFs in one grid)',
              systemPrompt:
                'You are an award-winning trailer director + cinematographer + storyboard artist. Your job: turn ONE reference image into a cohesive cinematic short sequence, then output AI-video-ready keyframes ',
            },
            result:
              "### A) Scene Breakdown\n\n*   **Subjects:**\n    *   **Subject A (Left):** Male, short light-brown hair, wearing a dark tailored suit and crisp white collared shirt (no tie visible/obscured). Leaning forward aggressively over the desk, left hand braced on the table, right hand extended to grip the envelope. Facing right.\n    *   **Subject B (Right):** Male, short dark hair, wearing a dark suit, white shirt, and dark tie. Standing more upright but angled down. Right hand holding out the envelope, left hand lingering near an open black leather briefcase. Facing left.\n    *   **Subject C (Background Left):** Female, dark hair pulled back severely, wearing a light icy-blue/grey button-up shirt. Sitting at a desk, facing forward. Passive, observant state.\n    *   **Props:** One thick manila envelope, one black leather briefcase, scattered white documents, a round analog wall clock (reading roughly 10:10).\n*   **Environment & Lighting:**\n    *   **Setting:** Modern, sterile corporate office interior viewed through a glass partition covered by horizontal mini-blinds. Heavy vertical window mullions frame the shot.\n    *   **Lighting:** Cold overhead fluorescent/LED (hard key), casting deep shadows under the eyes, nose, and jawlines. The scene is bathed in a distinct cyan/steel-blue color cast. The contrast ratio is high (low-key lighting), implying late-night after-hours.\n    *   **Vibe Keywords:** Covert, sterile, voyeuristic, tense, nocturnal, transactional, chilling.\n*   **Visual Anchors (Must remain constant):**\n    *   *The Barrier:* Horizontal window blinds consistently cutting across the foreground, obscuring/revealing.\n    *   *Color Palette:* Deep cyan, steel blue, and harsh white/grey. Deep crushed blacks.\n    *   *The MacGuffin:* The thick, tan manila envelope (the warmest color in the room).\n    *   *The Gaze:* Subject C always visible in the deep background behind Subject A.\n    *   *Lighting Direction:* Top-down, slightly backlit overheads.\n\n### B) Theme & Story\n\n*   **Theme:** The devastating weight of a secret transaction hidden in plain sight.\n*   **Logline:** Late at night in a sterile corporate high-rise, a covert handover of sensitive documents shifts the balance of power—while a silent witness watches from the shadows.\n*   **Emotional Arc:**\n    *   *Setup:* Voyeuristic observation of a tense, late-night corporate meeting.\n    *   *Build:* The physical exchange begins, hyper-focused on the item of value.\n    *   *Turn:* The realization that the transaction is being actively observed by a third party.\n    *   *Payoff:* The deal concludes, sealing a pact drenched in lingering paranoia.\n\n### C) Cinematic Approach\n\n*   **Shot Progression Strategy:** We start with an establishing medium-long shot, mimicking the reference to plant the viewer as a voyeur outside the glass. We systematically push in (MCU, then ECU) to build claustrophobia around the envelope. At the climax of the exchange, we rack focus to the background (the Turn), before pulling back to leave them in their sterile cage.\n*   **Camera Movement Plan:** Extremely controlled and mechanical. Slow, creeping push-ins on a dolly to increase tension. Lateral tracking (trucking) to create a visual \"strobing\" parallax effect through the foreground blinds. No handheld movement; the camera must feel as cold and calculating as the subjects.\n*   **Lens & Exposure:** A mix of 35mm for the wider voyeur shots and 85mm for the tight, compressed close-ups. Depth of Field (DoF) will be weaponized: shallow when focused through the blinds to make them a soft foreground blur, and specifically used for a precise rack-focus from the foreground men to the background woman. Cinematic 1/48s shutter for smooth motion.\n*   **Light & Color:** High-contrast steel-blue/cyan grade. We will prioritize rendering the texture of the suits, the gloss of the leather briefcase, and the matte grain of the manila envelope. No artificial film grain—this requires a pristine, sharp, digital cinema sensor look (e.g., ARRI Alexa LF) to maintain the clinical corporate atmosphere.\n\n***\n\n### D) Keyframes (9-Shot Sequence)\n\n**[KF1 | 2.5s | LS]**\n*   **Composition:** Replicates reference image. Framed perfectly symmetrically between two vertical black window mullions. The horizontal blinds cut across the entire frame. Subjects A and B in profile; Subject C out of focus in the deep background left.\n*   **Action/beat:** Subject B slowly lifts the manila envelope from the briefcase, holding it out. Subject A leans in.\n*   **Camera:** Eye level. Slow, imperceptible 5% push-in.\n*   **Lens/DoF:** 35mm. Medium DoF. Blinds in extreme foreground are slightly soft; the men are sharp.\n*   **Lighting & grade:** Cold overhead cyan. High contrast.\n*   **Sound/atmos:** Low, pulsing air-conditioning hum.\n\n**[KF2 | 1.5s | MCU]**\n*   **Composition:** Clean over-the-shoulder (OTS) from behind Subject A, looking through the blinds at Subject B. Subject B's face is bisected by shadow.\n*   **Action/beat:** Subject B hesitates for a fraction of a second, his eyes flicking to Subject A's face before looking back at the envelope.\n*   **Camera:** Eye level, locked off.\n*   **Lens/DoF:** 85mm. Shallow DoF. Subject B is razor-sharp; Subject A's shoulder and the blinds are highly blurred.\n*   **Lighting & grade:** Harsh top-light catching the sweat/oil on Subject B's forehead.\n*   **Sound/atmos:** The crisp rustle of thick paper.\n\n**[KF3 | 1.5s | ECU]**\n*   **Composition:** Extreme close-up on the physical exchange over the desk. Horizontal blind slats cut across the very top and bottom of the frame.\n*   **Action/beat:** Subject B's hand holds the envelope; Subject A's hand enters the frame and tightly grips the other side of it. Knuckles slightly white.\n*   **Camera:** Desk height, angled slightly down. Locked off.\n*   **Lens/DoF:** 100mm Macro. Extremely shallow DoF. Focus is dead center on the edge of the envelope and their overlapping fingers.\n*   **Lighting & grade:** Envelope provides the only warm, yellowish reflection in the cold blue scene.\n*   **Sound/atmos:** The sharp friction of skin on heavy paper.\n\n**[KF4 | 2.0s | MCU]**\n*   **Composition:** Reverse OTS from behind Subject B, looking at Subject A. Profile angle.\n*   **Action/beat:** Subject A pulls the envelope firmly toward his chest, his jaw clenching. He does not break eye contact with B.\n*   **Camera:** Eye level. Slow 1m lateral truck right (parallax shifting the foreground blinds).\n*   **Lens/DoF:** 85mm. Shallow DoF. Focus tracks Subject A perfectly.\n*   **Lighting & grade:** Deep shadow on the left side of Subject A's face.\n*   **Sound/atmos:** Fabric of the suit shifting.\n\n**[KF5 | 1.5s | Low-Angle / MS]**\n*   **Composition:** Power angle from the surface of the desk, looking up at both men. The open briefcase is in the foreground right.\n*   **Action/beat:** The envelope is successfully transferred to Subject A. Subject B's hand retreats.\n*   **Camera:** Worm’s-eye, resting on the desk looking up. Static.\n*   **Lens/DoF:** 24mm. Deep DoF. Both men's torsos and faces are sharp against the ceiling lights.\n*   **Lighting & grade:** Overhead lights flare slightly into the lens, creating a clinical glow.\n*   **Sound/atmos:** Heavy metallic click of the briefcase latch closing.\n\n**[KF6 | 2.0s | MS]**\n*   **Composition:** Profile two-shot of the men, similar to KF1 but slightly tighter. The background is visible between them.\n*   **Action/beat:** Both men stand still for a beat, the tension thick.\n*   **Camera:** Eye level. Static.\n*   **Lens/DoF:** 50mm. Medium DoF. Focus begins sharply on the men in the midground.\n*   **Lighting & grade:** Unchanged. Cold cyan overheads.\n*   **Sound/atmos:** Silence, save for the hum.\n\n**[KF7 | 2.5s | MS to LS / Rack Focus]**\n*   **Composition:** Same framing as KF6.\n*   **Action/beat:** The turn. Subject A begins to step back. As he moves, the focus violently shifts past him.\n*   **Camera:** Eye level. Static.\n*   **Lens/DoF:** 50mm. Shallow DoF. *Rack focus* from the men in the midground to Subject C (the woman) in the deep background. The men become soft blurs.\n*   **Lighting & grade:** Subject C is illuminated by the eerie glow of her computer monitor (blue-white).\n*   **Sound/atmos:** A low, ominous synthesized drone begins to swell.\n\n**[KF8 | 2.5s | CU]**\n*   **Composition:** Telephoto punch-in directly on Subject C, framed between the blurred silhouettes of the two men. Horizontal blinds cast harsh, prison-like shadows across her face.\n*   **Action/beat:** She does not blink. She simply watches the exchange. Her expression is utterly unreadable.\n*   **Camera:** Eye level. Very slow, creeping push-in.\n*   **Lens/DoF:** 135mm. Shallow DoF. Only her eyes and the bridge of her nose are in sharp focus.\n*   **Lighting & grade:** High contrast. Monitor glow on her face, deep darkness around her.\n*   **Sound/atmos:** A single, muffled tick of the wall clock.\n\n**[KF9 | 4.0s | LS]**\n*   **Composition:** Return to the wide establishing framing of KF1, but further back. The thick black vertical window frame dominates the left third.\n*   **Action/beat:** Subject A turns and walks out of the frame to the left. Subject B picks up his briefcase and turns right. Subject C remains perfectly still in the background.\n*   **Camera:** Eye level. Slow lateral dolly to the left, causing the vertical black mullion to slowly wipe the frame, swallowing the scene in darkness.\n*   **Lens/DoF:** 35mm. Deep DoF.\n*   **Lighting & grade:** The men step out of the overhead lights and into deep shadows before leaving the frame.\n*   **Sound/atmos:** Footsteps echoing on carpet, fading out. Drone sustains and cuts to black.\n\n***\n\n### E) ONE Master Contact Sheet Image\n\n*(To generate the master storyboard grid, copy and paste the following prompt into your AI image generator of choice, such as Midjourney v6 or DALL-E 3)*\n\n**Image Generation Prompt:**\n> A master storyboard contact sheet containing a 3x3 grid of 9 cinematic frames. The grid tells a sequential story of a late-night corporate handover. **Visual Constants across ALL 9 panels:** Cinematic 4k resolution, cold sterile cyan and steel-blue lighting, deep crushed blacks. Same environment: viewed through an office glass window covered in horizontal mini-blinds. Same subjects: Subject A (man on left, dark suit, light hair), Subject B (man on right, dark suit, dark tie), Subject C (woman in background left, light icy-blue shirt, observing). Props: a thick manila envelope and open black briefcase on a desk. **Panel 1 (Top Left):** Wide symmetrical shot through blinds, two men exchanging envelope, woman blurred in background. **Panel 2 (Top Mid):** Medium close up over-the-shoulder of the right man, harsh top lighting. **Panel 3 (Top Right):** Extreme close up macro shot of two pairs of hands gripping the manila envelope over the desk, shallow depth of field. **Panel 4 (Mid Left):** Medium close up over-the-shoulder of the left man pulling envelope to his chest. **Panel 5 (Center):** Low worm's-eye angle from the desk looking up at the two men, briefcase in foreground. **Panel 6 (Mid Right):** Profile two-shot of the men standing perfectly still. **Panel 7 (Bottom Left):** Rack focus shot; the men in foreground are now blurry, the woman in the background is now in sharp focus. **Panel 8 (Bottom Mid):** Tight close up on the woman's face, framed between the men's blurry shoulders, prison-like shadows from blinds on her face. **Panel 9 (Bottom Right):** Wide shot, tracking left, the men are walking away into shadows, a heavy black window frame obscuring the view. High end cinematography, Arri Alexa, low key lighting, David Fincher style --ar 16:9 --v 6.0 --style raw",
            status: 'success',
          },
          type: 'text-generation',
          dragging: false,
          measured: {
            width: 404,
            height: 363,
          },
          parentId: 'group-1772262887639',
          position: {
            x: 574.9268445506254,
            y: 113.23431461849084,
          },
          selected: false,
        },
        {
          id: 'note-1771830291363',
          data: {
            type: 'note',
            label: 'About the Director Node',
            config: {
              note: '<h1>Storyboard Director</h1><p></p><p>This AI node is the brain of the workflow. It receives your reference image and writes a <strong>complete cinematic breakdown</strong> including:</p><p></p><ul><li><p><strong>Scene analysis</strong> — Identifies all subjects, wardrobe, props, environment, and lighting</p></li><li><p><strong>Theme &amp; story</strong> — Proposes a logline and 4-beat emotional arc (setup / build / turn / payoff)</p></li><li><p><strong>Cinematic approach</strong> — Shot progression, camera movement plan, lens choices, color grading</p></li><li><p><strong>9–12 keyframe descriptions</strong> — Each with composition, action, camera, lens/DoF, lighting, and sound notes</p></li></ul><p></p><p>You can customize the system prompt to change the cinematic style (e.g., documentary, horror, comedy) or adjust the number of keyframes.</p>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 524,
          },
          parentId: 'group-1772262887639',
          position: {
            x: 20,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'group-1772263015692',
          data: {
            color: 'pink',
            label: 'Result',
            style: {
              width: 1311.2473980907814,
              height: 627.9986158134466,
            },
          },
          type: 'group',
          style: {
            width: 1311.2473980907814,
            height: 627.9986158134466,
          },
          dragging: false,
          measured: {
            width: 1311,
            height: 628,
          },
          position: {
            x: -1657.0928751145452,
            y: -3105.64899085243,
          },
          selected: false,
        },
        {
          id: 'select-image-1772259858363',
          data: {
            type: 'select-image',
            label: 'Additional Reference',
            config: {
              selectedImageId: 3755374,
              selectedIsVideo: true,
              selectedImageUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d5a9cf4d-7cbe-4efb-a170-97fc88e55607.mp4',
            },
            result:
              'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d5a9cf4d-7cbe-4efb-a170-97fc88e55607.mp4',
          },
          type: 'select-image',
          dragging: false,
          measured: {
            width: 704,
            height: 398,
          },
          parentId: 'group-1772263015692',
          position: {
            x: 587.2473980907814,
            y: 50,
          },
          selected: false,
        },
        {
          id: 'note-1772259887094',
          data: {
            type: 'note',
            label: 'Final Assembly',
            config: {
              note: '<h1>Final Assembly</h1><p></p><p>Once all video clips and the music track are generated:</p><p></p><ol><li><p>Save each video clip from the Animate nodes</p></li><li><p>Save the music from the Background Score node</p></li><li><p>Open our <a target=\"_blank\" rel=\"noopener noreferrer nofollow\" href=\"https://easy-peasy.ai/ai-video-editor\">Video Editor</a></p></li><li><p>Import all clips in scene order and add the soundtrack</p></li><li><p>Adjust timing, add transitions, and export your final cinematic sequence</p></li></ol>',
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 384,
          },
          parentId: 'group-1772263015692',
          position: {
            x: 20,
            y: 63.476749455426216,
          },
          selected: false,
        },
        {
          id: 'note-1772260036425',
          data: {
            type: 'note',
            label: 'Preview',
            config: {
              note: "<h1>Preview Videos</h1><p></p><p>Click on any <strong>Animate</strong> node to preview the generated video clip. Make sure to <strong>unmute</strong> if you want to hear audio.</p><p></p><p>If a scene doesn't match the storyboard, try regenerating the upscale first, then regenerate the video.</p>",
            },
          },
          type: 'note',
          dragging: false,
          measured: {
            width: 400,
            height: 132,
          },
          parentId: 'group-1772263015692',
          position: {
            x: 754.163187761317,
            y: 475.9986158134466,
          },
          selected: false,
        },
      ],
      edges: [
        {
          id: 'xy-edge__select-image-1771822946616image-text-generation-1771817038968image',
          source: 'select-image-1771822946616',
          target: 'text-generation-1771817038968',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1771822946616image-image-edit-1771823144473image',
          source: 'select-image-1771822946616',
          target: 'image-edit-1771823144473',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__text-generation-1771817038968text-image-edit-1771823144473prompt',
          source: 'text-generation-1771817038968',
          target: 'image-edit-1771823144473',
          selected: false,
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771823842421image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771823842421',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771823894361image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771823894361',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771823984336image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771823984336',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771824130193image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771824130193',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771824238073image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771824238073',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771824309636image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771824309636',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771824371297image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771824371297',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771824420626image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771824420626',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-image-edit-1771824461933image',
          source: 'image-edit-1771823144473',
          target: 'image-edit-1771824461933',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823894361image-image-to-video-1771828017665image',
          source: 'image-edit-1771823894361',
          target: 'image-to-video-1771828017665',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823984336image-image-to-video-1771828196864image',
          source: 'image-edit-1771823984336',
          target: 'image-to-video-1771828196864',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824130193image-image-to-video-1771828248698image',
          source: 'image-edit-1771824130193',
          target: 'image-to-video-1771828248698',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824238073image-image-to-video-1771828402245image',
          source: 'image-edit-1771824238073',
          target: 'image-to-video-1771828402245',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823842421image-image-to-video-1771828504321image',
          source: 'image-edit-1771823842421',
          target: 'image-to-video-1771828504321',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824309636image-image-to-video-1771828556597image',
          source: 'image-edit-1771824309636',
          target: 'image-to-video-1771828556597',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824371297image-image-to-video-1771828609733image',
          source: 'image-edit-1771824371297',
          target: 'image-to-video-1771828609733',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824420626image-image-to-video-1771828617598image',
          source: 'image-edit-1771824420626',
          target: 'image-to-video-1771828617598',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824461933image-image-to-video-1771828615610image',
          source: 'image-edit-1771824461933',
          target: 'image-to-video-1771828615610',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771823144473image-text-generation-1771838677319image',
          source: 'image-edit-1771823144473',
          target: 'text-generation-1771838677319',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__text-generation-1771817038968text-text-generation-1771838677319prompt',
          source: 'text-generation-1771817038968',
          target: 'text-generation-1771838677319',
          selected: false,
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1771824420626image-image-edit-1771839424286image',
          source: 'image-edit-1771824420626',
          target: 'image-edit-1771839424286',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824420626image-image-edit-1771839500792image',
          source: 'image-edit-1771824420626',
          target: 'image-edit-1771839500792',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771824420626image-image-edit-1771839579673image',
          source: 'image-edit-1771824420626',
          target: 'image-edit-1771839579673',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771839424286image-image-to-video-1771839732931image',
          source: 'image-edit-1771839424286',
          target: 'image-to-video-1771839732931',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771839579673image-image-to-video-1771839745171image',
          source: 'image-edit-1771839579673',
          target: 'image-to-video-1771839745171',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771839500792image-image-to-video-1771839742595image',
          source: 'image-edit-1771839500792',
          target: 'image-to-video-1771839742595',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'product-commercial-aura-active',
    name: 'Product Commercial – Water Brand Ad',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/631e02ba-14ae-4711-925b-e4982bb570cb.mp4',
    description:
      'Create a cinematic 25-second product commercial for a premium water brand. This workflow generates 6 scenes (image + video), a voiceover, and background music — everything you need for a polished video ad.',
    category: 'Video',
    flow_data: {
      nodes: [
        // ── Intro Group ──
        {
          id: 'group-1771731668180',
          type: 'group',
          position: { x: 1089.9647960685825, y: -1101.0175633061954 },
          style: { width: 1687.9830497884868, height: 503.0067596870158 },
          selected: false,
          data: {
            color: 'orange',
            label: 'Intro',
            style: {
              width: 1687.9830497884868,
              height: 503.0067596870158,
            },
          },
        },
        {
          id: 'note-1771729607175',
          type: 'note',
          position: { x: 20, y: 51.0067596870158 },
          parentId: 'group-1771731668180',
          selected: false,
          data: {
            type: 'note',
            label: 'Getting Started',
            config: {
              note: '<h1>Product Commercial Template</h1><h2>Aura Active — "Drink the View"</h2><p></p><p>This template walks you through creating a <strong>complete 25-second video ad</strong> for a premium water brand using only AI tools.</p><p></p><p><strong>What you will create:</strong></p><ul><li><p>6 cinematic video scenes (image → video pipeline)</p></li><li><p>Professional voiceover narration</p></li><li><p>Background music track</p></li></ul><p></p>',
            },
          },
        },
        {
          id: 'note-1771731542582',
          type: 'note',
          position: { x: 449.48356244107003, y: 50 },
          parentId: 'group-1771731668180',
          selected: false,
          data: {
            type: 'note',
            label: 'Getting Started (Copy)',
            config: {
              note: '<h1><strong>How it works:</strong></h1><ol><li><p><strong>Step 1</strong> — Upload your product photo (orange group below)</p></li><li><p><strong>Step 2</strong> — Generate "first frame" images from text prompts (purple group)</p></li><li><p><strong>Step 3</strong> — Convert each image into a video clip (orange group below purple)</p></li><li><p><strong>Step 4</strong> — Generate voiceover and music (pink &amp; cyan groups)</p></li><li><p><strong>Step 5</strong> — Combine everything in our Video Editor <a target="_blank" rel="noopener noreferrer nofollow" href="https://easy-peasy.ai/ai-video-editor">https://easy-peasy.ai/ai-video-editor</a></p></li></ol><p></p><p>The final assembled video is shown to the right →</p><p>You can unmute it to hear the audio.</p>',
            },
          },
        },
        {
          id: 'upload-1771729727521',
          type: 'upload',
          position: { x: 963.9830497884868, y: 74.48219121052398 },
          parentId: 'group-1771731668180',
          selected: false,
          data: {
            type: 'upload',
            label: 'Final Result — Assembled Ad',
            config: {
              audioUrl: '',
              imageUrl: '',
              videoUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b1cdbcf8-d263-42eb-8a4f-4f129e417493.mp4',
              mediaType: 'video',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b1cdbcf8-d263-42eb-8a4f-4f129e417493.mp4',
          },
        },

        // ── Step 1: Upload Product Photo ──
        {
          id: 'group-1771680086397',
          type: 'group',
          position: { x: 3140.13, y: -1203.17 },
          style: { width: 997.29, height: 827 },
          selected: false,
          data: {
            color: 'orange',
            label: 'Step 1 — Upload Your Product Photo',
            style: { width: 997.29, height: 827 },
          },
        },
        {
          id: 'note-1771680048679',
          type: 'note',
          position: { x: 34.28711674951592, y: 100.8386745911364 },
          parentId: 'group-1771680086397',
          selected: false,
          data: {
            type: 'note',
            label: 'Upload Your Product',
            config: {
              note: '<h1>Step 1 — Upload Your Product Photo</h1><p></p><p>Upload a <strong>high-quality photo of your product</strong> (e.g. a water bottle, cosmetic, gadget) using the Upload node to the right. You can remove existing photo by clicking X red button.</p><p></p><p><strong>Why?</strong> Two of the six scenes use "Image to Image" editing. They take your product photo and place it into cinematic settings (a trail runner drinking, a hero product shot on a mountain rock).</p><p></p><p><strong>Tips:</strong></p><ul><li><p>Use a clean, well-lit photo on a simple background</p></li><li><p>PNG with transparency works best</p></li><li><p>The upload node will pass this image to the Image-to-Image nodes in Step 2</p></li></ul><p></p>',
            },
          },
        },
        {
          id: 'upload-1771413655694',
          type: 'upload',
          position: { x: 473.29, y: 50 },
          parentId: 'group-1771680086397',
          selected: false,
          data: {
            type: 'upload',
            label: 'Product Photo',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/788422ac-8d4d-451d-97ba-3d72c14fe6fb.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/788422ac-8d4d-451d-97ba-3d72c14fe6fb.png',
          },
        },

        // ── Step 2: First Frames (Image Generation) ──
        {
          id: 'group-1771680186062',
          type: 'group',
          position: { x: 125.85402373400655, y: 54.1991843547745 },
          style: { width: 6725.5, height: 508.75 },
          selected: false,
          data: {
            color: 'purple',
            label: 'Step 2 — Generate First Frames (Start Images)',
            style: { width: 6725.5, height: 508.75 },
          },
        },
        {
          id: 'note-1771680124629',
          type: 'note',
          position: { x: 135.6907992725267, y: -4.437580947795709 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'note',
            label: 'First Frames — How It Works',
            config: {
              note: '<h1>Step 2 — Generate First Frames</h1><p></p><p>Each video scene starts with a <strong>single still image</strong> called a "first frame." The AI video generator then animates it into a full clip.</p><p></p><p><strong>This row contains 6 image nodes — one per scene:</strong></p><ol><li><p><strong>Summit Hiker</strong> — Text to Image (hiker on snowy ridge)</p></li><li><p><strong>Water Droplet</strong> — Text to Image (floating droplet reflecting mountains)</p></li><li><p><strong>Cycling Duo</strong> — Text to Image (father &amp; son biking by river)</p></li><li><p><strong>Refreshing Moment</strong> — Image to Image (your product photo → runner drinking)</p></li><li><p><strong>Underwater Vitality</strong> — Text to Image (bubbles rising underwater)</p></li><li><p><strong>Product Hero</strong> — Image to Image (your product photo → bottle on mountain rock)</p></li></ol><p></p><p></p>',
            },
          },
        },
        // Standalone note for customization tip
        {
          id: 'note-1771731948138',
          type: 'note',
          position: { x: 928.469207979204, y: 632.2150104921074 },
          selected: false,
          data: {
            type: 'note',
            label: '#31 Note',
            config: {
              note: '<h2><strong>To customize</strong></h2><h2><strong>Edit the prompt inside each node to match your brand and scenes. Change dimensions, model, or style as needed.</strong></h2><p></p>',
            },
          },
        },
        // Scene 1: Summit Hiker
        {
          id: 'image-generation-1771413264228',
          type: 'image-generation',
          position: { x: 837.100292294057, y: 50 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'image-generation',
            label: 'Scene 1 — Summit Hiker',
            config: {
              model: 'Nano Banana Pro',
              prompt:
                'A lone hiker in a bright orange technical jacket and black mountaineering pants is captured in an extreme wide shot, walking along a sharp, snow-covered mountain ridge. The landscape is vast and epic, with jagged peaks stretching into the distance under a clear, piercing blue sky. The sun is high, casting long, dramatic shadows on the white snow, creating high contrast. The texture of the snow is visible, with wind-swept patterns and icy crusts. The image has a cinematic commercial quality, with sharp focus, high dynamic range, and vibrant colors, emphasizing the scale of nature and the solitude of the journey. Shot on a large format camera with a 24mm lens.',
              outputs: 1,
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2e99afc5-92ee-4492-b516-25e0c5253679.png',
            status: 'success',
          },
        },
        // Scene 2: Water Droplet
        {
          id: 'image-generation-1771413287406',
          type: 'image-generation',
          position: { x: 1782.4430795222922, y: 60.99184433439541 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'image-generation',
            label: 'Scene 2 — Water Droplet',
            config: {
              model: 'Nano Banana Pro',
              prompt:
                'A single, perfect sphere of water is suspended in mid-air, captured in a macro close-up shot. The droplet is crystal clear, acting as a lens that inverts and reflects the majestic mountain landscape behind it in sharp detail. The background is a soft, creamy bokeh of greens and blues, representing a forest and sky, ensuring the droplet stands out as the hero. The lighting is natural and back-lit, causing the edges of the droplet to glow with a rim light effect. The texture of the water looks viscous and pure. 8k resolution, hyper-realistic style, resembling high-end product photography.',
              outputs: 1,
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/f6529799-1154-4233-910d-626e23e28f51.png',
            status: 'success',
          },
        },
        // Scene 3: Cycling Duo
        {
          id: 'image-generation-1771413412775',
          type: 'image-generation',
          position: { x: 2833.5, y: 60.43 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'image-generation',
            label: 'Scene 3 — Cycling Duo',
            config: {
              model: 'Nano Banana Pro',
              prompt:
                'A father and his young son are captured in a dynamic side-profile tracking shot, riding mountain bikes along a winding paved road that hugs a sparkling river. The father wears a sleek cycling jersey and helmet, looking back with a smile at his son who is wearing helmet as well, who is pedaling energetically on a smaller bike. To the left, a rushing river reflects the sunlight; to the right, a steep cliff face rises. The lighting is golden hour, casting long, warm shadows and creating a lens flare that cuts through the scene. The image captures motion blur on the background and wheels, conveying speed and energy. Cinematic 35mm style.',
              outputs: 1,
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/fa2c35ac-8478-4ef0-b97a-f510597b2e53.png',
            status: 'success',
          },
        },
        // Scene 4: Refreshing Moment (Image to Image)
        {
          id: 'image-edit-1771413677671',
          type: 'image-edit',
          position: { x: 3926.55, y: 60.74 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Scene 4 — Refreshing Moment',
            config: {
              useHD: false,
              prompt:
                "A close-up portrait of a female trail runner taking a break, holding a transparent bottle of 'Aura Active' water. She is tilting her head back, drinking deeply, with a few droplets of water and sweat visible on her sun-kissed skin. The bottle has a modern, minimalist label. The background is a blur of deep green forest and sunlight filtering through leaves. The lighting is soft but directional, highlighting the clarity of the water in the bottle and the refreshment on her face. The image has a fresh, high-energy commercial look with vivid skin tones and crisp details.",
              editModel: 'Nano Banana Pro',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5bda626d-820f-43d8-b01e-ad2c26898d3e.png',
            status: 'success',
          },
        },
        // Scene 5: Underwater Vitality
        {
          id: 'image-generation-1771413448570',
          type: 'image-generation',
          position: { x: 4956.34, y: 75.27 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'image-generation',
            label: 'Scene 5 — Underwater Vitality',
            config: {
              model: 'Nano Banana Pro',
              prompt:
                'An underwater shot looking up towards the surface of a crystal-clear mountain lake. Thousands of tiny bubbles are rising frantically towards the surface, where sunlight breaks through the water in majestic god rays. The water is a deep, vibrant turquoise, transitioning to a lighter blue near the surface. The texture of the water is turbulent yet clear. The image is abstract and artistic, focusing on the interplay of light and fluid dynamics, symbolizing oxygenation and life. High shutter speed capture, freezing the motion of the bubbles.',
              outputs: 1,
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/981565cd-4a25-46e3-8f85-5c670841d62c.png',
            status: 'success',
          },
        },
        // Scene 6: Product Hero (Image to Image)
        {
          id: 'image-edit-1771413896069',
          type: 'image-edit',
          position: { x: 5935.097225050958, y: 69.60353638216623 },
          parentId: 'group-1771680186062',
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Scene 6 — Product Hero Shot',
            config: {
              useHD: false,
              prompt:
                "A single, premium 'Aura Active' mineral water bottle stands triumphantly on a weathered gray rock in the foreground. The bottle features a sleek silver cap and a modern label with a mountain motif. It is covered in cold, fresh condensation droplets that catch the light, emphasizing its chilled temperature. The background is a breathtaking, slightly out-of-focus vista of a turquoise alpine lake and jagged snow-capped mountains under a clear blue sky. The lighting is bright and natural, highlighting the crystal clarity of the plastic and the purity of the water. High-end advertising photography style, 8k resolution, crisp detail.",
              editModel: 'Nano Banana Pro',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e95426a1-65c1-4bc6-9b75-a3ae089f27f9.png',
            status: 'success',
          },
        },

        // ── Step 3: Video Clips ──
        {
          id: 'group-1771680317763',
          type: 'group',
          position: { x: 124.41255612608825, y: 897.7875398535091 },
          style: { width: 6708.99, height: 499.99 },
          selected: false,
          data: {
            color: 'orange',
            label: 'Step 3 — Animate Into Video Clips',
            style: { width: 6708.99, height: 499.99 },
          },
        },
        {
          id: 'note-1771680250378',
          type: 'note',
          position: { x: 72.62135625560722, y: 8.084231640599796 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'note',
            label: 'Video Clips — How It Works',
            config: {
              note: '<h1>Step 3 — Animate Into Video Clips</h1><p></p><p>Each "first frame" image from Step 2 is fed into an <strong>Image to Video</strong> node. The AI animates the still image into a short video clip with camera movement and motion.</p><p></p><p><strong>Models used:</strong> Veo 3.1, Kling 3.0 Pro, Kling 3.0 Standard. You can change the model in each node depending on quality and speed preferences.</p><p></p><p><strong>Tip:</strong> The video prompt describes <em>motion and camera movement</em>, not the scene itself — the image already provides the visual. Focus on what should <em>move</em>.</p>',
            },
          },
        },
        // Standalone note for video scene list
        {
          id: 'note-1771732117911',
          type: 'note',
          position: { x: -430.8700738022629, y: 956.6051354945286 },
          selected: false,
          data: {
            type: 'note',
            label: '#32 Note',
            config: {
              note: '<p><strong>This row contains 6 video nodes — one per scene:</strong></p><ol><li><p><strong>Summit Hiker</strong> — Camera pans along the ridge, snow dust kicks up</p></li><li><p><strong>Water Droplet</strong> — Droplet shimmers and wobbles in slow motion</p></li><li><p><strong>Cycling Duo</strong> — Tracking shot alongside the cyclists</p></li><li><p><strong>Refreshing Moment</strong> — Runner drinks and looks to the horizon</p></li><li><p><strong>Underwater Vitality</strong> — Bubbles rise in slow motion through god rays</p></li><li><p><strong>Product Hero</strong> — Slow push-in with condensation rolling down the bottle</p></li></ol><p></p>',
            },
          },
        },
        // Scene 1 Video
        {
          id: 'image-to-video-1771413315870',
          type: 'image-to-video',
          position: { x: 816.810491307546, y: 73.40389578052168 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'image-to-video',
            label: 'Scene 1 — Hiker Video',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'The hiker walks steadily along the narrow snowy ridge, battling a light wind that tugs at their jacket. The camera pans slowly to the right, following their movement while revealing the breathtaking depth of the valley below. Snow dust kicks up slightly with each step. The lighting remains bright and consistent, enhancing the feeling of altitude and fresh, cold air. The movement is smooth and cinematic, conveying determination and freedom.',
              duration: '8s',
              resolution: '4k',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d624ec7e-167a-47d9-b102-9dd77d06a0b9.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f5d14/jlibtTtrLJbYufwuDNsZ2_4215b30ee2e549eba6e112eafbb75441.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f5d14/av3D7Gz1EySrqWpUc1qDJ_37c1365e8ac24e9baf02cf9c978bcfb9.jpg',
            },
            status: 'success',
          },
        },
        // Scene 2 Video
        {
          id: 'image-to-video-1771413370913',
          type: 'image-to-video',
          position: { x: 1775.5681290293555, y: 71.4828833755829 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'image-to-video',
            label: 'Scene 2 — Droplet Video',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'The suspended water droplet hovers gently in the air, shimmering as it catches the sunlight. Inside the droplet, the reflection of the mountains distorts slightly as the water ripples in slow motion. The background remains soft and out of focus, shifting slightly as if a breeze is moving the trees. The droplet maintains its spherical shape but wobbles delicately, emphasizing its surface tension and purity before slowly beginning to fall out of frame.',
              duration: '8s',
              resolution: '720p',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ed6967b1-73c9-4365-aa0d-b993bee1f3e6.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8ef67b/ioBA_zu5ieBABQwB9gNLU_c8040b1001c64c96a27f6c0549700f4a.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8ef67b/1BGmizTIgTmD6eLrW4cLH_d1b09def90884e25a848f1569eadd958.jpg',
            },
            status: 'success',
          },
        },
        // Scene 3 Video
        {
          id: 'image-to-video-1771413748791',
          type: 'image-to-video',
          position: { x: 2818.460626426252, y: 66.58437737708641 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'image-to-video',
            label: 'Scene 3 — Cycling Video',
            config: {
              model: 'Kling 3.0 Pro Image',
              prompt:
                'The father and son cycle in sync along the riverside road. The camera tracks alongside them at the same speed, keeping them in sharp focus while the background trees and river blur past. The father points ahead towards the mountains, and the son looks up excitedly. The sunlight flickers through the trees, creating a strobing light effect on their faces. The movement is energetic and fluid, symbolizing active lifestyle and family bonding.',
              duration: '3',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_602da430-bfa8-44f1-a997-16a1f7352891.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8f5d95/IHne7yZyoU2NJ3PUa5TKe_dee0e5a2d09840609074444308010ac4.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8f5d95/BYt-4UIx4QfCVpLFhWRkH_1af69dba781b4c22a81c3319ba1dcbc5.jpg',
            },
            status: 'success',
          },
        },
        // Scene 4 Video
        {
          id: 'image-to-video-1771413787192',
          type: 'image-to-video',
          position: { x: 3899.5129413857526, y: 73.14999999999998 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'image-to-video',
            label: 'Scene 4 — Drinking Video',
            config: {
              model: 'Kling 3.0 Standard Image',
              prompt:
                'The runner tilts the bottle higher, taking a long, refreshing drink. Her throat moves as she swallows, and she lowers the bottle with a satisfied exhale, wiping her mouth with the back of her hand. She smiles and looks towards the horizon, regaining her breath. The sunlight dances through the leaves in the background, creating a dappled light effect that moves across her face. The atmosphere is revitalizing and triumphant.',
              duration: '4',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_b38cb91c-1168-4133-b960-11cc2308e304.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8ef6e9/9SNGyXavhPvRiQ1LgXjO2_35a39a53cf9e4c7b90784a9d6ef99224.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8ef6e9/UmGrIRxeksRXzPg26lv0K_dc0517f7da1a45149812195d1f1632b2.jpg',
            },
            status: 'success',
          },
        },
        // Scene 5 Video
        {
          id: 'image-to-video-1771413833090',
          type: 'image-to-video',
          position: { x: 4969.63, y: 69.87 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'image-to-video',
            label: 'Scene 5 — Underwater Video',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'Streams of bubbles rush upwards in slow motion, twisting and turning as they rise through the blue water. The sun rays penetrate the surface, shifting and dancing as the water ripples above. The camera floats slowly upwards, following the bubbles towards the light. The motion is hypnotic and fluid, emphasizing the purity and mineral-rich quality of the water.',
              duration: '8s',
              resolution: '720p',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c53d9984-f39f-4bda-9ade-5af178103a4f.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8ef6a0/8Wg52A-eWItNP96gYorvA_c9f3c923c1f345298b5e4a28db13eb71.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8ef6a0/QtVCfBISm-TtRbzxMdPv4_af6cd5fc1ff145909a0fde412dc67066.jpg',
            },
            status: 'success',
          },
        },
        // Scene 6 Video
        {
          id: 'image-to-video-1771413913607',
          type: 'image-to-video',
          position: { x: 5918.258706217204, y: 67 },
          parentId: 'group-1771680317763',
          selected: false,
          data: {
            type: 'image-to-video',
            label: 'Scene 6 — Product Hero Video',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                "The camera performs a slow, cinematic push-in towards the single 'Aura Active' bottle standing on the rock. A single droplet of condensation rolls slowly down the side of the cold plastic, tracing a path through the mist on the bottle. The background lake water ripples gently in the distance, and thin clouds drift slowly across the mountain peaks. A subtle, warm lens flare sweeps across the top corner of the frame, highlighting the silver cap and the premium quality of the brand. The movement is smooth and majestic, serving as the perfect closing shot",
              duration: '8s',
              resolution: '720p',
              aspectRatio: '16:9',
              generateAudio: false,
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/f5c3171c-0203-4ace-80f6-c45f4746f220.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8ef6b1/Fw6XjJZk9WH9SHzkNwTHQ_6bbb5fb2b84a4d6a930f86382b3d60de.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8ef6b1/SG_uS4Q0wdFJH-8VyAUz-_77945fe69cb0479c83460328353246dd.jpg',
            },
            status: 'success',
          },
        },

        // ── Step 4a: Voiceover ──
        {
          id: 'group-1771680490130',
          type: 'group',
          position: { x: 117.75414105401399, y: 1778.39 },
          style: { width: 1444.69, height: 420.59 },
          selected: false,
          data: {
            color: 'pink',
            label: 'Step 4a — Generate Voiceover',
            style: { width: 1444.69, height: 420.59 },
          },
        },
        {
          id: 'note-1771680446900',
          type: 'note',
          position: { x: 46.831277491001885, y: 1.7037005161967045 },
          parentId: 'group-1771680490130',
          selected: false,
          data: {
            type: 'note',
            label: 'Voiceover — How It Works',
            config: {
              note: "<h1>Generate Voiceover</h1><p>A professional voiceover ties your scenes together. The <strong>Text Input</strong> node contains the narration script, and the <strong>Text to Speech</strong> node converts it into audio.</p><p><strong>Current script:</strong></p><p><em>\"Elevation isn't just a place\u2026 It's a feeling. Pure. Untouched. The energy that keeps you moving forward. Revitalize your body\u2026 with nature's clarity. Aura Active. Drink the view.\"</em></p><p><strong>To customize:</strong></p><ul><li><p>Edit the text in the <strong>Text Input</strong> node with your own brand script</p></li><li><p>Change the voice in the <strong>Text to Speech</strong> node (click Voice ID to browse voices)</p></li><li><p>Keep narration short \u2014 aim for 15 seconds of spoken audio to match the video length</p></li></ul><p></p>",
            },
          },
        },
        {
          id: 'input-1771680412149',
          type: 'input',
          position: { x: 551.63, y: 238.59 },
          parentId: 'group-1771680490130',
          selected: false,
          data: {
            type: 'input',
            label: 'Voiceover Script',
            config: {
              value:
                "Elevation isn't just a place... ..It's a feeling. Pure. Untouched. The energy that keeps you moving forward. Revitalize your body... ..with nature's clarity. Aura Active. Drink the view.",
            },
            result:
              "Elevation isn't just a place... ..It's a feeling. Pure. Untouched. The energy that keeps you moving forward. Revitalize your body... ..with nature's clarity. Aura Active. Drink the view.",
            status: 'success',
          },
        },
        {
          id: 'text-to-speech-1771680339197',
          type: 'text-to-speech',
          position: { x: 1115.75, y: 92.94 },
          parentId: 'group-1771680490130',
          selected: false,
          data: {
            type: 'text-to-speech',
            label: 'Voiceover Audio',
            config: {
              text: '',
              prompt: '@#18 Text Input',
              voiceID: 'FF7KdobWPaiR0vkcALHF',
            },
            result:
              'https://media.easy-peasy.ai/tts/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e27d627b-4d0b-407f-9a05-5316ed1ec54d.mp3',
            status: 'success',
          },
        },

        // ── Step 4b: Background Music ──
        {
          id: 'group-1771692044296',
          type: 'group',
          position: { x: 1913.8173408348812, y: 1780.38 },
          style: { width: 1412.59, height: 423.54 },
          selected: false,
          data: {
            color: 'cyan',
            label: 'Step 4b — Generate Background Music',
            style: { width: 1412.59, height: 423.54 },
          },
        },
        {
          id: 'note-1771692005260',
          type: 'note',
          position: { x: 40.57655091759807, y: 17.470125942827508 },
          parentId: 'group-1771692044296',
          selected: false,
          data: {
            type: 'note',
            label: 'Music — How It Works',
            config: {
              note: '<h1>Generate Background Music</h1><p></p><p>Background music sets the emotional tone for your commercial. The <strong>Music Generation</strong> node creates an instrumental track from a text description.</p><p></p><p><strong>Current prompt:</strong></p><p><em>"Ethereal ambient soundscape, peaceful and crystalline. Soft piano melody, flowing water textures, atmospheric pads, light glockenspiel, slow build-up, majestic nature documentary style, hopeful, serene, zero drums."</em></p><p></p><p></p>',
            },
          },
        },
        // Standalone note for music customization tips
        {
          id: 'note-1771732274315',
          type: 'note',
          position: { x: 3471.8841584684774, y: 1780.2164132300672 },
          selected: false,
          data: {
            type: 'note',
            label: '#33 Note',
            config: {
              note: '<p><strong>To customize:</strong></p><ul><li><p>Edit the <strong>Text Input</strong> node with a description of the mood you want</p></li><li><p>Use keywords like: tempo (e.g. "120 BPM"), instruments, genre, and emotional tone</p></li><li><p>Toggle "Instrumental" in the Music Generation node — keep it ON for ads so music doesn\'t compete with voiceover</p></li></ul><p></p><p><strong>Alternative prompt ideas:</strong></p><ul><li><p><strong>Cinematic:</strong> "Epic orchestral, driving percussion, soaring strings, heroic, 120 BPM"</p></li><li><p><strong>Sporty:</strong> "Upbeat pop-rock instrumental, energetic drums, electric guitar, motivational"</p></li></ul><p></p>',
            },
          },
        },
        {
          id: 'input-1771686298092',
          type: 'input',
          position: { x: 492.77, y: 221.54 },
          parentId: 'group-1771692044296',
          selected: false,
          data: {
            type: 'input',
            label: 'Music Description',
            config: {
              value:
                'Ethereal ambient soundscape, peaceful and crystalline. Soft piano melody, flowing water textures, atmospheric pads, light glockenspiel, slow build-up, majestic nature documentary style, hopeful, serene, zero drums. For commercial advertisement. ',
            },
            result:
              'Ethereal ambient soundscape, peaceful and crystalline. Soft piano melody, flowing water textures, atmospheric pads, light glockenspiel, slow build-up, majestic nature documentary style, hopeful, serene, zero drums. For commercial advertisement. ',
            status: 'success',
          },
        },
        {
          id: 'music-generation-1771686246251',
          type: 'music-generation',
          position: { x: 1112.59, y: 50 },
          parentId: 'group-1771692044296',
          selected: false,
          data: {
            type: 'music-generation',
            label: 'Background Music',
            config: {
              prompt: '@#25 Text Input',
              isCustomMode: false,
              isInstrumental: true,
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/tts/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/de04486a-3ecc-411c-94eb-56e5f14e208d.mp3',
            status: 'success',
          },
        },
        // ── Master Prompt Group ──
        {
          id: 'group-1771850571795',
          type: 'group',
          position: { x: 4551.397976293917, y: -1307.5657001484647 },
          style: {
            width: 907.5705971391162,
            height: 1082,
          },
          data: {
            color: 'orange',
            label: 'Master Prompt — AI Creative Director',
            style: {
              width: 907.5705971391162,
              height: 1082,
            },
          },
        },
        {
          id: 'note-1771849619362',
          type: 'note',
          position: { x: 20, y: 50 },
          parentId: 'group-1771850571795',
          selected: false,
          data: {
            type: 'note',
            label: 'Master Prompt — How It Works',
            config: {
              note: '<h1>Master Prompt</h1><p></p><p>This node is your <strong>one-stop creative brief</strong>. Instead of writing prompts for each scene manually, you give the AI your brand details and it generates <strong>an entire campaign package</strong> — image prompts, video prompts, voiceover script, and music direction. You can then copy output prompts to the corresponding nodes below.</p><p></p><h2>How to use it</h2><p>Open the <strong>Master Prompt</strong> node to the right and fill in the bracketed fields:</p><ul><li><p><strong>Brand Name</strong> — your brand (e.g. "Aura Active")</p></li><li><p><strong>Product/Service</strong> — what you\'re advertising</p></li><li><p><strong>Target Audience</strong> — who the ad is for</p></li><li><p><strong>Brand Vibe/Aesthetic</strong> — visual style (Cinematic, Energetic, Premium, etc.)</p></li><li><p><strong>Key Message/Tagline</strong> — the main takeaway</p></li><li><p><strong>Video Duration</strong> — how long the final ad should be</p></li><li><p><strong>Mandatory Elements</strong> — anything specific you need shown</p></li></ul><p></p><h2>What it outputs</h2><ol><li><p><strong>Campaign Overview</strong> — creative concept summary</p></li><li><p><strong>Video Script</strong> — scene-by-scene table with timing, visuals, and audio</p></li><li><p><strong>AI Image Prompts</strong> — ready-to-paste prompts for each scene\'s first frame</p></li><li><p><strong>AI Video Prompts</strong> — motion/camera prompts for animating each scene</p></li><li><p><strong>Music Prompt</strong> — genre, tempo, instruments, and mood for background track</p></li></ol><p></p><p><strong>Tip:</strong> Your uploaded product photo is connected to this node, so the AI can see and reference your actual product in its prompts.</p><p></p>',
            },
          },
        },
        {
          id: 'text-generation-1771849540169',
          type: 'text-generation',
          position: { x: 483.5705971391162, y: 65.76812757369089 },
          parentId: 'group-1771850571795',
          selected: false,
          data: {
            type: 'text-generation',
            label: 'Master Prompt',
            config: {
              model: 'gpt-5.4-thinking',
              prompt:
                'Act as an expert AI Creative Director and Video Production Specialist. Your task is to design a complete, production-ready AI Video Advertisement Package.\n\nHere are the details for the campaign:\n\n- Brand Name: [ENTER BRAND NAME]\n\n- Product/Service: [ENTER PRODUCT OR SERVICE DESCRIPTION]\n\n- Target Audience: [ENTER TARGET AUDIENCE]\n\n- Brand Vibe/Aesthetic: [ENTER VIBE - e.g., Cinematic, Nostalgic, Cyberpunk, Bright & Airy, Premium, Energetic]\n\n- Key Message/Tagline: [ENTER TAGLINE OR MAIN MESSAGE]\n\n- Video Duration: [ENTER DURATION - e.g., 15s, 30s, 60s]\n\n- Mandatory Elements: [ENTER ANY SPECIFIC REQUESTS - e.g., "Must show a golden retriever," "Must have a close-up of the bottle"]\n\nBased on the provided information, please output the campaign strictly in the following format:\n\n### 1. Campaign Overview\n\nProvide a 2-3 sentence summary of the creative concept, the visual hook, and the overall color palette/lighting style to ensure consistency.\n\n### 2. Video Script\n\nCreate a table outlining the flow of the ad with the following columns:\n\n| Scene # | Time (Seconds) | Visual Scene Description | Audio / Voiceover / SFX |\n\n### 3. AI Generation Prompts (Scene by Scene)\n\nFor EVERY scene listed in the script, provide the exact prompts needed for AI generators:\n\nScene [X]: [Brief Scene Name]\n\n*   Start Image Prompt (For Midjourney/Flux/Nano Banana): Write a highly detailed prompt describing the Subject, Action, Environment, Lighting, Camera Angle, Lens Type (e.g., 35mm, macro), and Visual Style (e.g., 8k, photorealistic, cinematic). Focus strictly on the visual composition and styling. Do NOT include camera movement.\n\n*   Video Generation Prompt (For Kling/Runway/Luma): Describe the MOVEMENT. How does the camera move? (e.g., slow pan left, tracking shot, push-in). How does the subject move? What dynamic elements are in the background? (e.g., wind blowing, water rippling, lighting shifting).\n\n### 4. AI Music & Sound Design\n\n*   Music Prompt (For Suno/Udio): Provide a prompt describing the Genre, Tempo/BPM, Key Instruments, and Mood to generate the perfect background track that fits the pacing of the video.',
            },
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__image-generation-1771413264228image-image-to-video-1771413315870image',
          source: 'image-generation-1771413264228',
          target: 'image-to-video-1771413315870',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-generation-1771413287406image-image-to-video-1771413370913image',
          source: 'image-generation-1771413287406',
          target: 'image-to-video-1771413370913',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-generation-1771413412775image-image-to-video-1771413748791image',
          source: 'image-generation-1771413412775',
          target: 'image-to-video-1771413748791',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-generation-1771413448570image-image-to-video-1771413833090image',
          source: 'image-generation-1771413448570',
          target: 'image-to-video-1771413833090',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1771413655694image-image-edit-1771413677671image',
          source: 'upload-1771413655694',
          target: 'image-edit-1771413677671',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1771413655694image-image-edit-1771413896069image',
          source: 'upload-1771413655694',
          target: 'image-edit-1771413896069',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771413677671image-image-to-video-1771413787192image',
          source: 'image-edit-1771413677671',
          target: 'image-to-video-1771413787192',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1771413896069image-image-to-video-1771413913607image',
          source: 'image-edit-1771413896069',
          target: 'image-to-video-1771413913607',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1771680412149text-text-to-speech-1771680339197text',
          source: 'input-1771680412149',
          target: 'text-to-speech-1771680339197',
          sourceHandle: 'text',
          targetHandle: 'text',
        },
        {
          id: 'xy-edge__input-1771686298092text-music-generation-1771686246251text',
          source: 'input-1771686298092',
          target: 'music-generation-1771686246251',
          sourceHandle: 'text',
          targetHandle: 'text',
        },
        {
          id: 'xy-edge__upload-1771413655694image-text-generation-1771849540169image',
          source: 'upload-1771413655694',
          target: 'text-generation-1771849540169',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'talking-vegetables',
    name: 'Talking Vegetables',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/talking-vegetables-800.mp4',
    description:
      'Create fun animated videos with Pixar-style talking vegetables and fruits sharing surprising facts. Perfect for educational content, social media, or kids entertainment.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'note-1770124918237',
          type: 'note',
          position: { x: 972.4338535574545, y: 67.09594480640055 },
          dragging: false,
          measured: { width: 404, height: 145 },
          selected: false,
          data: {
            type: 'note',
            label: 'How to Use',
            config: {
              note: '<h1>How to Use</h1><p><strong>1.</strong> Change the vegetable or fruit name in the <strong>"Vegetable/Fruit Input"</strong> node (e.g., "Carrot", "Banana", "Avocado").</p><p><strong>2.</strong> Run the workflow - it will automatically:</p><ul><li>Generate an interesting fact about your food</li><li>Create a Pixar-style character image</li><li>Write a funny video script</li><li>Produce an animated video</li></ul><p><strong>3.</strong> Download your video from the final node!</p>',
            },
          },
        },
        {
          id: 'input-1770123111124',
          type: 'input',
          position: { x: 908.9285714285716, y: 510.4761904761905 },
          dragging: false,
          measured: { width: 150, height: 162 },
          selected: false,
          data: {
            type: 'input',
            label: 'Vegetable/Fruit Input',
            config: { value: 'Tomato' },
            result: 'Tomato',
            status: 'success',
          },
        },
        {
          id: 'text-generation-1770123281350',
          type: 'text-generation',
          position: { x: 1532.0149419215063, y: 703.4937417739269 },
          dragging: false,
          measured: { width: 404, height: 856 },
          selected: false,
          data: {
            type: 'text-generation',
            label: 'FACT/IMAGE Generation',
            config: {
              model: 'gpt-5.4-pro',
              prompt: '@Vegetable/Fruit Input ',
              systemPrompt:
                "You are a fun vegetable/fruit facts expert AND Pixar-style image prompt writer.\n\nStep 1: Find ONE interesting, surprising fact about the given vegetable/fruit (storage tips, reactions with other foods, or surprising properties).\n\nStep 2: Create a detailed image prompt for a 3D Pixar/Dreamworks animated character of this vegetable/fruit with:\n- Big expressive cartoon eyes, small mouth, tiny arms\n- Water droplets for freshness\n- Kitchen or refrigerator setting\n- Character looking like it's about to share important news\n- Warm cinematic lighting, subsurface scattering\n\nOutput format (exactly like this):\nFACT: [the fact]\nIMAGE: [the image prompt]",
            },
            result:
              'FACT: Tomatoes release ethylene gas as they ripen, which can speed up ripening in nearby produce—so keeping a tomato next to an avocado or banana can make them ripen faster.\n\nIMAGE: A 3D Pixar/Dreamworks-style animated tomato character on a refrigerator shelf, bright red with subtle natural skin texture and tiny green stem on top, big expressive cartoon eyes and a small "about to speak" mouth, tiny noodle-like arms held slightly up as if delivering urgent news, covered in realistic water droplets for freshness; surrounded by kitchen items (clear crisper drawer, eggs in a carton, a banana and avocado in the background, glass jars, soft fridge light glow). The tomato is leaning forward with an excited, conspiratorial expression, cinematic warm lighting with gentle rim light, shallow depth of field, highly detailed subsurface scattering through the tomato flesh, glossy highlights on droplets, soft shadows, high-quality 3D render, family-friendly, vibrant colors, ultra-clean composition.',
            status: 'success',
          },
        },
        {
          id: 'text-generation-1770123500565',
          type: 'text-generation',
          position: { x: 2328.0440821410884, y: 771.4847982100906 },
          dragging: false,
          measured: { width: 404, height: 292 },
          selected: false,
          data: {
            type: 'text-generation',
            label: 'Video Prompt',
            config: {
              model: 'gpt-5.4-pro',
              prompt: '@FACT/IMAGE Generation ',
              systemPrompt:
                'You are a comedic video prompt writer for talking food animations.\n\nYou will receive output containing a FACT and IMAGE description. Create a SHORT video prompt (1-2 sentences) where:\n\n1. The vegetable/fruit character SPEAKS directly to camera in FIRST PERSON, sharing the fact dramatically\n2. A visual gag demonstrates the fact with exaggerated comedy\n3. Include specific movements: eye expressions, arm gestures, reactions\n\nExample: If fact is about onions making potatoes sprout:\n"Worried onion character backs away saying \'Stay away potato!\' while a nearby potato suddenly grows googly eyes and wild sprouts everywhere, onion gasps dramatically."\n\nOutput ONLY the video prompt, nothing else. Make it funny and expressive!',
            },
            result:
              'The tomato leans in close to the camera with squinty "I know a secret" eyes and frantic little arm waves, whisper-shouting, "I\'m ripening—and I\'m basically a walking ethylene gas bomb… I make everything around me ripen FASTER!" As it dramatically "puffs" invisible gas like a tiny superhero, the banana and avocado in the background instantly blush, swell, and go from green to ripe in a snap—banana doing a surprised double-take while the avocado\'s pit pops like a cork—while the tomato throws its arms up and side-eyes the camera like, "Told ya."',
            status: 'success',
          },
        },
        {
          id: 'image-generation-1770123673918',
          type: 'image-generation',
          position: { x: 2183.1798101500917, y: 106.80154059656928 },
          dragging: false,
          measured: { width: 424, height: 424 },
          selected: false,
          data: {
            type: 'image-generation',
            label: '#3 Text to Image',
            config: {
              model: 'Nano Banana Pro',
              prompt: '@FACT/IMAGE Generation',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/db394266-e577-4c82-a80d-bdb18acf3b7b.png',
            status: 'success',
          },
        },
        {
          id: 'image-to-video-1770124070362',
          type: 'image-to-video',
          position: { x: 3145.1165335433643, y: 293.32373159119214 },
          dragging: false,
          measured: { width: 424, height: 424 },
          selected: false,
          data: {
            type: 'image-to-video',
            label: '#4 Image to Video',
            config: {
              model: 'Grok Imagine Image',
              prompt: '@Video Prompt',
              duration: '10',
              aspectRatio: '1:1',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/da18a230-d9a0-4896-a935-1f04be297cea.mp4',
              lastFrame:
                'https://fal.media/files/kangaroo/P-zC9d9ZasllT0g7XiuYo_025c676c2cf946a383f1b25720c988cc.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8cfed1/wxutjcBWWuVdoS7MEKLr1_886a1bf7554d46f9812ff5392282d03f.jpg',
            },
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__input-1770123111124text-text-generation-1770123281350prompt',
          source: 'input-1770123111124',
          target: 'text-generation-1770123281350',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__text-generation-1770123281350text-text-generation-1770123500565prompt',
          source: 'text-generation-1770123281350',
          target: 'text-generation-1770123500565',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__text-generation-1770123281350text-image-generation-1770123673918prompt',
          source: 'text-generation-1770123281350',
          target: 'image-generation-1770123673918',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-generation-1770123673918image-image-to-video-1770124070362image',
          source: 'image-generation-1770123673918',
          target: 'image-to-video-1770124070362',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__text-generation-1770123500565text-image-to-video-1770124070362prompt',
          source: 'text-generation-1770123500565',
          target: 'image-to-video-1770124070362',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
      ],
    },
  },
  {
    id: 'generate-images-scenarios',
    name: 'Generate Images – Starter Scenarios',
    backgroundImage:
      'https://media.easy-peasy.ai/ff5e9a62-f4f6-4c39-b2c1-8294b2426304/f4d15a6e-dcf9-4746-b497-441471e40581.png',
    description:
      'Three ways to generate images: simple prompt, AI-written prompt, and reference image editing, each with inline documentation and a video walkthrough.',
    category: 'Image',
    flow_data: {
      nodes: [
        // Scenario 1 – Simple text to image
        {
          id: 'note-simple',
          type: 'note',
          position: { x: -1190.7522012758886, y: 148.44113286649258 },
          data: {
            type: 'note',
            label: 'Text to image',
            config: {
              note: '<p><strong>Use this path when you already have a clear prompt.</strong></p><p><em>1. Open the "</em><strong><em>Text To Image</em></strong><em>" node.</em></p><p>2. Edit the Prompt field with your description.</p><p>3. Run the node and preview the generated image.</p>',
            },
          },
        },
        {
          id: 'video-simple-doc',
          type: 'upload',
          position: { x: -663.0750284158892, y: -8.38541459811838 },
          data: {
            type: 'upload',
            label: 'Docs – Video walkthrough',
            config: {
              audioUrl: '',
              imageUrl: '',
              videoUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/078b3baa-ae15-4160-86ff-a0093b9a7080.mp4',
              mediaType: 'video',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/078b3baa-ae15-4160-86ff-a0093b9a7080.mp4',
          },
        },
        {
          id: 'note-1763356184588',
          type: 'note',
          position: { x: 683.8002128903789, y: 189.55439266493653 },
          data: {
            type: 'note',
            label: 'Prompt',
            config: {
              note: '<p><strong>Try this prompt:</strong></p><p><strong><br></strong><em>"A vibrant coral reef bustling with marine life, under the clear blue ocean."</em></p><p></p>',
            },
          },
        },
        {
          id: 'image-generation-1763358382897',
          type: 'image-generation',
          position: { x: 160.69986423830102, y: 4.856592877922374 },
          data: {
            type: 'image-generation',
            label: 'Text To Image',
            config: {
              model: 'Seedream 4.0',
              prompt: '',
              outputs: 1,
              dimensions: '1:1',
            },
          },
        },

        // Scenario 2 – Prompt via text generation
        {
          id: 'note-textgen',
          type: 'note',
          position: { x: -1193.4356428295218, y: 832.7720176005272 },
          data: {
            type: 'note',
            label: 'Prompt via text generation',
            config: {
              note: '<p><strong>Use this path when you have a rough idea but need help writing a strong image prompt.</strong></p><p>1. In the "<strong>Text Generation</strong>" node, describe your idea in the Prompt field.</p><p>2. Run the node to get a detailed prompt.</p><p>3. The text output is already connected into the image node.</p><p>4. Run the image node and preview the generated image directly inside it.</p>',
            },
          },
        },
        {
          id: 'video-textgen-doc',
          type: 'upload',
          position: { x: -695.2773085925265, y: 823.048488292064 },
          data: {
            type: 'upload',
            label: 'Docs – Video walkthrough',
            config: {
              audioUrl: '',
              imageUrl: '',
              videoUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/19dac9d1-ec84-49c5-bd92-ee5b0a32cde6.mp4',
              mediaType: 'video',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/19dac9d1-ec84-49c5-bd92-ee5b0a32cde6.mp4',
          },
        },
        {
          id: 'text-generation-1763357981542',
          type: 'text-generation',
          position: { x: 173.43783675769794, y: 852.2371324386529 },
          data: {
            type: 'text-generation',
            label: 'Generate prompt from idea',
            config: {
              model: 'gpt-5-mini',
              prompt:
                'Write a paragraph long image generation prompt for a product hero image showing a new smartwatch on a dark gradient background with soft reflections.',
              systemPrompt: '',
            },
          },
        },
        {
          id: 'image-generation-1763357998712',
          type: 'image-generation',
          position: { x: 779.8109137333112, y: 845.0935424904849 },
          data: {
            type: 'image-generation',
            label: 'Generate image (AI-written prompt)',
            config: {
              model: 'Seedream 4.0',
              prompt: '@Generate prompt from idea',
              outputs: 1,
              dimensions: '16:9',
            },
          },
        },

        // Scenario 3 – Reference image editing
        {
          id: 'note-reference',
          type: 'note',
          position: { x: -1197.5225021030494, y: 1687.655444290995 },
          data: {
            type: 'note',
            label: 'Reference image → image',
            config: {
              note: '<p><strong>Use this path when you want to generate variations or enhancements based on an existing image.</strong></p><p>1. In "<strong>Upload Media</strong>" or "<strong>Select Media</strong>" node, select an image from your previous generations or upload from your device.</p><p>2. Adjust instructions in the "<strong>Image to Image</strong>" node.</p><p>3. Run the edit node and preview the updated image directly inside it.</p>',
            },
          },
        },
        {
          id: 'video-reference-doc',
          type: 'upload',
          position: { x: -695.8485339180713, y: 1631.1100259630202 },
          data: {
            type: 'upload',
            label: 'Docs – Video walkthrough',
            config: {
              audioUrl: '',
              imageUrl: '',
              videoUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/7dfa9cc4-9683-4bee-9314-81ee9c4e4d47.mp4',
              mediaType: 'video',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/7dfa9cc4-9683-4bee-9314-81ee9c4e4d47.mp4',
          },
        },
        {
          id: 'select-image-1763360456049',
          type: 'select-image',
          position: { x: 198.08318063868285, y: 1774.767232682571 },
          data: {
            type: 'select-image',
            label: 'Reference image from gallery',
            config: {},
          },
        },
        {
          id: 'image-edit-1763360467869',
          type: 'image-edit',
          position: { x: 785.038930645026, y: 1648.13953345711 },
          data: {
            type: 'image-edit',
            label: 'Image to image (reference)',
            config: {
              useHD: false,
              prompt: '',
              editModel: 'Seedream 4.0',
              dimensions: '1:1',
            },
          },
        },
      ],
      edges: [
        // Scenario 2
        {
          id: 'xy-edge__text-generation-1763357981542text-image-generation-1763357998712prompt',
          source: 'text-generation-1763357981542',
          target: 'image-generation-1763357998712',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },

        // Scenario 3
        {
          id: 'xy-edge__select-image-1763360456049image-image-edit-1763360467869image',
          source: 'select-image-1763360456049',
          target: 'image-edit-1763360467869',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'generate-video-scenarios',
    name: 'Generate Videos – Starter Scenarios',
    backgroundImage:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/9d34dbec-d616-493c-b685-4b9025ac2d94.png',
    description:
      'Four powerful ways to create videos: from text, from images, using frame interpolation, and by editing images before animation.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'note-1763531435956',
          type: 'note',
          position: { x: 80, y: 84 },
          data: {
            type: 'note',
            label: 'Text to Video',
            config: {
              note: '<h3>Text to Video</h3><p><strong>Create videos directly from text descriptions.</strong></p><p>1. Open the <strong>Text to Video</strong> node.</p><p>2. Select a model (e.g., Veo 3.1 Fast).</p><p>3. Choose your aspect ratio.</p><p>4. Enter a detailed prompt or connect a Text Input node.</p><p>5. Click <strong>Run</strong>.</p>',
            },
          },
          measured: { width: 400, height: 240 },
        },
        {
          id: 'input-1763529822497',
          type: 'input',
          position: { x: 1021, y: 95.99999999999994 },
          data: {
            type: 'input',
            label: 'Prompt Input',
            config: {
              value:
                'A young woman stands motionless in front of a vibrant gradient wall that shifts from magenta to orange, with bold graffiti-style white text blurred in the background. She wears a glossy, oversized purple puffer jacket adorned with neon-like graphic patches, the hood up, framing her serious gaze. Her face is decorated with colorful sticker decals, and soft colored lighting wraps around her form, creating vivid reflections and cinematic depth.\n\nThe camera performs a slow arc to the right, beginning from her front-left side and gliding smoothly to a three-quarter right angle. She stays perfectly still throughout, maintaining unbroken eye contact with the lens. As the perspective shifts, the lighting reflections slide across the shiny fabric of the jacket, emphasizing its texture and futuristic design. The background remains consistent, the graffiti text subtly shifting angle but never distorting.\n\nMaintain full consistency of light gradients and text size across the arc. No distortion or stretching of the jacket material or face stickers. Background must retain its color fade and depth with smooth parallax, and lighting transitions must feel natural on her face and reflective jacket as the camera moves.',
            },
            result:
              'A young woman stands motionless in front of a vibrant gradient wall that shifts from magenta to orange, with bold graffiti-style white text blurred in the background. She wears a glossy, oversized purple puffer jacket adorned with neon-like graphic patches, the hood up, framing her serious gaze. Her face is decorated with colorful sticker decals, and soft colored lighting wraps around her form, creating vivid reflections and cinematic depth.\n\nThe camera performs a slow arc to the right, beginning from her front-left side and gliding smoothly to a three-quarter right angle. She stays perfectly still throughout, maintaining unbroken eye contact with the lens. As the perspective shifts, the lighting reflections slide across the shiny fabric of the jacket, emphasizing its texture and futuristic design. The background remains consistent, the graffiti text subtly shifting angle but never distorting.\n\nMaintain full consistency of light gradients and text size across the arc. No distortion or stretching of the jacket material or face stickers. Background must retain its color fade and depth with smooth parallax, and lighting transitions must feel natural on her face and reflective jacket as the camera moves.',
            status: 'success',
          },
          dragging: false,
          measured: { width: 150, height: 462 },
          selected: false,
        },
        {
          id: 'text-to-video-1763529812197',
          type: 'text-to-video',
          position: { x: 2636.7405924996215, y: 145.2748527927347 },
          data: {
            type: 'text-to-video',
            label: 'Text to Video',
            config: {
              model: 'Veo 3.1 Fast',
              prompt: '@Prompt Input',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/3af20d38-6011-4d68-80da-dea4b2e2c5a9.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/tiger/GqCr6GL3XR77krH4lOdzE_1b39b2ff857740e692456cf85208d381.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/elephant/mgzAqPwrT1XGHNiUg6jqc_ca6e82eb8153423d91b1121654e0c6ae.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 280, height: 404 },
          selected: true,
        },
        {
          id: 'note-1763531687950',
          type: 'note',
          position: { x: 69, y: 1168 },
          data: {
            type: 'note',
            label: 'Image to Video',
            config: {
              note: '<h3>Image to Video</h3><p><strong>Animate an existing image.</strong></p><p>1. Use <strong>Text to Image</strong> to generate a base, or <strong>Upload Media</strong>.</p><p>2. Connect it to the <strong>Image to Video</strong> node.</p><p>3. Select model and aspect ratio.</p><p>4. Optionally add a prompt to guide the animation.</p><p>5. Click <strong>Run</strong>.</p>',
            },
          },
          measured: { width: 400, height: 240 },
        },
        {
          id: 'image-generation-1763529424936',
          type: 'image-generation',
          position: { x: 832.7255133583665, y: 1093.1216427467725 },
          data: {
            type: 'image-generation',
            label: 'Generate Base Image',
            config: {
              model: 'Seedream 4.0',
              prompt:
                "A robotic feline character in the midst of piloting a futuristic flying car. Surrounding it is a dazzling cityscape of the future, filled with astoundingly tall buildings boasting innovative architecture and radiant neon lights. The skies are teeming with similar flying vehicles, vivid billboards, and towering holographic advertisements. The sky is clear showcasing bright constellations which make it seem as if the stars themselves are participating in the city's spectacle.",
              outputs: 1,
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/3ce5b820-96bc-4667-8775-f2223344cdd0.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 687, height: 388 },
          selected: false,
        },
        {
          id: 'image-to-video-1763529589527',
          type: 'image-to-video',
          position: { x: 2526.859004195258, y: 1039.8902053433467 },
          data: {
            type: 'image-to-video',
            label: 'Animate Image',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt: 'animate this image. everything in the scene is moving.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/48eba9cf-3cce-4b38-872d-cd9585583d85.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/kangaroo/xLt9Q3EojHLroa27thrxD_e74e0b84840b4baf8fd89b696938beb4.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/elephant/j8rk0zhPbZ7WDgAj4U-cr_054e6deec20840cb95212e7120f97a94.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'note-1763531956468',
          type: 'note',
          position: { x: 16, y: 2484 },
          data: {
            type: 'note',
            label: 'First & Last Frame',
            config: {
              note: '<h3>First & Last Frame to Video</h3><p><strong>Generate a video transitioning between two images.</strong></p><p>1. Prepare start and end images (via Generation or Upload).</p><p>2. Connect them to the <strong>First-Last Frame to Video</strong> node.</p><p>3. Select model and settings.</p><p>4. Click <strong>Run</strong>.</p>',
            },
          },
          measured: { width: 400, height: 256 },
        },
        {
          id: 'image-generation-1763465932372',
          type: 'image-generation',
          position: { x: 962, y: 2309 },
          data: {
            type: 'image-generation',
            label: 'Start Frame',
            config: {
              model: 'Seedream 4.0',
              prompt:
                '{\n  "description": "A high-fidelity, highly detailed, studio portrait of a beautiful young East Asian woman with a severe, chin-length black bob haircut and delicate see-through bangs. She has an intense, slightly seductive gaze, with dark, smokey eyeliner, long lashes, and soft rose-toned lips. She is styled in a dark, gothic-inspired outfit: a cropped black camisole top with thin straps and metal rings, heavily layered with multiple silver and black chains and a thick black leather choker. A matching black leather cuff with studs is worn on her right upper arm. The overall aesthetic is dark, edgy, and high-fashion K-pop/Goth.",\n  "negative_prompt": "blurry, low quality, distorted, messy background, unnatural skin, weird hands, watermark, text, logo",\n  "style": "Cinematic portrait, professional studio lighting, high resolution, detailed skin texture, fashion editorial, high contrast",\n  "camera": {\n    "shot_type": "Medium Close-up / Head-and-Shoulders Portrait",\n    "focal_length_mm": 85,\n    "f_stop": 1.8,\n    "shutter_speed": "1/160s (suggestive of flash sync)",\n    "angle": "Slightly High Angle (looking slightly down at the subject)",\n    "tilt_roll": "0 degrees (level horizon)",\n    "lens_effect": "Shallow depth of field (DoF), sharp focus on eyes, soft background bokeh"\n  },\n  "pose": {\n    "body_position": "Torso turned slightly to the right (viewer\'s left), with the left shoulder dipped lower.",\n    "head_position": "Head turned back towards the camera, chin slightly lowered, direct and intense eye contact.",\n    "arm_position": "Right arm raised slightly, out of frame, suggesting it is resting on an unseen surface. Left arm visible only by the shoulder strap.",\n    "facial_expression": "Serious, confident, intense, with lips slightly parted."\n  },\n  "lighting_and_color": {\n    "main_light": "Softbox from the upper-right (camera left), illuminating the face and chest evenly.",\n    "rim_light": "Subtle backlight or hair light used to separate the hair and shoulders from the background.",\n    "background_color": "Solid deep violet / magenta (often achieved with a colored gel on a background light).",\n    "color_palette": "Black, silver, magenta, and soft skin tones."\n  }\n}',
              outputs: 1,
              dimensions: '1:1',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ccf70c33-16b9-4464-832d-51a058d2b93b.png',
            status: 'success',
          },
          measured: { width: 388, height: 388 },
        },
        {
          id: 'image-edit-1763468197381',
          type: 'image-edit',
          position: { x: 1928, y: 1993 },
          data: {
            type: 'image-edit',
            label: 'Create End Frame',
            config: {
              useHD: false,
              prompt:
                'she is in Paris wearing modern coat. Standing with coffee paper cup in one hand. Other hand in pocket. and smiling',
              editModel: 'Seedream 4.0',
              dimensions: '1:1',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/91461576-f22d-43e9-bd5a-2895f2f636e1.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 388, height: 388 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1763468167186',
          type: 'first-last-frame-video',
          position: { x: 2790.913901523585, y: 2285.7685625965737 },
          data: {
            type: 'first-last-frame-video',
            label: 'Transition Video',
            config: {
              model: 'Veo 3.1 First-Last Frame',
              prompt: 'transform',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5406370d-d0aa-4d51-b68d-1499ccaa72e8.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/zebra/bFq0aAy5zLTgEJEc6SzXA_f39af4fe70aa4bdcb3b07ff8fffc2cfb.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/koala/a-MTaT6zxh96Xmnr8zZLD_a80deff5858a4c95a72f1ed1ab7e297b.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 280, height: 404 },
          selected: false,
        },
        {
          id: 'note-1763532129301',
          type: 'note',
          position: { x: 20.25267006597568, y: 3719.898681762506 },
          data: {
            type: 'note',
            label: 'Edit & Animate',
            config: {
              note: '<h3>Edit &amp; Animate</h3><p><strong>Modify an image before animating it.</strong></p><p>1. Upload an image using <strong>Upload Media</strong>.</p><p>2. Connect to <strong>Image to Image</strong> to transform it.</p><p>3. Connect the result to <strong>Image to Video</strong>.</p><p>4. Configure settings and click <strong>Run</strong>.</p>',
            },
          },
          dragging: false,
          measured: { width: 400, height: 196 },
          selected: false,
        },
        {
          id: 'upload-1763271572230',
          type: 'upload',
          position: { x: 731.0979465665341, y: 3700.0000000000005 },
          data: {
            type: 'upload',
            label: 'Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b8e317de-9758-4aa1-9bd7-c35cc5c4ea8e.webp',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b8e317de-9758-4aa1-9bd7-c35cc5c4ea8e.webp',
          },
          dragging: false,
          measured: { width: 687, height: 388 },
          selected: false,
        },
        {
          id: 'input-1763271598125',
          type: 'input',
          position: { x: 1003, y: 3152 },
          data: {
            type: 'input',
            label: 'Edit Instructions',
            config: {
              value:
                'Real city photo transformed into knitted fabric style; all buildings, textures and objects recreated as yarn structures; precise stitch details, realistic depth, perspective preserved; soft wool texture, volumetric lighting, natural shadows, high-resolution, hyperdetailed, 3D knitted appearance.», 4K',
            },
            result:
              'Real city photo transformed into knitted fabric style; all buildings, textures and objects recreated as yarn structures; precise stitch details, realistic depth, perspective preserved; soft wool texture, volumetric lighting, natural shadows, high-resolution, hyperdetailed, 3D knitted appearance.», 4K',
            status: 'success',
          },
          dragging: false,
          measured: { width: 150, height: 262 },
          selected: false,
        },
        {
          id: 'image-edit-1763271610937',
          type: 'image-edit',
          position: { x: 1760.8234599249006, y: 3709.396129388406 },
          data: {
            type: 'image-edit',
            label: 'Transform Style',
            config: {
              useHD: false,
              prompt: '@Edit Instructions',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4fc94d0a-ff9f-4a75-bb8b-e82edd4e8cf2.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 687, height: 388 },
          selected: false,
        },
        {
          id: 'image-to-video-1763271961524',
          type: 'image-to-video',
          position: { x: 2737.7685625965737, y: 3701.451026716733 },
          data: {
            type: 'image-to-video',
            label: 'Animate Result',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Famous Singapore Merlions. boats, people walking, plane.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ca4c3cad-78ea-42b6-90b9-8d4950a6a499.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/zebra/dDsDHV8jC3ytXr6QhcMfT_3aac563c4596450ba78f947dfdbb802c.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/zebra/OQ-BtOdByO-x_Fa5MzSrx_7d7f47f2e4e648ce8163565d880c0b0e.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1763271572230image-image-edit-1763271610937image',
          source: 'upload-1763271572230',
          target: 'image-edit-1763271610937',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763271610937prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763271610937',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763271610937image-image-to-video-1763271961524image',
          source: 'image-edit-1763271610937',
          target: 'image-to-video-1763271961524',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-generation-1763465932372image-image-edit-1763468197381image',
          source: 'image-generation-1763465932372',
          target: 'image-edit-1763468197381',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-generation-1763465932372image-first-last-frame-video-1763468167186first-frame',
          source: 'image-generation-1763465932372',
          target: 'first-last-frame-video-1763468167186',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__image-edit-1763468197381image-first-last-frame-video-1763468167186last-frame',
          source: 'image-edit-1763468197381',
          target: 'first-last-frame-video-1763468167186',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__image-generation-1763529424936image-image-to-video-1763529589527image',
          source: 'image-generation-1763529424936',
          target: 'image-to-video-1763529589527',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763529822497text-text-to-video-1763529812197prompt',
          source: 'input-1763529822497',
          target: 'text-to-video-1763529812197',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
      ],
    },
  },
  {
    id: 'workflow-fundamentals',
    name: 'Workflow Fundamentals – Getting Started',
    backgroundImage:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/3fbc2183-ed10-4c47-a823-b3aababb6997.png',
    description:
      'Learn the basics of building workflows: connecting nodes, using text prompts, image editing, and generating videos with different models.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'upload-1769142812479',
          type: 'upload',
          position: { x: 907.0455791202074, y: 258.05505866737013 },
          data: {
            type: 'upload',
            label: 'Reference Image',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/a2bb9b12-7ac9-46ab-808b-965f3b99e6ab.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/a2bb9b12-7ac9-46ab-808b-965f3b99e6ab.png',
          },
        },
        {
          id: 'image-edit-1769143068878',
          type: 'image-edit',
          position: { x: 1805.7564981277621, y: 269.9601543765289 },
          data: {
            type: 'image-edit',
            label: 'Transform with AI',
            config: {
              useHD: false,
              prompt: '@Scene Description',
              editModel: 'Nano Banana Pro',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/3fbc2183-ed10-4c47-a823-b3aababb6997.png',
            status: 'success',
          },
        },
        {
          id: 'image-to-video-1769143775320',
          type: 'image-to-video',
          position: { x: 2802.6305591238197, y: 271.43698956182584 },
          data: {
            type: 'image-to-video',
            label: 'Quick Video (Simple Prompt)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'Camera slowly pans across the cityscape as vehicles fly by, golden sunset light reflecting off buildings.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/abdc14d4-ab5c-4320-96d0-0258abd6b538.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8b8282/IMvvNSjhrMffEVdGNdcCS_947a86888d36473b8b303286fa64500f.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8b8282/pXJojaJqy2gNwJcUu1RsK_3a906e09d325499b8fbc97c7926ba5f3.jpg',
            },
            status: 'success',
          },
        },
        {
          id: 'input-1769150225760',
          type: 'input',
          position: { x: 1049.3558909277024, y: 835.74021310165 },
          data: {
            type: 'input',
            label: 'Scene Description',
            config: {
              value: 'a futuristic cityscape at sunset with flying vehicles.',
            },
          },
        },
        {
          id: 'text-generation-1769150484384',
          type: 'text-generation',
          position: { x: 1973.924213735971, y: 994.8966639475531 },
          data: {
            type: 'text-generation',
            label: 'Expand to Cinematic Prompt',
            config: {
              prompt:
                '@Scene Description Convert this image description into a simple cinematic video prompt with camera movements and atmospheric details.',
            },
          },
        },
        {
          id: 'image-to-video-1769150524287',
          type: 'image-to-video',
          position: { x: 2803.47279081012, y: 915.6238446203529 },
          data: {
            type: 'image-to-video',
            label: 'Cinematic Video (AI Prompt)',
            config: {
              model: 'Seedance 1.5 Pro Image',
              prompt: '@Expand to Cinematic Prompt',
              duration: '12',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/cad2f4d7-358b-4de7-a3a6-3b86b5a169fa.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8c2ed9/zqiNs-pudju2CWQon4Tzm_01151f6aa0e04df1b75ebf848325a453.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8c2ed9/syZWCcBFKk1Fz_kusJ158_c345655c98a340acbd10c2c61f289219.jpg',
            },
            status: 'success',
          },
        },
        {
          id: 'note-1769592566234',
          type: 'note',
          position: { x: 1029.8271031967554, y: 1166.7068835911646 },
          data: {
            type: 'note',
            label: 'How This Workflow Works',
            config: {
              note: '<h2>Workflow Fundamentals</h2><p>This template demonstrates two approaches to video generation:</p><p></p><p><strong>Path 1 – Simple Prompt (Top Row)</strong></p><ol><li>Upload or select a reference image</li><li>Transform it using Image to Image with a text description</li><li>Generate a video with a manually written prompt</li></ol><p></p><p><strong>Path 2 – AI-Enhanced Prompt (Bottom Row)</strong></p><ol><li>Start with a simple scene description in the Text Input node</li><li>Use Text Generation to expand it into a detailed cinematic prompt</li><li>Generate a longer, more cinematic video</li></ol><p></p><p><strong>Tips:</strong></p><ul><li>Edit the "Scene Description" node to change both paths at once</li><li>Compare the two video outputs to see how prompt detail affects results</li><li>Try different video models for varied styles</li></ul><p></p><p>📺 <a href="https://youtu.be/t4I5dQ3p2f8" target="_blank"><strong>Watch the full tutorial on YouTube</strong></a></p>',
            },
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1769142812479image-image-edit-1769143068878image',
          source: 'upload-1769142812479',
          target: 'image-edit-1769143068878',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1769143068878image-image-to-video-1769143775320image',
          source: 'image-edit-1769143068878',
          target: 'image-to-video-1769143775320',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1769150225760text-image-edit-1769143068878prompt',
          source: 'input-1769150225760',
          target: 'image-edit-1769143068878',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__input-1769150225760text-text-generation-1769150484384prompt',
          source: 'input-1769150225760',
          target: 'text-generation-1769150484384',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__text-generation-1769150484384text-image-to-video-1769150524287prompt',
          source: 'text-generation-1769150484384',
          target: 'image-to-video-1769150524287',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1769143068878image-image-to-video-1769150524287image',
          source: 'image-edit-1769143068878',
          target: 'image-to-video-1769150524287',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'singapore-knitted-city',
    name: 'Singapore Knitted City - Short Video',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/ca4c3cad-78ea-42b6-90b9-8d4950a6a499.mp4',
    description:
      'Transform multiple Singapore landmark images into knitted fabric style and create animated videos, culminating in a special animation of the word "SINGAPORE" being knitted letter by letter.',
    category: 'Video',
    flow_data: {
      edges: [
        {
          id: 'xy-edge__upload-1763271361463image-image-edit-1763271445878image',
          source: 'upload-1763271361463',
          target: 'image-edit-1763271445878',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763271445878prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763271445878',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__upload-1763271572230image-image-edit-1763271610937image',
          source: 'upload-1763271572230',
          target: 'image-edit-1763271610937',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763271610937prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763271610937',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763271846962prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763271846962',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__upload-1763271833471image-image-edit-1763271846962image',
          source: 'upload-1763271833471',
          target: 'image-edit-1763271846962',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763271445878image-image-to-video-1763271870024image',
          source: 'image-edit-1763271445878',
          target: 'image-to-video-1763271870024',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763271610937image-image-to-video-1763271961524image',
          source: 'image-edit-1763271610937',
          target: 'image-to-video-1763271961524',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763271846962image-image-to-video-1763272047623image',
          source: 'image-edit-1763271846962',
          target: 'image-to-video-1763272047623',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763272146682image-image-edit-1763272174822image',
          source: 'upload-1763272146682',
          target: 'image-edit-1763272174822',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763272174822prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763272174822',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763272174822image-image-to-video-1763272201926image',
          source: 'image-edit-1763272174822',
          target: 'image-to-video-1763272201926',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763272549490image-image-edit-1763272565677image',
          source: 'upload-1763272549490',
          target: 'image-edit-1763272565677',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763272565677prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763272565677',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763272565677image-image-to-video-1763272640210image',
          source: 'image-edit-1763272565677',
          target: 'image-to-video-1763272640210',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763272845750prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763272845750',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__upload-1763272826377image-image-edit-1763272845750image',
          source: 'upload-1763272826377',
          target: 'image-edit-1763272845750',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763272845750image-image-to-video-1763272882488image',
          source: 'image-edit-1763272845750',
          target: 'image-to-video-1763272882488',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763273651788prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763273651788',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__upload-1763273643930image-image-edit-1763273651788image',
          source: 'upload-1763273643930',
          target: 'image-edit-1763273651788',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763273651788image-image-to-video-1763273782346image',
          source: 'image-edit-1763273651788',
          target: 'image-to-video-1763273782346',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763273819734image-image-edit-1763273831900image',
          source: 'upload-1763273819734',
          target: 'image-edit-1763273831900',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763273831900prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763273831900',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763273831900image-image-to-video-1763274188911image',
          source: 'image-edit-1763273831900',
          target: 'image-to-video-1763274188911',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763274226355image-image-edit-1763274260813image',
          source: 'upload-1763274226355',
          target: 'image-edit-1763274260813',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763274260813prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763274260813',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763274260813image-image-to-video-1763274360236image',
          source: 'image-edit-1763274260813',
          target: 'image-to-video-1763274360236',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763275563188prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763275563188',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__upload-1763275554503image-image-edit-1763275563188image',
          source: 'upload-1763275554503',
          target: 'image-edit-1763275563188',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763275563188image-image-to-video-1763275585193image',
          source: 'image-edit-1763275563188',
          target: 'image-to-video-1763275585193',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763281006106image-image-edit-1763281032310image',
          source: 'upload-1763281006106',
          target: 'image-edit-1763281032310',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763271598125text-image-edit-1763281032310prompt',
          source: 'input-1763271598125',
          target: 'image-edit-1763281032310',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763281032310image-image-edit-1763281660397image',
          source: 'image-edit-1763281032310',
          target: 'image-edit-1763281660397',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763281660397image-first-last-frame-video-1763282188615first-frame',
          source: 'image-edit-1763281660397',
          target: 'first-last-frame-video-1763282188615',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__image-edit-1763281032310image-first-last-frame-video-1763282188615last-frame',
          source: 'image-edit-1763281032310',
          target: 'first-last-frame-video-1763282188615',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
      ],
      nodes: [
        {
          id: 'upload-1763271361463',
          data: {
            type: 'upload',
            label: '#1 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/6e05ced3-c909-4b14-ac1c-d6a5b6cb2dca.avif',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/6e05ced3-c909-4b14-ac1c-d6a5b6cb2dca.avif',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: -93.53229333273475, y: 146.31914848038895 },
          selected: false,
        },
        {
          id: 'image-edit-1763271445878',
          data: {
            type: 'image-edit',
            label: '#2 Image to Image',
            config: {
              useHD: false,
              prompt:
                '@#4 Input Real city photo transformed into knitted fabric style; all buildings, textures and objects recreated as yarn structures; precise stitch details, realistic depth, perspective preserved; soft wool texture, volumetric lighting, natural shadows, high-resolution, hyperdetailed, 3D knitted appearance.», 4K',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2bc3be3c-35cc-4066-8587-bcac13650ae9.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1199.2066537686967, y: 92.99595807800091 },
          selected: false,
        },
        {
          id: 'upload-1763271572230',
          data: {
            type: 'upload',
            label: '#3 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b8e317de-9758-4aa1-9bd7-c35cc5c4ea8e.webp',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b8e317de-9758-4aa1-9bd7-c35cc5c4ea8e.webp',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: -50.98514130330767, y: 848.0454536160024 },
          selected: false,
        },
        {
          id: 'input-1763271598125',
          data: {
            type: 'input',
            label: '#4 Input',
            config: {
              value:
                'Real city photo transformed into knitted fabric style; all buildings, textures and objects recreated as yarn structures; precise stitch details, realistic depth, perspective preserved; soft wool texture, volumetric lighting, natural shadows, high-resolution, hyperdetailed, 3D knitted appearance.», 4K',
            },
            result:
              'Real city photo transformed into knitted fabric style; all buildings, textures and objects recreated as yarn structures; precise stitch details, realistic depth, perspective preserved; soft wool texture, volumetric lighting, natural shadows, high-resolution, hyperdetailed, 3D knitted appearance.», 4K',
            status: 'success',
          },
          type: 'input',
          dragging: false,
          measured: { width: 150, height: 262 },
          position: { x: 803.4596338504913, y: -596.6791529094368 },
          selected: false,
        },
        {
          id: 'image-edit-1763271610937',
          data: {
            type: 'image-edit',
            label: '#5 Image to Image',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4fc94d0a-ff9f-4a75-bb8b-e82edd4e8cf2.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1204.1495807086783, y: 836.2173891095392 },
          selected: false,
        },
        {
          id: 'upload-1763271833471',
          data: {
            type: 'upload',
            label: '#6 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/80143af8-626e-47f7-92fa-23c3af78615e.avif',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/80143af8-626e-47f7-92fa-23c3af78615e.avif',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 579, height: 388 },
          position: { x: -33.4370408772956, y: -396.09175655649506 },
          selected: false,
        },
        {
          id: 'image-edit-1763271846962',
          data: {
            type: 'image-edit',
            label: '#2 Image to Image (Copy)',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/096b42f7-9322-4252-8433-175e8ccec700.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1178.070115272745, y: -435.12933878660806 },
          selected: false,
        },
        {
          id: 'image-to-video-1763271870024',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Famous Singapore Marina Bay Sands. boats, cars on bridge. plane.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/514fcfda-373e-4497-9f28-5e32d409e34c.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/panda/ZpJ-nOdM6Xh9ujr6mcFri_26e3584fbd9b458fb9ac9d3fd9b0894b.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/tiger/foWaNl6TPYOWtpHjCnxsb_0229e51b99e54ae591e2425689cb0fb4.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2188.265068036775, y: 98.50459830296563 },
          selected: false,
        },
        {
          id: 'image-to-video-1763271961524',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Famous Singapore Merlions. boats, people walking, plane.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ca4c3cad-78ea-42b6-90b9-8d4950a6a499.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/zebra/dDsDHV8jC3ytXr6QhcMfT_3aac563c4596450ba78f947dfdbb802c.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/zebra/OQ-BtOdByO-x_Fa5MzSrx_7d7f47f2e4e648ce8163565d880c0b0e.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2187.6502731118594, y: 819.1468241067813 },
          selected: false,
        },
        {
          id: 'image-to-video-1763272047623',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Famous Singapore Gardens By the Bay.people walking. Plane',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5011d8b2-510d-4167-b19d-0d5d298bdb2e.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/penguin/hCta6Bv6uoZ12ptRY_bXr_716962c53fa0490a820ab46ad5f6bff3.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/tiger/B_mrsBNB-mTx5IoFLURQP_69126fa03bbc4128aedebe2b11d8acd2.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2185.9110498826167, y: -454.27181078422836 },
          selected: false,
        },
        {
          id: 'upload-1763272146682',
          data: {
            type: 'upload',
            label: '#9 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/75e9c1f2-a341-487e-aed9-8b7158d89796.jpeg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/75e9c1f2-a341-487e-aed9-8b7158d89796.jpeg',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 686, height: 388 },
          position: { x: -57.98535702193294, y: -1008.6987354645707 },
          selected: false,
        },
        {
          id: 'image-edit-1763272174822',
          data: {
            type: 'image-edit',
            label: '#10 Image to Image',
            config: { prompt: '@#4 Input' },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/6cc24b83-e96a-431b-8ff4-98cc368fa074.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 388, height: 388 },
          position: { x: 1304.1934845020512, y: -1049.4099113930135 },
          selected: false,
        },
        {
          id: 'image-to-video-1763272201926',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt: 'animate. Famous Changi Airport. Cars. Plane.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/928fa46d-dfd4-4ecc-ab87-cac60667d8ee.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/rabbit/LD9rbtjfvQ-xDgYSm5edM_e5eb464f693747718c46c56d99537974.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/kangaroo/99z3RU-fr5xwDKhgeaYnh_692a877561be46f4830e36f4d3a88466.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2191.651744616761, y: -1025.283551990772 },
          selected: false,
        },
        {
          id: 'upload-1763272549490',
          data: {
            type: 'upload',
            label: '#11 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/5bab48e1-250e-4375-b982-be1c6a99aa0b.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/5bab48e1-250e-4375-b982-be1c6a99aa0b.jpg',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 580, height: 388 },
          position: { x: -22.749210857126144, y: -1572.6774497830438 },
          selected: false,
        },
        {
          id: 'image-edit-1763272565677',
          data: {
            type: 'image-edit',
            label: '#10 Image to Image (Copy)',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/97465238-59db-41c0-9680-2bdeee641797.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1195.5238969647792, y: -1549.8636203775252 },
          selected: false,
        },
        {
          id: 'image-to-video-1763272640210',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video (Copy) (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Famous Jewel Vertex in Changi Airport. Train, Waterfall. People walking',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e483e0ff-966b-4d69-9f16-a33aee155e7d.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/tiger/_g1k_mitGUubLrkgZWuIl_814ba6419d4e4f6db4afcc66e5e2fb9c.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/kangaroo/bDEgNasyvazqPZHcNGdSP_fc6259f660ae4d9da20b8e9f20edc8aa.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2201.48872934939, y: -1629.4259245091384 },
          selected: false,
        },
        {
          id: 'upload-1763272826377',
          data: {
            type: 'upload',
            label: '#13 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b81becd1-6a87-4261-8a35-6c6b902116d2.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/b81becd1-6a87-4261-8a35-6c6b902116d2.jpg',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: -36.44179362222275, y: -2176.2323047214304 },
          selected: false,
        },
        {
          id: 'image-edit-1763272845750',
          data: {
            type: 'image-edit',
            label: '#14 Image to Image',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/fdaab28f-1c47-47eb-a916-e588e8ca2799.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1196.2280500308457, y: -2166.2323047214304 },
          selected: false,
        },
        {
          id: 'image-to-video-1763272882488',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video (Copy) (Copy) (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. The famous East Coast in Singapore. Ships, boats, plane, people on bicycles',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/cd7a8b48-0415-4ba7-acc9-d3ed47505868.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/lion/2vPH35DV3D2BPoAaHF68G_03041219b0b942b1a6bd91ecdecfd0ae.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/penguin/fC5ogaoajZ7q6LsFR9jzd_76fc5912fbbc4b0ca4b5e8c0af278a33.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2197.028970565404, y: -2172.1462461304905 },
          selected: false,
        },
        {
          id: 'upload-1763273643930',
          data: {
            type: 'upload',
            label: '#15 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/914ffd87-0698-4f25-9d62-331e1aec25c2.avif',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/914ffd87-0698-4f25-9d62-331e1aec25c2.avif',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 580, height: 388 },
          position: { x: 33.25161315585257, y: -2721.992667257948 },
          selected: false,
        },
        {
          id: 'image-edit-1763273651788',
          data: {
            type: 'image-edit',
            label: '#16 Image to Image',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/71b66765-40ab-47ad-a55a-993fa6269404.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1149.0548520144976, y: -2759.8094337611765 },
          selected: false,
        },
        {
          id: 'image-to-video-1763273782346',
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video (Copy) (Copy) (Copy) (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt: 'animate. The famous Lau Pasat food court in Singapore. ',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/bff6085f-4b7e-46e3-acf8-499eeb1c35ee.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/koala/wiI8a6tL3eJTjIRz6ALO6_e0a6e4d0f742461e9b84779836b7f6c2.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/zebra/aOBtwDWnY3y_bFtnb82gO_5a25410610d54c94a5e8b3c892e140c5.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2211.4242992428053, y: -2785.207628319022 },
          selected: false,
        },
        {
          id: 'upload-1763273819734',
          data: {
            type: 'upload',
            label: '#17 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/703b7667-d4e3-4f4a-8a81-57d9345a23e1.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/703b7667-d4e3-4f4a-8a81-57d9345a23e1.jpg',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 61.98032036272866, y: -3432.3109730310202 },
          selected: false,
        },
        {
          id: 'image-edit-1763273831900',
          data: {
            type: 'image-edit',
            label: '#18 Image to Image',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0742f264-5d10-43b4-8721-f7db7675677f.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1178.2539124419989, y: -3409.0373703188993 },
          selected: false,
        },
        {
          id: 'image-to-video-1763274188911',
          data: {
            type: 'image-to-video',
            label:
              '#7 Image to Video (Copy) (Copy) (Copy) (Copy) (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. The famous port in Singapore. cranes, ships, boats.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/32026103-2e47-4d46-9720-946426491d64.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/koala/25LyDXgpF8fLumLk_vRCY_786d4e0da3cf40fab39199ee148b1684.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/tiger/aprIglRqMS9ecrCwqLavs_33b74bdf54374872b5907191c16cefd8.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2231.5653244172295, y: -3427.038378252525 },
          selected: false,
        },
        {
          id: 'upload-1763274226355',
          data: {
            type: 'upload',
            label: '#19 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/86e13ce1-9ef4-4da8-992a-a36c38a29279.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/86e13ce1-9ef4-4da8-992a-a36c38a29279.jpg',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 280, height: 388 },
          position: { x: 242.8835152421002, y: -4094.7206066137 },
          selected: false,
        },
        {
          id: 'image-edit-1763274260813',
          data: {
            type: 'image-edit',
            label: '#20 Image to Image',
            config: {
              useHD: false,
              prompt: '@#4 Input. Expand add pedestrians',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e1af9c59-0be8-481f-a08a-6a8c48b69615.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1183.5522045573518, y: -4153.380683895308 },
          selected: false,
        },
        {
          id: 'image-to-video-1763274360236',
          data: {
            type: 'image-to-video',
            label:
              '#7 Image to Video (Copy) (Copy) (Copy) (Copy) (Copy) (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Singapore central bussiness district. People walking, bus going in front, cars. Singapore has left side cars movement. ',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ca060fea-c71e-4b5b-a1dc-6ff851d1a9c9.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/penguin/c83J4oA3MjXBJUNV1F6h1_9f02500d21b147ac9b276a0d05007915.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/tiger/KaXbhse1eK0X7CKyHHqca_dc2296da5936467491248edd2452d314.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2239.320105327706, y: -4177.125925597258 },
          selected: false,
        },
        {
          id: 'upload-1763275554503',
          data: {
            type: 'upload',
            label: '#22 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/bfb4526a-bb47-4794-ab8d-602f0a06bef7.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/bfb4526a-bb47-4794-ab8d-602f0a06bef7.jpg',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 639, height: 388 },
          position: { x: 177.50314563113125, y: -4780.83685156427 },
          selected: false,
        },
        {
          id: 'image-edit-1763275563188',
          data: {
            type: 'image-edit',
            label: '#20 Image to Image (Copy)',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/350c9961-f2e3-4304-8fa6-8602c266429a.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1174.3106546339584, y: -4798.500070639054 },
          selected: false,
        },
        {
          id: 'image-to-video-1763275585193',
          data: {
            type: 'image-to-video',
            label:
              '#7 Image to Video (Copy) (Copy) (Copy) (Copy) (Copy) (Copy) (Copy) (Copy)',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'animate. Singapore multiracial people with Singapore flags, Happy.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4fd29148-f0a3-4cf7-aba6-0b2c574e67c4.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/lion/IQpH6jbjwRcXXPxcwu-DF_c5e0d1f9effb464b8902b0c2fe4f19f2.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/kangaroo/MyTS-ToURXMkqHAsDd7zc_73caae8716f44f22a95438842dae4195.jpg',
            },
            status: 'success',
          },
          type: 'image-to-video',
          dragging: false,
          measured: { width: 715, height: 404 },
          position: { x: 2247.7272889864366, y: -4782.23883735808 },
          selected: false,
        },
        {
          id: 'upload-1763281006106',
          data: {
            type: 'upload',
            label: '#24 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/8f4176e1-f8fd-4c6f-ab77-c449ca66e94a.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/8f4176e1-f8fd-4c6f-ab77-c449ca66e94a.png',
          },
          type: 'upload',
          dragging: false,
          measured: { width: 388, height: 388 },
          position: { x: 232.80158490445194, y: -5962.73312283233 },
          selected: false,
        },
        {
          id: 'image-edit-1763281032310',
          data: {
            type: 'image-edit',
            label: '#25 Image to Image',
            config: {
              useHD: false,
              prompt: '@#4 Input',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ec4e358f-80fa-4473-8f52-9131f36b4982.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1346.2915797053406, y: -5606.836241330804 },
          selected: false,
        },
        {
          id: 'image-edit-1763281660397',
          data: {
            type: 'image-edit',
            label: '#26 Image to Image',
            config: {
              useHD: false,
              prompt: 'remove Singapore word',
              editModel: 'Seedream 4.0',
              dimensions: '16:9',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/9bbf8a6d-a896-4623-a5ee-c4b930905182.png',
            status: 'success',
          },
          type: 'image-edit',
          dragging: false,
          measured: { width: 687, height: 388 },
          position: { x: 1323.7591928782479, y: -6482.152325337199 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1763282188615',
          data: {
            type: 'first-last-frame-video',
            label: '#29 First-Last Frame to Video',
            config: {
              model: 'Veo 3.1 Fast First-Last Frame',
              prompt:
                'The word Singapore is being knitted in fabric Style gradually. Starting from letter S, then I, N, G, A, P, O, R, E.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1ff46f83-ef63-40b8-9f97-9ec340db9dc2.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/monkey/sWWdxVZmv9PFWexvgKS4H_4c117c5ecc0f4d85aa043bc92c3c9df4.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/tiger/hpRN_-WvIZfJ9O1oXiGoN_c9819dfaac934b63af4f613d4486a10a.jpg',
            },
            status: 'success',
          },
          type: 'first-last-frame-video',
          dragging: false,
          measured: { width: 404, height: 636 },
          position: { x: 3170.2685154533583, y: -6111.227976970715 },
          selected: false,
        },
      ],
    },
  },
  {
    id: 'virtual-try-on',
    name: 'Virtual Try-On',
    backgroundImage: 'https://media.easy-peasy.ai/assets/try-on.jpeg',
    description:
      'Generate a model image, dress them with uploaded clothing items using AI image editing, and animate the result into a video with AI-generated motion.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-1763876102643',
          type: 'note',
          position: { x: 506.0678219592229, y: 547.3569432375308 },
          data: {
            type: 'note',
            label: 'Virtual Try-On',
            config: {
              note: '<h1>Virtual Try-On</h1><p><strong>Generate a model and dress them with clothing items.</strong></p><p>1. Generate a base model using <strong>Text to Image</strong> node.</p><p>2. Upload clothing items using <strong>Upload Media</strong> nodes (cardigan, pants, shoes, etc.).</p><p>3. Connect all images to the <strong>Image to Image</strong> node.</p><p>4. Use a prompt to dress the model with the uploaded clothes.</p><p>5. Click <strong>Run</strong> to see the virtual try-on result.</p>',
            },
          },
          measured: { width: 400, height: 288 },
        },
        {
          id: 'image-generation-1763874564000',
          type: 'image-generation',
          position: { x: 1031.551662462894, y: 829.6258880033098 },
          data: {
            type: 'image-generation',
            label: '#1 Generate Model',
            config: {
              model: 'Nano Banana Pro',
              prompt:
                'hyper-realistic full body shot of a young beautiful woman with fair skin, long voluminous wavy blonde hair parted in the middle. Natural look. Her body is visible from shoes to head',
              outputs: 1,
              dimensions: '9:16',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/61d88610-4acc-4796-bb9a-a0fa778489e4.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 339, height: 604 },
          selected: false,
        },
        {
          id: 'upload-1763875112168',
          type: 'upload',
          position: { x: 1032.9259345678352, y: 228.45733917252187 },
          data: {
            type: 'upload',
            label: '#2 Upload - Cardigan',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/45dca3db-a4d0-4ed1-af7e-85cd72a818e5.avif',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/45dca3db-a4d0-4ed1-af7e-85cd72a818e5.avif',
          },
          dragging: false,
          measured: { width: 311, height: 388 },
          selected: false,
        },
        {
          id: 'upload-1763875121425',
          type: 'upload',
          position: { x: 1517.2626911146285, y: 239.45250103956465 },
          data: {
            type: 'upload',
            label: '#3 Upload - Pants',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/fe2c4f77-afad-4e94-a2d6-f665226e0f15.avif',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/fe2c4f77-afad-4e94-a2d6-f665226e0f15.avif',
          },
          dragging: false,
          measured: { width: 311, height: 388 },
          selected: false,
        },
        {
          id: 'upload-1763875461219',
          type: 'upload',
          position: { x: 1972.8847787534235, y: 255.33886275995724 },
          data: {
            type: 'upload',
            label: '#4 Upload - Slingbacks',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/d49f9bad-7c6b-434b-979e-88c700400e52.avif',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/d49f9bad-7c6b-434b-979e-88c700400e52.avif',
          },
          dragging: false,
          measured: { width: 311, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1763875501740',
          type: 'image-edit',
          position: { x: 1486.5097543830832, y: 840.852789609101 },
          data: {
            type: 'image-edit',
            label: '#5 Dress Model',
            config: {
              useHD: false,
              prompt:
                'Dress the woman with the clothes from the attached images',
              editModel: 'Nano Banana Pro',
              dimensions: '9:16',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a2c9c303-5b60-418e-9fdb-d5f2ff99b1aa.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 339, height: 604 },
          selected: false,
        },
        {
          id: 'image-to-video-1763876819783',
          type: 'image-to-video',
          position: { x: 1955.6929545491935, y: 843.3956318709785 },
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video',
            config: {
              model: 'Veo 3.1 Image',
              prompt: '@#8 Text Generation',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8878cc56-380a-44c5-b7c2-accbcdae3a86.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/penguin/5oCMKy_fRaT7rl-5AqII6_7807252d4b5046e5b1af27d28f5a7f11.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/kangaroo/YYMnsQ3S0lTJefxm2zaya_91165e1c76334e37b256f8847b6c2109.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 342, height: 604 },
          selected: false,
        },
        {
          id: 'text-generation-1763876876859',
          type: 'text-generation',
          position: { x: 1715.516989388251, y: 1627.4335816978883 },
          data: {
            type: 'text-generation',
            label: '#8 Text Generation',
            config: {
              model: 'claude-sonnet-4-5',
              prompt:
                'Please analyze this image and create the prompt for video generation. The video duration 8 seconds. She walks gracefully towards the camera and saying that now you can dress your model  with any clothing items.',
            },
            result:
              '# Video Generation Prompt\n\n**Duration:** 8 seconds\n\n**Scene Description:**\nA young woman with long, wavy blonde hair stands on a natural forest path surrounded by tall trees and dappled sunlight. She is wearing a vibrant red cardigan with gold buttons, black leather pants, and black patent leather flats. The setting is a serene outdoor woodland environment with soft natural lighting filtering through the trees.\n\n**Action & Movement:**\nThe woman walks gracefully and confidently toward the camera along the forest path, maintaining eye contact with a warm, friendly expression. Her movement is smooth and natural, with a slight gentle sway characteristic of a casual walking pace.\n\n**Audio/Dialogue:**\nAs she walks, she speaks directly to the camera saying: "Now you can dress your model with any clothing items."\n\n**Camera Work:**\n- Start: Full body shot, woman positioned in mid-distance on the path\n- Movement: Slow forward tracking shot or static camera as subject approaches\n- End: Medium-to-close shot as she reaches the foreground\n- Camera angle: Straight on, eye-level perspective\n- Maintain focus on subject throughout with soft bokeh background\n\n**Mood & Tone:**\nProfessional yet approachable, promotional/tutorial style, natural and organic aesthetic with a contemporary fashion demonstration feel.\n\n**Technical Details:**\n- Natural outdoor lighting (golden hour or midday forest filtered light)\n- Shallow depth of field to keep focus on subject\n- Smooth, stabilized camera movement\n- Clear audio for dialogue delivery',
            status: 'success',
          },
          dragging: false,
          measured: { width: 404, height: 1012 },
          selected: false,
        },
      ],
      edges: [
        {
          id: 'xy-edge__image-generation-1763874564000image-image-edit-1763875501740image',
          source: 'image-generation-1763874564000',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763875112168image-image-edit-1763875501740image',
          source: 'upload-1763875112168',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763875121425image-image-edit-1763875501740image',
          source: 'upload-1763875121425',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763875461219image-image-edit-1763875501740image',
          source: 'upload-1763875461219',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763875501740image-image-to-video-1763876819783image',
          source: 'image-edit-1763875501740',
          target: 'image-to-video-1763876819783',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763875501740image-text-generation-1763876876859image',
          source: 'image-edit-1763875501740',
          target: 'text-generation-1763876876859',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__text-generation-1763876876859text-image-to-video-1763876819783prompt',
          source: 'text-generation-1763876876859',
          target: 'image-to-video-1763876819783',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
      ],
    },
  },
  {
    id: 'virtual-try-on-hijab-abaya',
    name: 'Virtual Try-On: Hijab & Abaya',
    backgroundImage:
      'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/ddbb208f-e00b-4b6e-8776-15c94de02160.png',
    description:
      'Generate a Malay woman model, dress them with hijab and abaya clothing items using AI image editing, and animate the result into a promotional video.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-1763876102643',
          type: 'note',
          position: { x: 506.0678219592229, y: 547.3569432375308 },
          data: {
            type: 'note',
            label: 'Virtual Try-On',
            config: {
              note: '<h1>Virtual Try-On: hijab and abaya set</h1><p><strong>Generate a model and dress them with clothing items.</strong></p><p>1. Generate a base model using <strong>Text to Image</strong> node.</p><p>2. Upload clothing items using <strong>Upload Media</strong> nodes (cardigan, pants, shoes, etc.).</p><p>3. Connect all images to the <strong>Image to Image</strong> node.</p><p>4. Use a prompt to dress the model with the uploaded clothes.</p><p>5. Click <strong>Run</strong> to see the virtual try-on result.</p>',
            },
          },
          measured: { width: 400, height: 324 },
          selected: false,
        },
        {
          id: 'image-generation-1763874564000',
          type: 'image-generation',
          position: { x: 1193.3223097976079, y: 845.6899260739199 },
          data: {
            type: 'image-generation',
            label: '#1 Generate Model',
            config: {
              model: 'Nano Banana Pro',
              prompt:
                'hyper-realistic full body shot of a young beautiful Malay woman in SG. Natural look. Her body is visible from shoes to head',
              outputs: 1,
              dimensions: '9:16',
            },
            result:
              'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/b7b8427c-b366-47e8-b9e9-06512d002f99.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 424, height: 756 },
          selected: false,
        },
        {
          id: 'upload-1763875112168',
          type: 'upload',
          position: { x: 1032.9259345678352, y: 228.45733917252187 },
          data: {
            type: 'upload',
            label: '#2 Upload - Cardigan',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/6fbfe109-5a88-4673-b102-e6650934f2a2.webp',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/6fbfe109-5a88-4673-b102-e6650934f2a2.webp',
          },
          dragging: false,
          measured: { width: 704, height: 393 },
          selected: false,
        },
        {
          id: 'upload-1763875121425',
          type: 'upload',
          position: { x: 1853.9410571516814, y: 210.6852291434315 },
          data: {
            type: 'upload',
            label: '#3 Upload - Pants',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/2db3a53e-b111-49d9-860b-444c11413ddf.webp',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/2db3a53e-b111-49d9-860b-444c11413ddf.webp',
          },
          dragging: false,
          measured: { width: 424, height: 564 },
          selected: false,
        },
        {
          id: 'upload-1763875461219',
          type: 'upload',
          position: { x: 2366.7385838210166, y: 242.45579436989573 },
          data: {
            type: 'upload',
            label: '#4 Upload - Slingbacks',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/2bcf6adb-6bd9-4df6-8386-5c8090f50c54.webp',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/2bcf6adb-6bd9-4df6-8386-5c8090f50c54.webp',
          },
          dragging: false,
          measured: { width: 424, height: 424 },
          selected: false,
        },
        {
          id: 'image-edit-1763875501740',
          type: 'image-edit',
          position: { x: 1803.1910064972133, y: 845.5751945567238 },
          data: {
            type: 'image-edit',
            label: '#5 Dress Model',
            config: {
              useHD: false,
              prompt:
                'Dress the woman with the clothes from the attached images',
              editModel: 'Nano Banana Pro',
              dimensions: '9:16',
            },
            result:
              'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/ddbb208f-e00b-4b6e-8776-15c94de02160.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 424, height: 756 },
          selected: false,
        },
        {
          id: 'image-to-video-1763876819783',
          type: 'image-to-video',
          position: { x: 2427.8908816896796, y: 849.3981478939507 },
          data: {
            type: 'image-to-video',
            label: '#7 Image to Video',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'Please analyze this image and create the prompt for video generation. The video duration 8 seconds. She walks gracefully towards the camera and saying "Hi guys! Now you can dress your model with any clothing items."',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/19157fa6-85f5-48b5-879d-6df77bca97ee.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a8a0c3c/EcciNBTlsmT3feODHpltg_74474e3baa2940e7be5322236b34d0db.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a8a0c3c/6RpRSyZKlL_fnNeSSBCWE_131752f1cb53470e9039f8ea7bbbb287.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 424, height: 751 },
          selected: false,
        },
      ],
      edges: [
        {
          id: 'xy-edge__image-generation-1763874564000image-image-edit-1763875501740image',
          source: 'image-generation-1763874564000',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763875112168image-image-edit-1763875501740image',
          source: 'upload-1763875112168',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763875121425image-image-edit-1763875501740image',
          source: 'upload-1763875121425',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763875461219image-image-edit-1763875501740image',
          source: 'upload-1763875461219',
          target: 'image-edit-1763875501740',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763875501740image-image-to-video-1763876819783image',
          source: 'image-edit-1763875501740',
          target: 'image-to-video-1763876819783',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'interior-design-visualization',
    name: 'Interior Design Visualization',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/0d6ab58a-9793-4526-9186-edc1cf0bb2b6.mp4',
    description:
      'Transform a 2D floor plan into a stunning 3D isometric maquette, then generate photorealistic room renders and create an animated video tour of the entire property.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'note-1763881231198',
          type: 'note',
          position: { x: 1365.3624259678472, y: -57.79751422122588 },
          data: {
            type: 'note',
            label: 'Interior Design Visualization',
            config: {
              note: '<h1>Interior Design Visualization</h1><p><strong>Transform a floor plan into photorealistic interior renders and a video tour.</strong></p><p>1. Upload a 2D floor plan using <strong>Upload Media</strong>.</p><p>2. Convert it to a 3D isometric maquette using <strong>Image to Image</strong> with the provided prompt.</p><p>3. Generate eye-level photorealistic renders for each room (living room, kitchen, bedrooms, patio).</p><p>4. Animate each room with <strong>Image to Video</strong> for cinematic camera movements.</p><p>5. Merge all videos into a complete property tour using <strong>Merge Videos</strong>.</p>',
            },
          },
          dragging: false,
          measured: { width: 400, height: 100 },
        },
        {
          id: 'upload-1763878676953',
          type: 'upload',
          position: { x: 861.9390187712868, y: 364.4744564678687 },
          data: {
            type: 'upload',
            label: '#1 Upload Floor Plan',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/42047260-2244-4a68-a979-12729c6a6c79.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/42047260-2244-4a68-a979-12729c6a6c79.jpg',
          },
          dragging: false,
          measured: { width: 561, height: 388 },
          selected: false,
        },
        {
          id: 'input-1763878698839',
          type: 'input',
          position: { x: 1388.7769474335255, y: 1013.2687914845983 },
          data: {
            type: 'input',
            label: '#2 Maquette Prompt',
            config: {
              value:
                'Top-down, fully 3D isometric render of the entire floor plan. \n\nCreate a clean, highly detailed miniature architectural maquette with accurate room proportions and layout, matching the reference exactly. Remove all text labels.\n\nKeep all walls, doors, furnitures, appliances or rooms properly places \n\nUse colorful, modern furniture and decorative objects in each room—sofas, beds, tables, cabinets, lamps, plants, rugs, appliances—arranged realistically and in scale.\n\nHigh-resolution, photorealistic materials (wood floors, tiles, fabrics, glass). Soft global lighting, subtle shadows, crisp edges. Professional, polished, high-quality output.',
            },
            result:
              'Top-down, fully 3D isometric render of the entire floor plan. \n\nCreate a clean, highly detailed miniature architectural maquette with accurate room proportions and layout, matching the reference exactly. Remove all text labels.\n\nKeep all walls, doors, furnitures, appliances or rooms properly places \n\nUse colorful, modern furniture and decorative objects in each room—sofas, beds, tables, cabinets, lamps, plants, rugs, appliances—arranged realistically and in scale.\n\nHigh-resolution, photorealistic materials (wood floors, tiles, fabrics, glass). Soft global lighting, subtle shadows, crisp edges. Professional, polished, high-quality output.',
            status: 'success',
          },
          dragging: false,
          measured: { width: 150, height: 462 },
          selected: false,
        },
        {
          id: 'image-edit-1763878692330',
          type: 'image-edit',
          position: { x: 1731.8222462085698, y: 362.47273514213066 },
          data: {
            type: 'image-edit',
            label: '#3 Create 3D Maquette',
            config: {
              useHD: false,
              prompt: '@#2 Maquette Prompt',
              editModel: 'Nano Banana Pro',
              dimensions: '5:4',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/26c16a04-cbd1-454f-ab8d-0e244a09f4a4.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 481, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1763880571993',
          type: 'image-edit',
          position: { x: 2634.423673736813, y: -562.5848189758239 },
          data: {
            type: 'image-edit',
            label: '#4 Render Bedroom 2',
            config: {
              useHD: false,
              prompt:
                "Use the maquette image as the precise visual reference for the rooms' furniture, colors, materials, décor, and layout. Capture bedroom 2 (not master bedroom) from eye-level camera angles, with the depth, softness, and atmosphere of real-estate interior photography. The photograph accurately translates the maquette's design into real-life materials and fabrics, keeping the exact same spatial arrangement of walls, doors, windows or furniture, same shapes, the same color palette. The final result is a high-end interior photography brochure, polished, cohesive, and visually sophisticated.",
              editModel: 'Nano Banana Pro',
              dimensions: '5:4',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0784cee5-65fc-4209-8b24-21f16937d625.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 481, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1763880321925',
          type: 'image-edit',
          position: { x: 2629.5811812748857, y: -79.47478407472053 },
          data: {
            type: 'image-edit',
            label: '#5 Render Living Room',
            config: {
              useHD: false,
              prompt:
                "Use the maquette image as the precise visual reference for the rooms' furniture, colors, materials, décor, and layout.\n\nCapture living room from eye-level camera angles, with the depth, softness, and atmosphere of real-estate interior photography.\n\nThe photograph accurately translates the maquette's design into real-life materials and fabrics, keeping the exact same spatial arrangement of walls, doors, windows or furniture, same shapes, the same color palette.\n\nThe final result is a high-end interior photography brochure, polished, cohesive, and visually sophisticated.",
              editModel: 'Nano Banana Pro',
              dimensions: '5:4',
              resolution: '4K',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/images/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/97171247-7e80-4e5f-b7d7-fede73861f96.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 481, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1763880421885',
          type: 'image-edit',
          position: { x: 2634.0286151912997, y: 385.9392718915068 },
          data: {
            type: 'image-edit',
            label: '#6 Render Kitchen',
            config: {
              useHD: false,
              prompt:
                "Use the maquette image as the precise visual reference for the rooms' furniture, colors, materials, décor, and layout. Capture kitchen from eye-level camera angles, with the depth, softness, and atmosphere of real-estate interior photography. The photograph accurately translates the maquette's design into real-life materials and fabrics, keeping the exact same spatial arrangement of walls, doors, windows or furniture, same shapes, the same color palette. The final result is a high-end interior photography brochure, polished, cohesive, and visually sophisticated.",
              editModel: 'Nano Banana Pro',
              dimensions: '5:4',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/123fb2a2-6e3c-4ce0-8ab3-8c9990a95f2d.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 481, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1763880446570',
          type: 'image-edit',
          position: { x: 2640.09799003246, y: 870.5154898676997 },
          data: {
            type: 'image-edit',
            label: '#7 Render Master Bedroom',
            config: {
              useHD: false,
              prompt:
                "Use the maquette image as the precise visual reference for the room furniture, colors, materials, décor, and layout. Capture master bedroom from eye-level camera angles, with the depth, softness, and atmosphere of real-estate interior photography. The photograph accurately translates the maquette's design into real-life materials and fabrics, keeping the exact same spatial arrangement of walls, doors, windows or furniture, same shapes, the same color palette. The final result is a high-end interior photography brochure, polished, cohesive, and visually sophisticated.",
              editModel: 'Nano Banana Pro',
              dimensions: '5:4',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/82ce36d7-e75d-4bb1-901c-4c9eeb234182.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 481, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1763880499154',
          type: 'image-edit',
          position: { x: 2637.949676521407, y: 1375.1206408971564 },
          data: {
            type: 'image-edit',
            label: '#8 Render Patio',
            config: {
              useHD: false,
              prompt:
                "Use the maquette image as the precise visual reference for the room furniture, colors, materials, décor, and layout. Capture patio from eye-level camera angles, with the depth, softness, and atmosphere of real-estate interior photography. The photograph accurately translates the maquette's design into real-life materials and fabrics, keeping the exact same spatial arrangement of walls, doors, windows or furniture, same shapes, the same color palette. The final result is a high-end interior photography brochure, polished, cohesive, and visually sophisticated.",
              editModel: 'Nano Banana Pro',
              dimensions: '5:4',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/66a70d5a-ed3a-4ac5-873e-88c420d20f05.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 481, height: 388 },
          selected: false,
        },
        {
          id: 'image-to-video-1763881922831',
          type: 'image-to-video',
          position: { x: 3696.900243128417, y: -547.00942877025 },
          data: {
            type: 'image-to-video',
            label: '#9 Animate Bedroom 2',
            config: {
              model: 'Veo 3.1 Image',
              prompt: 'Smooth forward dolly shot into the bedroom',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/89f9d298-5a2e-493b-8483-f5552ef0bfab.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/elephant/rkeu8OjWeT08k_AHO33Ga_6edddf46e1c24c57b26fa135f4fadcdf.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/elephant/8G2KNpEuteHmumETLnTJ3_cf5672b0865c4d6aa21600c960afa61d.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'image-to-video-1763881348466',
          type: 'image-to-video',
          position: { x: 3695.8466021944037, y: -70.5551576090094 },
          data: {
            type: 'image-to-video',
            label: '#10 Animate Living Room',
            config: {
              model: 'Veo 3.1 Image',
              prompt: 'Slow pan across the living room',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0d6ab58a-9793-4526-9186-edc1cf0bb2b6.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/penguin/1MO6Lrj-X5TaFy2sqYhix_6ebdcf9f3a594466b9e37ab785aa4329.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/rabbit/XxmGRyGBSsduuVw8sqrlI_c30659e33c6a41b0b4f8dd2a15c6ea7b.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'image-to-video-1763881830824',
          type: 'image-to-video',
          position: { x: 3706.9321436213067, y: 401.81612879387006 },
          data: {
            type: 'image-to-video',
            label: '#11 Animate Kitchen',
            config: {
              model: 'Veo 3.1 Image',
              prompt: 'Smooth  fly-over',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8ab81e5d-d0e0-4a68-af69-f8fa2cac22d3.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/panda/wnFqLAdaY6ceeLWhyrloT_67e07c604dbd4439b494196b8c333b1c.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/tiger/9sKZcEcLpFYmWEcf_JGmc_480665a816a64d60bac86d0a2c8fcf94.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'image-to-video-1763881891814',
          type: 'image-to-video',
          position: { x: 3709.0127241343707, y: 908.1372508596726 },
          data: {
            type: 'image-to-video',
            label: '#12 Animate Master Bedroom',
            config: {
              model: 'Veo 3.1 Image',
              prompt: 'Slow side-to-side pan across the bedroom',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/95f2a731-279a-4363-ab92-9b5753cecc5a.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/zebra/_EtNm4qrNDTRQ4emcIy7n_0eae2f18539d43fe9d4ee2e27960c99f.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/panda/oJKfx66KU79fwzVgxJsB2_b17c92955b5e4a5d808d711f1883ef17.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'video-merge-1763882036837',
          type: 'video-merge',
          position: { x: 4854.29693852302, y: 280.66123747387974 },
          data: {
            type: 'video-merge',
            label: '#13 Merge Property Tour',
            config: {},
            result: {
              raw: {
                raw: {
                  video: {
                    url: 'https://v3b.fal.media/files/b/elephant/FFl_8SbWJb7q13mISBCVR_merged_video.mp4',
                    file_name: 'merged_video.mp4',
                    file_size: 26226571,
                    content_type: 'video/mp4',
                  },
                  metadata: {
                    final_fps: 24,
                    has_audio: true,
                    final_width: 1920,
                    video_count: 4,
                    final_height: 1080,
                    input_videos: [
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0d6ab58a-9793-4526-9186-edc1cf0bb2b6.mp4',
                        index: 1,
                        width: 1920,
                        height: 1080,
                        duration: 8,
                        has_audio: true,
                      },
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8ab81e5d-d0e0-4a68-af69-f8fa2cac22d3.mp4',
                        index: 2,
                        width: 1920,
                        height: 1080,
                        duration: 8,
                        has_audio: true,
                      },
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/89f9d298-5a2e-493b-8483-f5552ef0bfab.mp4',
                        index: 3,
                        width: 1920,
                        height: 1080,
                        duration: 8,
                        has_audio: true,
                      },
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/95f2a731-279a-4363-ab92-9b5753cecc5a.mp4',
                        index: 4,
                        width: 1920,
                        height: 1080,
                        duration: 8,
                        has_audio: true,
                      },
                    ],
                    total_duration: 32,
                  },
                },
                videoUrl:
                  'https://v3b.fal.media/files/b/elephant/FFl_8SbWJb7q13mISBCVR_merged_video.mp4',
              },
              videoUrl:
                'https://v3b.fal.media/files/b/elephant/FFl_8SbWJb7q13mISBCVR_merged_video.mp4',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 573, height: 324 },
          selected: false,
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1763878676953image-image-edit-1763878692330image',
          source: 'upload-1763878676953',
          target: 'image-edit-1763878692330',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1763878698839text-image-edit-1763878692330prompt',
          source: 'input-1763878698839',
          target: 'image-edit-1763878692330',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__image-edit-1763878692330image-image-edit-1763880321925image',
          source: 'image-edit-1763878692330',
          target: 'image-edit-1763880321925',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763878692330image-image-edit-1763880421885image',
          source: 'image-edit-1763878692330',
          target: 'image-edit-1763880421885',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763878692330image-image-edit-1763880446570image',
          source: 'image-edit-1763878692330',
          target: 'image-edit-1763880446570',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763878692330image-image-edit-1763880499154image',
          source: 'image-edit-1763878692330',
          target: 'image-edit-1763880499154',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763878692330image-image-edit-1763880571993image',
          source: 'image-edit-1763878692330',
          target: 'image-edit-1763880571993',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763878676953image-image-edit-1763880499154image',
          source: 'upload-1763878676953',
          target: 'image-edit-1763880499154',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1763878676953image-image-edit-1763880446570image',
          source: 'upload-1763878676953',
          target: 'image-edit-1763880446570',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763880321925image-image-to-video-1763881348466image',
          source: 'image-edit-1763880321925',
          target: 'image-to-video-1763881348466',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763880421885image-image-to-video-1763881830824image',
          source: 'image-edit-1763880421885',
          target: 'image-to-video-1763881830824',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763880446570image-image-to-video-1763881891814image',
          source: 'image-edit-1763880446570',
          target: 'image-to-video-1763881891814',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1763880571993image-image-to-video-1763881922831image',
          source: 'image-edit-1763880571993',
          target: 'image-to-video-1763881922831',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-to-video-1763881348466video-video-merge-1763882036837video',
          source: 'image-to-video-1763881348466',
          target: 'video-merge-1763882036837',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
        {
          id: 'xy-edge__image-to-video-1763881830824video-video-merge-1763882036837video',
          source: 'image-to-video-1763881830824',
          target: 'video-merge-1763882036837',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
        {
          id: 'xy-edge__image-to-video-1763881922831video-video-merge-1763882036837video',
          source: 'image-to-video-1763881922831',
          target: 'video-merge-1763882036837',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
        {
          id: 'xy-edge__image-to-video-1763881891814video-video-merge-1763882036837video',
          source: 'image-to-video-1763881891814',
          target: 'video-merge-1763882036837',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
      ],
    },
  },
  {
    id: 'fashion-product-video-multi-scene',
    name: 'Fashion Product Video - Multi-Scene Showcase',
    backgroundImage:
      'https://media.easy-peasy.ai/assets/809bea1d-cd33-4de8-bb19-cd1c406ada4f.mp4',
    description:
      'Create a stunning fashion product showcase video by combining a model reference with a product image, generating multiple scene variations, animating each scene, and merging them into a polished multi-scene commercial.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'note-workflow-intro',
          type: 'note',
          position: { x: 171.88367946347296, y: 1411.6894854065745 },
          data: {
            type: 'note',
            label: 'Fashion Product Video',
            config: {
              note: '<h1>Fashion Product Video - Multi-Scene Showcase</h1><p><strong>Create a professional multi-scene product video combining a model with your product.</strong></p><p></p><h3>How This Workflow Works:</h3><p>1. <strong>Select Model Reference</strong> - Choose a base model image from your gallery that will be used as the reference for all scenes.</p><p>2. <strong>Upload Product Image</strong> - Upload your product (e.g., a clutch bag, accessory, or clothing item) that you want to showcase.</p><p>3. <strong>Generate Scene Variations</strong> - Three Image-to-Image nodes create different scenes:</p><ul><li><p>Scene 1: Model holding the product in a glamorous setting</p></li><li><p>Scene 2: Model at a bar/lounge with the product</p></li><li><p>Scene 3: Model posing elegantly near a curtain</p></li></ul><p>4. <strong>Animate Each Scene</strong> - Each generated image is animated into a short fashion-editorial style video clip.</p><p>5. <strong>Merge Videos</strong> - All clips are combined into one seamless multi-scene product showcase video.</p><p></p><h3>Tips:</h3><ul><li><p>Use high-quality model and product reference images for best results</p></li><li><p>Adjust prompts in Image-to-Image nodes to match your product type</p></li><li><p>Modify video prompts to control camera movement and animation style</p></li></ul><p></p>',
            },
          },
          measured: { width: 400, height: 884 },
        },
        {
          id: 'select-image-model',
          type: 'select-image',
          position: { x: 814.41, y: 1423.74 },
          data: {
            type: 'select-image',
            label: '#1 Select Model Reference',
            config: {
              selectedImageId: 3265494,
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/4b6ed15d-f21d-4352-b7cc-99305d8fc14b.png',
            },
            result:
              'https://media.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/4b6ed15d-f21d-4352-b7cc-99305d8fc14b.png',
          },
          measured: { width: 280, height: 388 },
        },
        {
          id: 'upload-product',
          type: 'upload',
          position: { x: 764.38, y: 1926.53 },
          data: {
            type: 'upload',
            label: '#2 Upload Product Image',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/bbe50dd8-fa4f-42bb-ba45-9eacfbc5f21c.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/bbe50dd8-fa4f-42bb-ba45-9eacfbc5f21c.png',
          },
          measured: { width: 388, height: 388 },
        },
        {
          id: 'image-edit-scene1',
          type: 'image-edit',
          position: { x: 1637.98, y: 725.53 },
          data: {
            type: 'image-edit',
            label: '#3 Scene 1 - Glamorous Entrance',
            config: {
              useHD: false,
              prompt:
                'Using the provided model and outfit reference, and the product reference, create a photorealistic, high-fashion image of the model holding the product elegantly.',
              editModel: 'Nano Banana Pro',
              dimensions: '9:16',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/d0e1df15-4d75-4427-a6c5-a456a2d4cdbd.png',
            status: 'success',
          },
          measured: { width: 339, height: 604 },
        },
        {
          id: 'image-edit-scene2',
          type: 'image-edit',
          position: { x: 1626.8, y: 1565.24 },
          data: {
            type: 'image-edit',
            label: '#4 Scene 2 - Bar Lounge',
            config: {
              useHD: false,
              prompt:
                'Using the provided model and outfit reference, and the product reference, create a photorealistic, high-fashion image of the model sitting in a stylish bar or lounge setting.',
              editModel: 'Nano Banana Pro',
              dimensions: '9:16',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/af761ad7-2de3-4b52-af3d-11edab0a750f.png',
            status: 'success',
          },
          measured: { width: 339, height: 604 },
        },
        {
          id: 'image-edit-scene3',
          type: 'image-edit',
          position: { x: 1612.49, y: 2337.74 },
          data: {
            type: 'image-edit',
            label: '#5 Scene 3 - Elegant Pose',
            config: {
              useHD: false,
              prompt:
                'Using the provided model and outfit reference, and the product reference, create a photorealistic, high-fashion image of the model standing elegantly near a curtain. She poses confidently for the camera, holding the product naturally. Keep her posture poised, body slightly angled to the camera, with a calm, confident expression. The final image should feel luxury-fashion editorial, stylish, and photorealistic.',
              editModel: 'Nano Banana Pro',
              dimensions: '9:16',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/575f74c2-038f-4b8b-b542-37d7187d4912.png',
            status: 'success',
          },
          measured: { width: 339, height: 604 },
        },
        {
          id: 'image-to-video-scene1',
          type: 'image-to-video',
          position: { x: 2140.86, y: 737.98 },
          data: {
            type: 'image-to-video',
            label: '#6 Animate Scene 1',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'Animate the model in a smooth, realistic motion sequence. She stands with calm confidence, makes eye contact with the camera for a moment, then begins walking forward with a poised, self-assured gait. Her movement remains elegant and unhurried, like a vogue-style luxury commercial — composed, graceful, and quietly powerful. Her posture remains relaxed, with subtle natural breathing and fluid arm motion. The camera captures this in a sophisticated, editorial manner, making the moment feel real, stylish, and aspirational.',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/c192cf2f-f70f-432a-8e8d-60535f0e70e1.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/koala/3ukO9q93vkkekaVrPLSx6_9bdec93c62be402dbd14b7ad899db70c.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/zebra/qY9By7kvF6684GSsWm-H4_230dc53970f74c7c91a8471a4fcf6d52.jpg',
            },
            status: 'success',
          },
          measured: { width: 342, height: 604 },
        },
        {
          id: 'image-to-video-scene2',
          type: 'image-to-video',
          position: { x: 2133.64, y: 1572.36 },
          data: {
            type: 'image-to-video',
            label: '#7 Animate Scene 2',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'Animate the model seated at the bar in side-view, not looking at the camera. She calmly takes a sip of her drink in a fancy glass. Her expression stays calm and confident, and the movement is subtle, poised, and fashion-editorial. The bartender remains softly blurred in the background, realistic bar lighting preserved.',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/809bea1d-cd33-4de8-bb19-cd1c406ada4f.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/monkey/NhR80d_h4zbsgKlLKJY9P_1b9c8ca5d8274debba230f0351525e2a.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/panda/wHeNdSDuBD_7ZqFxOq0KI_327c64c4b8c541179a439bd42cc5fec5.jpg',
            },
            status: 'success',
          },
          measured: { width: 342, height: 604 },
        },
        {
          id: 'image-to-video-scene3',
          type: 'image-to-video',
          position: { x: 2120.86, y: 2338.14 },
          data: {
            type: 'image-to-video',
            label: '#8 Animate Scene 3',
            config: {
              model: 'Veo 3.1 Fast Image',
              prompt:
                'Animate the model standing near the curtain, holding the product in her right hand. She poses calmly and gracefully, with soft, subtle movements — shifting her weight slightly, adjusting her posture, and occasionally tilting her head — maintaining a confident, serene expression. Lighting should be photorealistic. The camera remains steady or uses a very slow, subtle dolly/zoom to enhance the elegance of the pose. The movement should feel editorial, soft, and high-fashion, suitable for a luxury magazine–style short video clip.',
              duration: '8s',
              aspectRatio: '9:16',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/702f917d-b0a7-4269-a994-6b7e39686a48.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/rabbit/K23xLcHovFyESbj1M7NCs_47aac1ff420e4c91ade9a6568258d02a.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/zebra/Kk79b348Tql_NI3qzhzn4_3f007e1d4d274661b33981fe60078431.jpg',
            },
            status: 'success',
          },
          measured: { width: 342, height: 604 },
        },
        {
          id: 'video-merge-final',
          type: 'video-merge',
          position: { x: 2762.92, y: 1545.26 },
          data: {
            type: 'video-merge',
            label: '#9 Merge Final Video',
            config: {
              videoOrder: [
                'image-to-video-scene2',
                'image-to-video-scene3',
                'image-to-video-scene1',
              ],
            },
            result: {
              raw: {
                raw: {
                  video: {
                    url: 'https://v3b.fal.media/files/b/monkey/imMAvQFvwsjpB6peEu1Qv_merged_video.mp4',
                    file_name: 'merged_video.mp4',
                    file_size: 14131435,
                    content_type: 'video/mp4',
                  },
                  metadata: {
                    final_fps: 24,
                    has_audio: true,
                    final_width: 1080,
                    video_count: 3,
                    final_height: 1920,
                    input_videos: [
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/809bea1d-cd33-4de8-bb19-cd1c406ada4f.mp4',
                        index: 1,
                        width: 1080,
                        height: 1920,
                        duration: 8,
                        has_audio: true,
                      },
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/702f917d-b0a7-4269-a994-6b7e39686a48.mp4',
                        index: 2,
                        width: 1080,
                        height: 1920,
                        duration: 8,
                        has_audio: true,
                      },
                      {
                        fps: 24,
                        url: 'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/c192cf2f-f70f-432a-8e8d-60535f0e70e1.mp4',
                        index: 3,
                        width: 1080,
                        height: 1920,
                        duration: 8,
                        has_audio: true,
                      },
                    ],
                    total_duration: 24,
                  },
                },
                videoUrl:
                  'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/8f722d03-2660-4721-89fe-e8ba8d5a424b.mp4',
              },
              videoUrl:
                'https://videos.easy-peasy.ai/816f4e4a-621d-428e-8e8c-a4c273377f17/8f722d03-2660-4721-89fe-e8ba8d5a424b.mp4',
            },
            status: 'success',
          },
          measured: { width: 342, height: 604 },
        },
      ],
      edges: [
        {
          id: 'xy-edge__select-image-model-image-edit-scene1-image',
          source: 'select-image-model',
          target: 'image-edit-scene1',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-product-image-edit-scene1-image',
          source: 'upload-product',
          target: 'image-edit-scene1',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-model-image-edit-scene2-image',
          source: 'select-image-model',
          target: 'image-edit-scene2',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-product-image-edit-scene2-image',
          source: 'upload-product',
          target: 'image-edit-scene2',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-model-image-edit-scene3-image',
          source: 'select-image-model',
          target: 'image-edit-scene3',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-product-image-edit-scene3-image',
          source: 'upload-product',
          target: 'image-edit-scene3',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-scene1-image-to-video-scene1-image',
          source: 'image-edit-scene1',
          target: 'image-to-video-scene1',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-scene2-image-to-video-scene2-image',
          source: 'image-edit-scene2',
          target: 'image-to-video-scene2',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-scene3-image-to-video-scene3-image',
          source: 'image-edit-scene3',
          target: 'image-to-video-scene3',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-to-video-scene1-video-merge-final-video',
          source: 'image-to-video-scene1',
          target: 'video-merge-final',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
        {
          id: 'xy-edge__image-to-video-scene2-video-merge-final-video',
          source: 'image-to-video-scene2',
          target: 'video-merge-final',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
        {
          id: 'xy-edge__image-to-video-scene3-video-merge-final-video',
          source: 'image-to-video-scene3',
          target: 'video-merge-final',
          sourceHandle: 'video',
          targetHandle: 'video',
        },
      ],
    },
  },
  {
    id: 'contact-sheet-fashion-video',
    name: 'Contact Sheet Fashion Video Production',
    backgroundImage: 'https://media.easy-peasy.ai/assets/contact_sheet.jpg',
    description:
      'Advanced fashion video workflow using Contact Sheet Prompting with Nano Banana Pro. Upload a model image, apply wardrobe styling, generate 6 dynamic camera angle keyframes, then create multiple cinematic video clips with Veo 3.1 first-last frame animation.',
    category: 'Video',
    flow_data: {
      nodes: [
        {
          id: 'upload-1765423636774',
          type: 'upload',
          position: { x: 57.43499396582584, y: 781.3709348064107 },
          data: {
            type: 'upload',
            label: '#1 Upload Media',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/ada00b92-f018-4549-aec2-f2799d91990b.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/ada00b92-f018-4549-aec2-f2799d91990b.jpg',
          },
          dragging: false,
          measured: { width: 292, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1765423700790',
          type: 'image-edit',
          position: { x: 891.8215627429665, y: 788.2979286920223 },
          data: {
            type: 'image-edit',
            label: '#2 Image to Image',
            config: {
              useHD: false,
              prompt:
                'Show me a high fashion photoshoot image of the model wearing the oversized jacket and glasses, the image should show the a full body shot of the subject. The model is looking past the camera slightly bored expression and eyebrows raised. They have one hand raised with two fingers tapping the side of the glasses. The setting is a studio environment with a blue background. The model is wearing fashionable, dark grey baggy cotton pants. The jacket is extremely, almost comically oversized on the model. The image is from a low angle looking up at the subject. The image is shot on fuji velvia film on a 55mm prime lens with a hard flash, the light is concentrated on the subject and fades slightly toward the edges of the frame. The image is over exposed showing significant film grain and is oversaturated. The skin appears shiny (almost oily), and there are harsh white reflections on the glasses frames.',
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7de19b36-1c7d-4ff2-b3e9-7f1dc646660d.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 576, height: 388 },
          selected: false,
        },
        {
          id: 'image-edit-1765426031168',
          type: 'image-edit',
          position: { x: 1869.4181633858404, y: 357.91664534493754 },
          data: {
            type: 'image-edit',
            label: '#4 Contact Sheet Generation',
            config: {
              useHD: false,
              prompt:
                "Analyze the input image and silently inventory all fashion-critical details: the subject(s), exact wardrobe pieces, materials, colors, textures, accessories, hair, makeup, body proportions, environment, set geometry, light direction, and shadow quality. All wardrobe, styling, hair, makeup, lighting, environment, and color grade must remain 100% unchanged across all frames. Do not add or remove anything. Do not reinterpret materials or colors. Do not output any reasoning. Perfectly replicate the exact facial features of the model. Your visible output must be: One 2×3 contact sheet image (6 frames). Then a keyframe breakdown for each frame. Each frame must represent a resting point after a dramatic camera move — only describe the final camera position and what the subject is doing, never the motion itself. The six frames must be spatially dynamic, non-linear, and visually distinct. Required 6-Frame Shot List 1. High-Fashion Beauty Portrait (Close, Editorial, Intimate) Camera positioned very close to the subject's face, slightly above or slightly below eye level, using an elegant offset angle that enhances bone structure and highlights key wardrobe elements near the neckline. Shallow depth of field, flawless texture rendering, and a sculptural fashion-forward composition. 2. High-Angle Three-Quarter Frame Camera positioned overhead but off-center, capturing the subject from a diagonal downward angle.This frame should create strong shape abstraction and reveal wardrobe details from above. 3. Low-Angle Oblique Full-Body Frame Camera positioned low to the ground and angled obliquely toward the subject.This elongates the silhouette, emphasizes footwear, and creates a dramatic perspective distinct from Frames 1 and 2. 4. Side-On Compression Frame (Long Lens) Camera placed far to one side of the subject, using a tighter focal length to compress space.The subject appears in clean profile or near-profile, showcasing garment structure in a flattened, editorial manner. 5. Intimate Close Portrait From an Unexpected Height Camera positioned very close to the subject's face (or upper torso) but slightly above or below eye level.The angle should feel fashion-editorial, not conventional — offset, elegant, and expressive. 6. Extreme Detail Frame From a Non-Intuitive Angle Camera positioned extremely close to a wardrobe detail, accessory, or texture, but from an unusual spatial direction (e.g., from below, from behind, from the side of a neckline).This must be a striking, abstract, editorial detail frame. Continuity & Technical Requirements Maintain perfect wardrobe fidelity in every frame: exact garment type, silhouette, material, color, texture, stitching, accessories, closures, jewelry, shoes, hair, and makeup. Environment, textures, and lighting must remain consistent. Depth of field shifts naturally with focal length (deep for distant shots, shallow for close/detail shots). Photoreal textures and physically plausible light behavior required. Frames must feel like different camera placements within the same scene, not different scenes. All keyframes must be the exact same aspect ratio, and exactly 6 keyframes should be output. Maintain the exact visual style in all keyframes, where the image is shot on fuji velvia film with a hard flash, the light is concentrated on the subject and fades slightly toward the edges of the frame. The image is over exposed showing significant film grain and is oversaturated. The skin appears shiny (almost oily), and there are harsh white reflections on the glasses frames. Output FormatA)3x2 Contact Sheet Image (Mandatory)",
              editModel: 'Nano Banana Pro',
              dimensions: '3:2',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/6d96758a-4df6-420c-a58a-5605289fdd02.png',
            status: 'success',
          },
          dragging: false,
          measured: { width: 576, height: 388 },
          selected: false,
        },
        {
          id: 'image-splitter-1765426696970',
          type: 'image-splitter',
          position: { x: 2815.7801065474528, y: 131.03073297031432 },
          data: {
            type: 'image-splitter',
            label: '#46 Image Splitter',
            config: { rows: 2, columns: 3 },
            result: [
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e7efb40f-f005-43aa-9b61-3331d61966ce.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b76ab198-ee0d-40c6-b33f-5e79e00f8362.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0488982e-8290-45bf-bac2-19be11b16814.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/84bef970-a134-40b6-845f-e986afba2194.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c8b0e2d2-4d53-4d84-a2b2-38d8a243d1f2.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1f10e698-536d-43cb-bc13-883b90d3f007.png',
            ],
            status: 'success',
          },
          dragging: false,
          measured: { width: 386, height: 388 },
          selected: false,
        },
        {
          id: 'select-image-1765426745944-1-image-splitter-1765426696970',
          type: 'select-image',
          position: { x: 2814.7512699509593, y: 748.2252825241166 },
          data: {
            type: 'select-image',
            label: '#47 Split image 2',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b76ab198-ee0d-40c6-b33f-5e79e00f8362.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b76ab198-ee0d-40c6-b33f-5e79e00f8362.png',
            resultIndex: 1,
            sourceNodeId: 'image-splitter-1765426696970',
          },
          dragging: false,
          measured: { width: 386, height: 388 },
          selected: false,
        },
        {
          id: 'select-image-1765426745944-2-image-splitter-1765426696970',
          type: 'select-image',
          position: { x: 3359.7216723700662, y: 136.33169771788346 },
          data: {
            type: 'select-image',
            label: '#48 Split image 3',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0488982e-8290-45bf-bac2-19be11b16814.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0488982e-8290-45bf-bac2-19be11b16814.png',
            resultIndex: 2,
            sourceNodeId: 'image-splitter-1765426696970',
          },
          dragging: true,
          measured: { width: 386, height: 388 },
          selected: false,
        },
        {
          id: 'select-image-1765426745944-3-image-splitter-1765426696970',
          type: 'select-image',
          position: { x: 3379.637754459525, y: 746.9037849502793 },
          data: {
            type: 'select-image',
            label: '#49 Split image 4',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/84bef970-a134-40b6-845f-e986afba2194.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/84bef970-a134-40b6-845f-e986afba2194.png',
            resultIndex: 3,
            sourceNodeId: 'image-splitter-1765426696970',
          },
          dragging: false,
          measured: { width: 386, height: 388 },
          selected: false,
        },
        {
          id: 'select-image-1765426745945-4-image-splitter-1765426696970',
          type: 'select-image',
          position: { x: 3910.961810304984, y: 139.87857052728293 },
          data: {
            type: 'select-image',
            label: '#50 Split image 5',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c8b0e2d2-4d53-4d84-a2b2-38d8a243d1f2.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c8b0e2d2-4d53-4d84-a2b2-38d8a243d1f2.png',
            resultIndex: 4,
            sourceNodeId: 'image-splitter-1765426696970',
          },
          dragging: false,
          measured: { width: 386, height: 388 },
          selected: false,
        },
        {
          id: 'select-image-1765426745945-5-image-splitter-1765426696970',
          type: 'select-image',
          position: { x: 3912.559497335232, y: 746.3522367779264 },
          data: {
            type: 'select-image',
            label: '#51 Split image 6',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1f10e698-536d-43cb-bc13-883b90d3f007.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1f10e698-536d-43cb-bc13-883b90d3f007.png',
            resultIndex: 5,
            sourceNodeId: 'image-splitter-1765426696970',
          },
          dragging: false,
          measured: { width: 386, height: 388 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1765427119426',
          type: 'first-last-frame-video',
          position: { x: 2523.3725067551504, y: 1661.786765841798 },
          data: {
            type: 'first-last-frame-video',
            label: '#52 First-Last Frame to Video',
            config: {
              model: 'Veo 3.1 First-Last Frame',
              prompt:
                'The camera very slowly and smoothly orbits while zooming on the glasses. The subject moves barely moves, and is extremely deliberate and thoughtful in his movement.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/bf9b3f0e-ce7b-4210-8105-eecc8f9e47e7.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a85d4ba/K1g2LJ4jQ1faIRassMuLZ_c12da4734a6e48e5a159ec350db6ce6d.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a85d4ba/dsKqc1K3rUMLfJ-HuaUF6_79bd6d36f9104c8ca76ae34717fe4d6a.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1765427377684',
          type: 'first-last-frame-video',
          position: { x: 3544.0852314889644, y: 1668.5789793154893 },
          data: {
            type: 'first-last-frame-video',
            label: '#53 Camera Boom Movement',
            config: {
              model: 'Veo 3.1 First-Last Frame',
              prompt:
                'The camera very slowly and smoothly lowers on a boom. The subject moves barely moves, and is extremely deliberate and thoughtful in his movement.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/cd140edc-c876-4382-a345-4af8d5a77cda.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a85d4dd/BN1yn1HDKqt1DZ0KBZoRd_fa0d604dc8a64f0886cffa12ea58d79d.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a85d4dd/cx-UoHCaH9wo94-1GBnJv_50defb583b014fa0851220a436ba4686.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1765429308959',
          type: 'first-last-frame-video',
          position: { x: 4648.376171895813, y: 1666.8815398375853 },
          data: {
            type: 'first-last-frame-video',
            label: '#54 Smooth Camera Move',
            config: {
              model: 'Veo 3.1 First-Last Frame',
              prompt:
                'The camera very slowly and smoothly moves. The subject moves barely moves, and is extremely deliberate and thoughtful in his movement.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/036572fe-ab32-4c03-8893-6116395630ca.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a85d4ef/Q7wwVQToz2V4m-u6uMD8L_37cbe9eb995643069bd6f8a9fa5b4681.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a85d4ef/sJEr3uANKSv8aUQKkwnFh_2de2598bd6d54e4c83a446e00b159630.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1765429615685',
          type: 'first-last-frame-video',
          position: { x: 3159.012624341661, y: 2458.3460933919655 },
          data: {
            type: 'first-last-frame-video',
            label: '#55 Orbit and Zoom Detail',
            config: {
              model: 'Veo 3.1 First-Last Frame',
              prompt:
                'The camera very slowly and smoothly orbits while zooming on the glasses. The subject moves barely moves, and is extremely deliberate and thoughtful in his movement.',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4ca90be0-042b-4257-ad73-e909a6ac505c.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a85d501/Gq1Plln9U1EsZpHdFHApS_8545f47576134ad1885f6e625a92edf1.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a85d501/6EusDFyAQGJIMSwWNO1F4_75cccf037a9d45908fd0855bfb9f28ad.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'first-last-frame-video-1765429771680',
          type: 'first-last-frame-video',
          position: { x: 4428.586551578933, y: 2464.2900022298604 },
          data: {
            type: 'first-last-frame-video',
            label: '#56 High Boom Shot',
            config: {
              model: 'Veo 3.1 First-Last Frame',
              prompt: 'The camera moves up high on a boom. ',
              duration: '8s',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ea567b97-3319-49cf-81ab-e08ceb9a8b92.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a85d592/jGpBZ7OkK6yNnCfcLks8S_dd161d45c787498b8523827b6439a5f6.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a85d591/W3xafRkVOoUB-CVBGeI3h_e3f56c775aa34be29f8f1bdd42a5aa9f.jpg',
            },
            status: 'success',
          },
          dragging: false,
          measured: { width: 715, height: 404 },
          selected: false,
        },
        {
          id: 'note-1765429943603',
          type: 'note',
          position: { x: 676.9333710737648, y: 1920.4338894399973 },
          data: {
            type: 'note',
            label: 'Workflow Guide',
            config: {
              note: "<h1>Contact Sheet Prompting for Fashion Videos</h1><p>This advanced workflow uses Nano Banana Pro's reasoning capabilities to generate cohesive multi-angle fashion shoots.</p><p></p><p><strong>How it works:</strong></p><p>1. <strong>Upload</strong> your model image</p><p>2. <strong>Style the shot</strong> - Apply wardrobe, lighting, and pose using Image to Image</p><p>3. <strong>Generate contact sheet</strong> - NBP creates 6 keyframes with different camera angles (close-up, overhead, low-angle, profile, detail shots)</p><p>4. <strong>Split frames</strong> - Extract individual keyframes from the 2×3 grid</p><p>5. <strong>Animate</strong> - Create cinematic videos between keyframes using Veo 3.1</p><p></p><p><strong>Pro Tips:</strong></p><p>• Generate contact sheet at 2K or 4K for better detail</p><p>• Use smooth, minimal camera movements in video prompts</p><p>• Keep subject movement deliberate and minimal</p><p>• Adapt the contact sheet prompt for your specific needs</p>",
            },
          },
          dragging: false,
          measured: { width: 400, height: 100 },
          selected: false,
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1765423636774image-image-edit-1765423700790image',
          source: 'upload-1765423636774',
          target: 'image-edit-1765423700790',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1765423700790image-image-edit-1765426031168image',
          source: 'image-edit-1765423700790',
          target: 'image-edit-1765426031168',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1765426031168image-image-splitter-1765426696970image',
          source: 'image-edit-1765426031168',
          target: 'image-splitter-1765426696970',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1765423700790image-first-last-frame-video-1765427119426first-frame',
          source: 'image-edit-1765423700790',
          target: 'first-last-frame-video-1765427119426',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__select-image-1765426745944-2-image-splitter-1765426696970image-first-last-frame-video-1765427377684last-frame',
          source: 'select-image-1765426745944-2-image-splitter-1765426696970',
          target: 'first-last-frame-video-1765427377684',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__image-splitter-1765426696970image-first-last-frame-video-1765427119426last-frame',
          source: 'image-splitter-1765426696970',
          target: 'first-last-frame-video-1765427119426',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__image-splitter-1765426696970image-first-last-frame-video-1765427377684first-frame',
          source: 'image-splitter-1765426696970',
          target: 'first-last-frame-video-1765427377684',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__first-last-frame-video-1765427377684last-frame-first-last-frame-video-1765429308959first-frame',
          source: 'first-last-frame-video-1765427377684',
          target: 'first-last-frame-video-1765429308959',
          sourceHandle: 'last-frame',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__select-image-1765426745944-3-image-splitter-1765426696970image-first-last-frame-video-1765429308959last-frame',
          source: 'select-image-1765426745944-3-image-splitter-1765426696970',
          target: 'first-last-frame-video-1765429308959',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__select-image-1765426745944-3-image-splitter-1765426696970image-first-last-frame-video-1765429615685first-frame',
          source: 'select-image-1765426745944-3-image-splitter-1765426696970',
          target: 'first-last-frame-video-1765429615685',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
        {
          id: 'xy-edge__select-image-1765426745945-4-image-splitter-1765426696970image-first-last-frame-video-1765429615685last-frame',
          source: 'select-image-1765426745945-4-image-splitter-1765426696970',
          target: 'first-last-frame-video-1765429615685',
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__select-image-1765426745944-1-image-splitter-1765426696970image-first-last-frame-video-1765429771680last-frame',
          source: 'select-image-1765426745944-1-image-splitter-1765426696970',
          target: 'first-last-frame-video-1765429771680',
          selected: false,
          sourceHandle: 'image',
          targetHandle: 'last-frame',
        },
        {
          id: 'xy-edge__select-image-1765426745945-4-image-splitter-1765426696970image-first-last-frame-video-1765429771680first-frame',
          source: 'select-image-1765426745945-4-image-splitter-1765426696970',
          target: 'first-last-frame-video-1765429771680',
          sourceHandle: 'image',
          targetHandle: 'first-frame',
        },
      ],
    },
  },
  {
    id: 'consistent-characters-multiple-models',
    name: 'Consistent Characters Across AI Models',
    backgroundImage:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/42c06234-e0cf-4d64-8508-9a74fb80ec85.png',
    description:
      'Create scenes with consistent characters using multiple AI models. Upload reference images of two characters, describe your scene, and generate variations across different models to find the perfect result.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'select-image-1765864190205',
          type: 'select-image',
          position: { x: 1075.595238095238, y: 329.6428571428572 },
          dragging: false,
          measured: { width: 388, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Character #1 - Woman',
            config: {
              selectedImageId: 3360854,
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ba9cf544-322f-4d49-ba4d-bfe34a280579.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ba9cf544-322f-4d49-ba4d-bfe34a280579.png',
          },
        },
        {
          id: 'select-image-1765864265942',
          type: 'select-image',
          position: { x: 1090.4970842672305, y: 951.2825275114122 },
          dragging: false,
          measured: { width: 388, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Character #2 - Man',
            config: {
              selectedImageId: 3360719,
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d60fa3a8-b645-41f6-adbe-54fe1e410f4f.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d60fa3a8-b645-41f6-adbe-54fe1e410f4f.png',
          },
        },
        {
          id: 'input-1765864363939',
          type: 'input',
          position: { x: 1513.2285714285715, y: 117.27619047619059 },
          dragging: false,
          measured: { width: 150, height: 162 },
          selected: false,
          data: {
            type: 'input',
            label: 'Scene Description',
            config: {
              value:
                'medium shot cinematic scene of the woman  siting next to the man in a crowded mid-century style bar. each of them holding a whisky glass and they are talking to each other.',
            },
            result:
              'medium shot cinematic scene of the woman  siting next to the man in a crowded mid-century style bar. each of them holding a whisky glass and they are talking to each other.',
            status: 'success',
          },
        },
        {
          id: 'note-1765864689116',
          type: 'note',
          position: { x: 521.4285714285718, y: 555.2761904761907 },
          dragging: false,
          measured: { width: 400, height: 144 },
          selected: false,
          data: {
            type: 'note',
            label: 'How It Works',
            config: {
              note: '<h1>2 Consistent Characters</h1><p></p><p><strong>This workflow demonstrates:</strong></p><ul><li>Upload reference images of 2 characters (woman & man)</li><li>Write your scene description once</li><li>Test across 4 different AI models simultaneously</li><li>Edit individual character details as needed</li><li>Regenerate with modifications while maintaining consistency</li></ul><p></p><p><strong>Pro tip:</strong> Different AI models excel at different styles - run them all to find which works best for your vision!</p>',
            },
          },
        },
        {
          id: 'image-edit-1765864401519',
          type: 'image-edit',
          position: { x: 2136.028571428571, y: 148.9261904761907 },
          dragging: false,
          measured: { width: 452, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Generate with Nano Banana Pro',
            config: {
              useHD: false,
              prompt: '@#3 Text Input',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/80fd6606-b278-4999-8c09-beda60611e82.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765864467839',
          type: 'image-edit',
          position: { x: 2112.800527477712, y: 956.5018388693162 },
          dragging: false,
          measured: { width: 516, height: 388 },
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Generate with Seedream 4.5',
            config: {
              useHD: false,
              prompt: '@#3 Text Input',
              editModel: 'Seedream 4.5',
              dimensions: '4:3',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c2f9c14e-2e38-486c-8a56-ad64642b0674.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765864544349',
          type: 'image-edit',
          position: { x: 2962.2883516742745, y: 964.2737949184568 },
          dragging: false,
          measured: { width: 580, height: 388 },
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Generate with Reve',
            config: {
              useHD: false,
              prompt: '@#3 Text Input',
              editModel: 'Reve',
              dimensions: 'auto',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/747014a3-d528-41d9-a07f-54d9a632c2ea.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765864545018',
          type: 'image-edit',
          position: { x: 2847.228571428571, y: 231.92619047619064 },
          dragging: false,
          measured: { width: 687, height: 388 },
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Generate with FLUX.2 [pro]',
            config: {
              useHD: false,
              prompt: '@#3 Text Input',
              editModel: 'FLUX.2 [pro]',
              dimensions: '16:9',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8196235e-5938-4b83-b4c1-6531cf4ab474.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765871029295',
          type: 'image-edit',
          position: { x: 2140.1482702554617, y: 1819.8178897820023 },
          dragging: false,
          measured: { width: 388, height: 388 },
          selected: false,
          data: {
            type: 'image-edit',
            label: 'Edit Character #2 Details',
            config: {
              useHD: false,
              prompt: 'add glasses and change the color of blouse to purple',
              editModel: 'Nano Banana Pro',
              dimensions: 'auto',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ffa20b51-c563-4deb-9806-c8253666fe3e.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765871087816',
          type: 'image-edit',
          position: { x: 3010.648130712287, y: 1807.343517489244 },
          dragging: false,
          measured: { width: 516, height: 388 },
          selected: true,
          data: {
            type: 'image-edit',
            label: 'Regenerate with Modified Character',
            config: {
              useHD: false,
              prompt: '@#3 Text Input',
              editModel: 'Seedream 4.5',
              dimensions: '4:3',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/42c06234-e0cf-4d64-8508-9a74fb80ec85.png',
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__select-image-1765864190205image-image-edit-1765864401519image',
          source: 'select-image-1765864190205',
          target: 'image-edit-1765864401519',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1765864265942image-image-edit-1765864401519image',
          source: 'select-image-1765864265942',
          target: 'image-edit-1765864401519',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1765864363939text-image-edit-1765864401519prompt',
          source: 'input-1765864363939',
          target: 'image-edit-1765864401519',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__select-image-1765864190205image-image-edit-1765864467839image',
          source: 'select-image-1765864190205',
          target: 'image-edit-1765864467839',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1765864363939text-image-edit-1765864467839prompt',
          source: 'input-1765864363939',
          target: 'image-edit-1765864467839',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__select-image-1765864265942image-image-edit-1765864467839image',
          source: 'select-image-1765864265942',
          target: 'image-edit-1765864467839',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1765864190205image-image-edit-1765864545018image',
          source: 'select-image-1765864190205',
          target: 'image-edit-1765864545018',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1765864265942image-image-edit-1765864545018image',
          source: 'select-image-1765864265942',
          target: 'image-edit-1765864545018',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1765864363939text-image-edit-1765864545018prompt',
          source: 'input-1765864363939',
          target: 'image-edit-1765864545018',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__select-image-1765864265942image-image-edit-1765864544349image',
          source: 'select-image-1765864265942',
          target: 'image-edit-1765864544349',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1765864190205image-image-edit-1765864544349image',
          source: 'select-image-1765864190205',
          target: 'image-edit-1765864544349',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1765864363939text-image-edit-1765864544349prompt',
          source: 'input-1765864363939',
          target: 'image-edit-1765864544349',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
        {
          id: 'xy-edge__select-image-1765864265942image-image-edit-1765871029295image',
          source: 'select-image-1765864265942',
          target: 'image-edit-1765871029295',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1765871029295image-image-edit-1765871087816image',
          source: 'image-edit-1765871029295',
          target: 'image-edit-1765871087816',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1765864190205image-image-edit-1765871087816image',
          source: 'select-image-1765864190205',
          target: 'image-edit-1765871087816',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__input-1765864363939text-image-edit-1765871087816prompt',
          source: 'input-1765864363939',
          target: 'image-edit-1765871087816',
          sourceHandle: 'text',
          targetHandle: 'prompt',
        },
      ],
    },
  },
  {
    id: 'portrait-multi-style-transformation',
    name: 'Portrait Multi-Style Transformation',
    backgroundImage:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/f5e568fa-db44-477a-bb59-7ee65809590b.png',
    description:
      'Transform a single portrait into 10+ professional styles. Generate studio portraits, cinematic looks, neon aesthetics, oil paintings, Y2K vibes, and more. Includes bulk style generation via grid splitting for maximum variety.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-main-1763900000000',
          type: 'note',
          position: { x: -697.6856273723249, y: 311.61410235250173 },
          dragging: false,
          measured: { width: 400, height: 512 },
          selected: false,
          data: {
            type: 'note',
            label: 'One Photo → Many Styles',
            config: {
              note: '<h1>One Photo → Many Styles</h1><p><strong>Transform your portrait into 10+ distinct professional styles.</strong></p><p></p><p><strong>How to use:</strong></p><p>1. Upload your portrait photo using <strong>Upload Portrait Photo</strong>.</p><p></p><p>2. The workflow generates multiple variations:</p><ul><li>Clean Studio Portrait</li><li>Oil Painting Portrait</li><li>Y2K Aesthetic Selfie</li><li>Cinematic Golden Hour</li><li>Neon Urban Portrait</li><li>Glitch Art Avatar</li><li>E-Commerce/Portfolio Ready</li></ul><p></p><p>3. <strong>Bonus:</strong> Generate 15 art styles at once using the grid splitter workflow path.</p><p></p><p>4. Each style node can be customized by editing the prompt.</p><p></p><p>5. Upscale your favorite result to 4K quality.</p>',
            },
          },
        },
        {
          id: 'upload-photo-1763900001000',
          type: 'upload',
          position: { x: -37.740584590942646, y: 456.1347272423814 },
          dragging: false,
          measured: { width: 289, height: 388 },
          selected: false,
          data: {
            type: 'upload',
            label: 'Upload Portrait Photo',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/262eb3f3-16d5-4ad4-87ec-18970f9490a4.jpg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/262eb3f3-16d5-4ad4-87ec-18970f9490a4.jpg',
          },
        },
        {
          id: 'style-studio-1763900002000',
          type: 'image-edit',
          position: { x: 670.2680262175586, y: -1738.601640889323 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#1 Clean Studio Portrait',
            config: {
              useHD: false,
              prompt:
                'Professional studio portrait photography. Apply clean solid color background with subtle light vignette. Main light at 45° angle, fill light at 20% intensity, moderate sharpness. Remove noise and imperfections while keeping skin texture natural and realistic. Balance teeth and eyes naturally without over-enhancement. Professional headshot quality.',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/6ac03f0a-9fad-45cb-81c6-a544269f6092.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765870022791',
          type: 'image-edit',
          position: { x: 660.6586928004393, y: -1023.8517164017769 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#2 Oil Painting Portrait',
            config: {
              useHD: false,
              prompt:
                'Transform into classical oil painting portrait. Rich, painterly brushstrokes with visible texture and impasto technique. Soft edges and blended transitions like Renaissance portraiture. Warm color palette with earthy tones - burnt sienna, ochre, umber in shadows. Subtle canvas texture throughout. Chiaroscuro lighting with gentle gradations. Style inspired by Rembrandt, Velázquez, or John Singer Sargent. Not photorealistic - embrace artistic interpretation with painted quality and traditional fine art aesthetic.',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2995ed16-d808-434b-9944-77bb26158d15.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765870068599',
          type: 'image-edit',
          position: { x: 653.7341988654746, y: -346.6872310746307 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#3 Y2K Aesthetic Mirror Selfie',
            config: {
              useHD: false,
              prompt:
                'A soft-lit Y2K bedroom mirror selfie. Wearing a fitted white crop top with a pastel anime cat print, low-rise jeans, and a thin waist chain. She holds a phone with a decorated case, taking a mirror selfie with bright flash reflection. She has glossy red lips, subtle blush, gold hoop earrings, and natural glam makeup. The room has pastel lavender walls covered with vintage 90s–2000s pop star posters, a white vanity table with makeup items, a wooden dresser, beaded doorway curtain, and soft ambient lighting. Add a nostalgic early-2000s aesthetic, dreamy soft focus, subtle film grain, warm tones, and realistic reflections in the mirror.',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/84542086-736e-4f92-b436-5013061c1060.png',
            status: 'success',
          },
        },
        {
          id: 'style-golden-1763900005000',
          type: 'image-edit',
          position: { x: 642.1291913255906, y: 315.83578717157815 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#4 Cinematic Golden Hour',
            config: {
              useHD: false,
              prompt:
                'Cinematic sunset mood lighting. Warm golden-amber temperature in highlights, cool teal-blue in shadows. Add subtle lens flare and gentle S-curve contrast. Slightly soften background for depth separation while keeping subject sharp. Natural skin tones with movie-grade color grading. Dramatic directional lighting as if shot during golden hour. Color palette: gold, amber, warm orange, teal blue.',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/77275bb7-43ae-431b-a369-4b787028b8c5.png',
            status: 'success',
          },
        },
        {
          id: 'style-neon-1763900006000',
          type: 'image-edit',
          position: { x: 631.8184314667686, y: 1011.899108425644 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#5 Neon Urban Portrait',
            config: {
              useHD: false,
              prompt:
                'Cyberpunk urban night portrait. Simulate neon lighting with magenta-pink on one side and cyan-blue on the other side of face. Urban bokeh background with soft city lights. Increase local contrast, controlled film grain. Create variation with thin rain and wet reflections. Modern nocturnal street photography aesthetic with neon color accents.',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/172b582e-60aa-4f84-91dc-ac01e795a738.png',
            status: 'success',
          },
        },
        {
          id: 'style-illustrated-1763900007000',
          type: 'image-edit',
          position: { x: 635.4736302829839, y: 1696.876005539616 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#6 Glitch Art Avatar',
            config: {
              useHD: false,
              prompt: 'glitch art',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/8050a11c-44dd-4955-b68d-dae9a457d0e1.png',
            status: 'success',
          },
        },
        {
          id: 'style-ecommerce-1763900008000',
          type: 'image-edit',
          position: { x: 620.4487531506169, y: 2401.6003718555826 },
          dragging: false,
          measured: { width: 487, height: 604 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#7 E-Commerce Portfolio',
            config: {
              useHD: false,
              prompt:
                'Professional e-commerce product photography style. Pure white (#FFFFFF) background, even lighting with no shadows. Clean, crisp, high-resolution with accurate colors. Professional retouching while maintaining natural appearance. Commercial photography quality suitable for business profiles, portfolios, and professional listings.',
              editModel: 'Nano Banana Pro',
              dimensions: '4:5',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b0843c58-f050-499e-b802-7c77f7f8b989.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1765871197773',
          type: 'image-edit',
          position: { x: 1822.3907608973568, y: 406.9893726635274 },
          dragging: false,
          measured: { width: 692, height: 388 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#8 Generate 15 Art Styles Grid',
            config: {
              useHD: false,
              prompt:
                'Convert this photo into 3x3 grid with popular art styles',
              editModel: 'Nano Banana Pro',
              dimensions: '16:9',
              resolution: '4K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/f5e568fa-db44-477a-bb59-7ee65809590b.png',
            status: 'success',
          },
        },
        {
          id: 'image-splitter-1765872804444',
          type: 'image-splitter',
          position: { x: 2728.515455453748, y: 345.9926304394877 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'image-splitter',
            label: '#9 Split Into Individual Styles',
            config: { rows: 3, columns: 5 },
            result: [
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5508bf1b-29d4-48b3-8faa-51e9502961d6.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7024ecaa-e836-467e-aa16-9ea4badcb745.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/90848594-fe9d-4f57-a7f8-801ffae8dec2.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e7449e6b-e9b3-49c1-ba17-0a4c7b87fd8e.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1d955512-f7e8-48bc-a68d-bc87ab254391.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4b352864-55ed-46bd-8878-0dd6a0768c0e.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/566a3f61-4f68-4cc2-a119-a4b29d2cb4e4.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ba6535e9-1eb8-4739-a5f5-f505f023b677.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4c51252a-597e-44a9-b0a2-d2f5645c392f.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1e908d51-bcd0-48e7-805e-f62bfc527eef.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e9ae0e46-983a-45b7-a142-2ceae6032e74.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/39824ece-82ee-4386-9608-602e76a8cd07.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2388a988-bd9f-4cff-bf0f-8c3136a19665.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a3a1ca2d-e76d-4fbf-b086-f0a7cf8d91a5.png',
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5fdc8eb3-68e5-4cac-ad5a-ab22857e1ff4.png',
            ],
            status: 'success',
          },
        },
        {
          id: 'select-image-1765872852822-1-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 3235.658965865215, y: 340.4950265111527 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #2',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7024ecaa-e836-467e-aa16-9ea4badcb745.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/7024ecaa-e836-467e-aa16-9ea4badcb745.png',
            resultIndex: 1,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852822-2-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 3762.9539580552514, y: 354.31749721642854 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #3',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/90848594-fe9d-4f57-a7f8-801ffae8dec2.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/90848594-fe9d-4f57-a7f8-801ffae8dec2.png',
            resultIndex: 2,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852822-3-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 4282.862943072772, y: 346.5288354624991 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #4',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e7449e6b-e9b3-49c1-ba17-0a4c7b87fd8e.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e7449e6b-e9b3-49c1-ba17-0a4c7b87fd8e.png',
            resultIndex: 3,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-4-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 4776.809722243863, y: 359.50993838571475 },
          dragging: false,
          measured: { width: 418, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #5',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1d955512-f7e8-48bc-a68d-bc87ab254391.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1d955512-f7e8-48bc-a68d-bc87ab254391.png',
            resultIndex: 4,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-5-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 2734.2489902186085, y: 935.870908176491 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #6',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4b352864-55ed-46bd-8878-0dd6a0768c0e.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4b352864-55ed-46bd-8878-0dd6a0768c0e.png',
            resultIndex: 5,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-6-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 3254.1579752361304, y: 938.4671287611341 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #7',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/566a3f61-4f68-4cc2-a119-a4b29d2cb4e4.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/566a3f61-4f68-4cc2-a119-a4b29d2cb4e4.png',
            resultIndex: 6,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-7-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 3770.497156949768, y: 935.870908176491 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #8',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ba6535e9-1eb8-4739-a5f5-f505f023b677.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ba6535e9-1eb8-4739-a5f5-f505f023b677.png',
            resultIndex: 7,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-8-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 4293.975945271173, y: 928.0822464225616 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #9',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4c51252a-597e-44a9-b0a2-d2f5645c392f.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4c51252a-597e-44a9-b0a2-d2f5645c392f.png',
            resultIndex: 8,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-9-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 4777.537842103691, y: 933.2746875918479 },
          dragging: false,
          measured: { width: 418, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #10',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1e908d51-bcd0-48e7-805e-f62bfc527eef.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1e908d51-bcd0-48e7-805e-f62bfc527eef.png',
            resultIndex: 9,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-10-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 2729.7846689091507, y: 1483.6734515361927 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #11',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e9ae0e46-983a-45b7-a142-2ceae6032e74.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e9ae0e46-983a-45b7-a142-2ceae6032e74.png',
            resultIndex: 10,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852823-11-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 3226.327668664884, y: 1486.2696721208356 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #12',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/39824ece-82ee-4386-9608-602e76a8cd07.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/39824ece-82ee-4386-9608-602e76a8cd07.png',
            resultIndex: 11,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852824-12-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 3780.9611040020077, y: 1488.865892705479 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #13',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2388a988-bd9f-4cff-bf0f-8c3136a19665.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2388a988-bd9f-4cff-bf0f-8c3136a19665.png',
            resultIndex: 12,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852824-13-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 4284.319182792429, y: 1486.2696721208356 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #14',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a3a1ca2d-e76d-4fbf-b086-f0a7cf8d91a5.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a3a1ca2d-e76d-4fbf-b086-f0a7cf8d91a5.png',
            resultIndex: 13,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'select-image-1765872852824-14-image-splitter-1765872804444',
          type: 'select-image',
          position: { x: 4793.843285471378, y: 1496.6545544594082 },
          dragging: false,
          measured: { width: 418, height: 388 },
          selected: false,
          data: {
            type: 'select-image',
            label: 'Style #15',
            config: {
              selectedIsVideo: false,
              selectedImageUrl:
                'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5fdc8eb3-68e5-4cac-ad5a-ab22857e1ff4.png',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5fdc8eb3-68e5-4cac-ad5a-ab22857e1ff4.png',
            resultIndex: 14,
            sourceNodeId: 'image-splitter-1765872804444',
          },
        },
        {
          id: 'image-upscaler-1765872927382',
          type: 'image-upscaler',
          position: { x: 3779.551718789811, y: -304.2654534274963 },
          dragging: false,
          measured: { width: 417, height: 388 },
          selected: false,
          data: {
            type: 'image-upscaler',
            label: '#10 Upscale to 4K Quality',
            config: {
              HDR: 6,
              mode: 'Crisp',
              creativity: 3,
              fractality: 6,
              resemblance: 6,
              scaleFactor: 4,
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/93e10655-56fb-48ba-bcf3-6410eaa9564a.jpg',
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-photo-1763900001000image-style-studio-1763900002000image',
          source: 'upload-photo-1763900001000',
          target: 'style-studio-1763900002000',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-style-golden-1763900005000image',
          source: 'upload-photo-1763900001000',
          target: 'style-golden-1763900005000',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-style-neon-1763900006000image',
          source: 'upload-photo-1763900001000',
          target: 'style-neon-1763900006000',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-style-illustrated-1763900007000image',
          source: 'upload-photo-1763900001000',
          target: 'style-illustrated-1763900007000',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-style-ecommerce-1763900008000image',
          source: 'upload-photo-1763900001000',
          target: 'style-ecommerce-1763900008000',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-image-edit-1765870022791image',
          source: 'upload-photo-1763900001000',
          target: 'image-edit-1765870022791',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-image-edit-1765870068599image',
          source: 'upload-photo-1763900001000',
          target: 'image-edit-1765870068599',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-photo-1763900001000image-image-edit-1765871197773image',
          source: 'upload-photo-1763900001000',
          target: 'image-edit-1765871197773',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1765871197773image-image-splitter-1765872804444image',
          source: 'image-edit-1765871197773',
          target: 'image-splitter-1765872804444',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__select-image-1765872852823-5-image-splitter-1765872804444image-image-upscaler-1765872927382image',
          source: 'select-image-1765872852823-5-image-splitter-1765872804444',
          target: 'image-upscaler-1765872927382',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'brand-visual-storyboard',
    name: 'Brand Visual Storyboard',
    backgroundImage:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/00b076bf-3e9f-4d43-a397-6143fd836a98.png',
    description:
      'Create a professional designer mockup series from a single product image. Generates 9 high-end variations including hero shots, close-ups, levitating compositions, isometric arrangements, interaction shots, and editorial setups – perfect for branding portfolios.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-1769441689774',
          type: 'note',
          position: { x: 252.12729112676965, y: -899.8732848533624 },
          dragging: false,
          measured: { width: 404, height: 145 },
          selected: false,
          data: {
            type: 'note',
            label: 'Brand Visual Storyboard',
            config: {
              note: '<h1>Brand Visual Storyboard</h1><p><strong>Transform your product into a professional branding portfolio.</strong></p><p></p><p><strong>How to use:</strong></p><p>1. Delete the existing photo in <strong>Upload Media</strong> node and upload your product image.</p><p>2. Click run on each node to generate 9 different mockup variations.</p><p>3. Generated images are kept in each node for easy comparison and export.</p>',
            },
          },
        },
        {
          id: 'upload-1769420195450',
          type: 'upload',
          position: { x: 256.273676937333, y: -134.08220370465492 },
          dragging: false,
          measured: { width: 424, height: 424 },
          selected: false,
          data: {
            type: 'upload',
            label: 'Upload Product Image',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/0b14c7be-2255-4d64-94f6-33c7ffcfdc51.webp',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/0b14c7be-2255-4d64-94f6-33c7ffcfdc51.webp',
          },
        },
        {
          id: 'image-edit-1769438236059',
          type: 'image-edit',
          position: { x: 1260.337472909952, y: -921.8599718980181 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#1 Hero Shot – Studio',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same.\n\nThis is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven.\n\nCreate front-facing hero shot of the product in a clean studio setup. Neutral background, balanced composition, calm and confident presentation of the product.\n\nCAMERA & STYLE:\n\nUltra high-quality studio imagery with a real camera look.  Controlled depth of field, precise lighting, accurate materials and reflections. ',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/94cd9969-bf14-43b3-b3ca-6edbfc17a689.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438329961',
          type: 'image-edit',
          position: { x: 1254.654072169363, y: -193.4016944534751 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#2 Close-Up – Texture Detail',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity across. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Create close-up shot with the focus centered on the middle of the product. Focusing on surface texture, materials and print details.',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ac9ecc80-ec13-4054-9feb-933d0f1335b6.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438355652',
          type: 'image-edit',
          position: { x: 1859.2900505986495, y: -925.1673337983212 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#3 Environment – Brand Setting',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Shows the reference product placed in an environment that naturally fits the brand and product category. Studio setting inspired by the product design elements and colours. \nCAMERA & STYLE:\n\nUltra high-quality studio imagery with a real camera look.  Controlled depth of field, precise lighting, accurate materials and reflections. ',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ce043232-6447-4f16-a549-8051c66f4d2e.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438407271',
          type: 'image-edit',
          position: { x: 1826.410190627075, y: -183.3110432183843 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#4 Isometric – Grid Arrangement',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Create an isometric composition showing multiple products arranged in a precise geometric order from the top isometric angle. All products are placed at the same isometric top angle, evenly spaced, clean, structured and graphic.',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/ad7b0447-8853-4f16-97af-26946088832c.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438376049',
          type: 'image-edit',
          position: { x: 2414.2705477658324, y: 545.7755126490829 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#5 Interaction – Hands',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Shows the product shown in interaction on a neutral studio background. Hands and interaction elements are minimal and restrained, the look matches the style of the package.\nCAMERA & STYLE:\n\nUltra high-quality studio imagery with a real camera look.  Controlled depth of field, precise lighting, accurate materials and reflections. \n',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/00b076bf-3e9f-4d43-a397-6143fd836a98.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438661317',
          type: 'image-edit',
          position: { x: 1844.992543815336, y: 531.0165447652312 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#6 Levitating – Floating Product',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Shows the product levitating slightly tilted on a neutral background that matches the reference image color palette. Floating position is angled and intentional, the product is floating naturally in space.',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/979c6db7-85ef-4d71-9501-8dca29bea5fc.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438698782',
          type: 'image-edit',
          position: { x: 2447.728398296593, y: -916.2916012882265 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#7 In Use – Product Action',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Shows the product shown in use or interaction on a neutral studio background.\nCAMERA & STYLE:\n\nUltra high-quality studio imagery with a real camera look.  Controlled depth of field, precise lighting, accurate materials and reflections. ',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/078a62a9-ecc1-4170-92dc-2d3c75bae4de.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438754904',
          type: 'image-edit',
          position: { x: 2403.755221888816, y: -172.86657865410496 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#8 Editorial – Bold Setting',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Show the product in an unexpected yet aesthetically strong setting that feels bold, editorial and visually striking.\n\nUnexpected but highly stylized setting. Studio-based, and designer-driven. Bold composition that elevates the brand.\nCAMERA & STYLE:\n\nUltra high-quality studio imagery with a real camera look.  Controlled depth of field, precise lighting, accurate materials and reflections. ',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c5861849-9baa-4bb7-906a-a0ff81881a41.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1769438761072',
          type: 'image-edit',
          position: { x: 1229.1346095270173, y: 531.1189426180362 },
          dragging: false,
          measured: { width: 424, height: 567 },
          selected: false,
          data: {
            type: 'image-edit',
            label: '#9 Wide – Designer Setup',
            config: {
              useHD: false,
              prompt:
                'Use the reference image as the base product reference. Keep the same product, packaging design, branding, materials, colors, proportions and overall identity exactly as the reference. The product must remain clearly recognizable. The label, logo and proportions must stay exactly the same. This is a high-end designer mockup presentation for a branding portfolio. The focus is on form, composition, materiality and visual rhythm rather than realism or lifestyle narrative. The overall look should feel curated, editorial and design-driven. Create wide composition showing the product in interaction, placed within a refined designer setup. Clean props, controlled styling, cohesive with the rest of the series.\n\nCAMERA & STYLE:\n\nUltra high-quality studio imagery with a real camera look.  Controlled depth of field, precise lighting, accurate materials and reflections. ',
              editModel: 'Nano Banana Pro',
              dimensions: '3:4',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e778f3fb-c37d-4be2-891d-3bb5e54027b7.png',
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438236059image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438236059',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438329961image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438329961',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438355652image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438355652',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438376049image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438376049',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438407271image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438407271',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438661317image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438661317',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438698782image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438698782',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438754904image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438754904',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1769420195450image-image-edit-1769438761072image',
          source: 'upload-1769420195450',
          target: 'image-edit-1769438761072',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
  {
    id: 'virtual-staging-furniture-swap',
    name: 'Virtual Staging – Furniture Swap',
    backgroundImage:
      'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/1d37cbd3-aa1b-4e33-b8a6-1a578be5dd77.png',
    description:
      'Remove existing furniture from a room photo, place a new piece from a reference image, and animate the final result into a cinematic video tour.',
    category: 'Image',
    flow_data: {
      nodes: [
        {
          id: 'note-1763881231198',
          type: 'note',
          position: { x: 1175.978, y: 272.345 },
          data: {
            type: 'note',
            label: 'Virtual Staging – Furniture Swap',
            config: {
              note: '<h1>Virtual Staging – Furniture Swap</h1><p><strong>Replace furniture in a room photo with a new piece and create a video tour.</strong></p><p>1. Upload the <strong>room photo</strong> (Upload Media #22) and the <strong>furniture reference</strong> (Upload Media #16).</p><p>2. Use <strong>Image to Image #23</strong> to remove existing furniture from the room.</p><p>3. Use <strong>Image to Image #20</strong> to composite the new furniture into the empty room.</p><p>4. Animate the staged room with <strong>Image to Video</strong> for a cinematic walkthrough.</p>',
            },
          },
        },
        {
          id: 'upload-1772687785460',
          type: 'upload',
          position: { x: 1841.139, y: 218.54 },
          data: {
            type: 'upload',
            label: 'Upload Room Photo',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/3c7a8ddd-057a-42af-91c6-f97e3ffe2b78.png',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/3c7a8ddd-057a-42af-91c6-f97e3ffe2b78.png',
            status: 'success',
          },
        },
        {
          id: 'upload-1768722824050',
          type: 'upload',
          position: { x: 2846.838, y: -525.39 },
          data: {
            type: 'upload',
            label: 'Upload Furniture Reference',
            config: {
              audioUrl: '',
              imageUrl:
                'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/74c043c8-d9fb-4694-8921-69abbe34c512.jpeg',
              videoUrl: '',
              mediaType: 'image',
            },
            result:
              'https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/74c043c8-d9fb-4694-8921-69abbe34c512.jpeg',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1772687819503',
          type: 'image-edit',
          position: { x: 2848.569, y: 231.88 },
          data: {
            type: 'image-edit',
            label: 'Remove Existing Furniture',
            config: {
              useHD: false,
              prompt:
                'Make the room empty. Keep everything as it is. Just remove all furniture.',
              editModel: 'Nano Banana Pro',
              dimensions: 'auto',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/86b9f954-7b6d-4fc7-80ea-dd0d5e26b206.png',
            status: 'success',
          },
        },
        {
          id: 'image-edit-1772678332874',
          type: 'image-edit',
          position: { x: 3783.553, y: 236.986 },
          data: {
            type: 'image-edit',
            label: 'Place New Furniture',
            config: {
              useHD: false,
              prompt:
                'place the ISS sofa from the image into the room. Position it along the right wall. Adjust the angle to look it natural.',
              editModel: 'Nano Banana Pro',
              dimensions: 'auto',
              resolution: '2K',
            },
            result:
              'https://media.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/1d37cbd3-aa1b-4e33-b8a6-1a578be5dd77.png',
            status: 'success',
          },
        },
        {
          id: 'image-to-video-1772678484825',
          type: 'image-to-video',
          position: { x: 4839.289, y: 289.154 },
          data: {
            type: 'image-to-video',
            label: 'Animate Staged Room',
            config: {
              model: 'Veo 3.1 Image',
              prompt:
                'A slow, cinematic dolly-in shot of a modern minimalist living room. The camera starts wide, showing a large window with a city view and a built-in window seat, then smoothly glides toward a unique, curved bi-colored sofa (sage green exterior, mustard yellow interior). The light oak floors reflect soft, natural morning light. The textured gray paneled wall in the background adds depth. High-end architectural visualization, 4K, photorealistic, calm and elegant atmosphere, 60fps',
              duration: '8s',
              resolution: '720p',
              aspectRatio: '16:9',
            },
            result: {
              videoUrl:
                'https://videos.easy-peasy.ai/8a8096ae-bdbf-480e-a053-760d699da41c/a3904fa1-5f8c-4ef1-bf53-3a71decc3fe5.mp4',
              lastFrame:
                'https://v3b.fal.media/files/b/0a90e86c/G1uAZtCvGZbgN4lCHGzMN_2c3f4b991d774402b8b8dbb9e3321bf7.jpg',
              firstFrame:
                'https://v3b.fal.media/files/b/0a90e86c/lQUHXcNt7C4-smKo4apB1_b0c5e2c71f0e4b74be604e726af93605.jpg',
            },
            status: 'success',
          },
        },
      ],
      edges: [
        {
          id: 'xy-edge__upload-1772687785460image-image-edit-1772687819503image',
          source: 'upload-1772687785460',
          target: 'image-edit-1772687819503',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1772687819503image-image-edit-1772678332874image',
          source: 'image-edit-1772687819503',
          target: 'image-edit-1772678332874',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__upload-1768722824050image-image-edit-1772678332874image',
          source: 'upload-1768722824050',
          target: 'image-edit-1772678332874',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
        {
          id: 'xy-edge__image-edit-1772678332874image-image-to-video-1772678484825image',
          source: 'image-edit-1772678332874',
          target: 'image-to-video-1772678484825',
          sourceHandle: 'image',
          targetHandle: 'image',
        },
      ],
    },
  },
];

/**
 * Get a template by ID
 */
export function getTemplateById(id: string): WorkflowTemplate | undefined {
  return workflowTemplates.find((template) => template.id === id);
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: string): WorkflowTemplate[] {
  return workflowTemplates.filter((template) => template.category === category);
}

/**
 * Get all template categories
 */
export function getTemplateCategories(): string[] {
  const categories = new Set(
    workflowTemplates.map((template) => template.category),
  );
  return Array.from(categories);
}
